// ✅ FILE: src/pages/ProductsPage.jsx
import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
  useContext,
  createContext,
} from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Pagination from "@mui/material/Pagination";
import DocumentScannerIcon from "@mui/icons-material/DocumentScanner";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import CloseIcon from "@mui/icons-material/Close";
import Tesseract from "tesseract.js";
import { usePolling } from "../utils/usePolling";
/* =========================
   CONFIG
========================= */
import { SHEETS, API_BASE } from "../utils/api";
import { ORDER_SOURCES, fetchDealerOrders } from "../utils/orders";
import OrderRequestForm from "../components/OrderRequestForm";

const SHEET_URL = SHEETS.products;

// ✅ dealer-wise rate lookup (served from AWS cache)
const DEALER_RATE_URL = SHEETS.dealers();

// ✅ RINGS DATA API (served from AWS cache)
const RINGS_URL = SHEETS.rings;

const STOCK_URL = SHEETS.stock;

const PRODUCTS_CACHE_KEY = "products_cache_v2";
const PRODUCTS_CACHE_TS_KEY = "products_cache_ts_v2";

const RINGS_CACHE_KEY = "rings_cache_v2";
const RINGS_CACHE_TS_KEY = "rings_cache_ts_v2";

const MASTER_CACHE_TTL = 30 * 1000; // 30 sec

const getProductInputKey = (product) =>
  `${String(product?.type || "").trim()}__${String(product?.name || "").trim()}`;

// Always return cached data if it exists — TTL is intentionally ignored.
// Background polling + on-mount fetch keep the data fresh, so we should never
// show a loader on refresh just because the cache "expired". Stale-while-
// revalidate: show old data immediately, replace silently when fresh arrives.
const getCachedData = (dataKey /*, tsKey, ttl */) => {
  try {
    const raw = localStorage.getItem(dataKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed;
  } catch {
    return null;
  }
};

const setCachedData = (dataKey, tsKey, value) => {
  try {
    localStorage.setItem(dataKey, JSON.stringify(value));
    localStorage.setItem(tsKey, String(Date.now()));
  } catch {}
};

const normalizeText = (v) =>
  String(v || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

const normalizeSizeKey = (v) =>
  String(v || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ")
    .replace(/kgs?/g, "kg")
    .replace(/\s*x\s*/g, "x");

const normalizeRingStockKey = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ")
    .replace(/kgs?/g, "kg")
    .replace(/(\d)\s*mm\b/g, "$1mm")
    .replace(/\s*x\s*/g, "x")
    .replace(/\s+/g, " ")
    .trim();

const normalizeRingLabel = (value) => {
  const raw = String(value || "")
    .trim()
    .replace(/\s+/g, " ");
  if (!raw) return "";

  const match = raw.match(/^(\d+)\s*mm?\s*(.*)$/i);
  if (match) {
    const mm = `${match[1]}mm`;
    const rest = String(match[2] || "")
      .trim()
      .replace(/\s*x\s*/gi, "x");
    return rest ? `${mm} ${rest}` : mm;
  }

  return raw;
};

const toSafeStock = (v) => {
  const n = Number(v || 0);
  if (Number.isNaN(n)) return 0;
  return Math.max(0, n);
};

const getRingPerPieceWeight = (obj = {}) => {
  const keys = [
    "Per Piece Wt.",
    "Per Piece Wt",
    "PerPieceWt",
    "Per_Piece_Wt",
    "perPieceWt",
    "per_piece_wt",
    "Piece Weight",
    "PieceWeight",
    "Wt",
  ];

  for (const k of keys) {
    const v = obj?.[k];
    if (v !== undefined && v !== null && String(v).trim() !== "") {
      const n = Number(String(v).replace(/,/g, "").trim());
      if (!Number.isNaN(n) && n > 0) return n;
    }
  }

  return 0;
};

/* =========================
   TYPE MAPS
========================= */
const typeMap = {
  All: null,
  Mix: "Mix",
  Angle: "A",
  Flat: "F",
  Round: "R",
  Square: "S",
  Channel: "C",
  Rejected: "Rejected",
};

const typeFullName = {
  A: "Angle",
  F: "Flat",
  R: "Round",
  S: "Square",
  C: "Channel",
  Mix: "Mix",
  Rejected: "Rejected",
};

const searchTypeKeywords = [
  { words: ["angle", "angles", "a"], type: "A" },
  { words: ["flat", "flats", "f"], type: "F" },
  { words: ["round", "rounds", "r"], type: "R" },
  { words: ["square", "squares", "sq", "s"], type: "S" },
  { words: ["channel", "channels", "c"], type: "C" },
  { words: ["mix", "mixed"], type: "Mix" },
  { words: ["rejected", "reject", "rej"], type: "Rejected" },
];

const detectTypeFromQuery = (q) => {
  const text = (q || "").toLowerCase().trim();
  if (!text) return null;
  const tokens = text.split(/\s+/).filter(Boolean);
  for (const item of searchTypeKeywords) {
    for (const w of item.words) if (tokens.includes(w)) return item.type;
  }
  return null;
};

const stripTypeWords = (q) => {
  const tokens = (q || "").toLowerCase().trim().split(/\s+/).filter(Boolean);
  const allTypeWords = new Set(searchTypeKeywords.flatMap((x) => x.words));
  return tokens
    .filter((t) => !allTypeWords.has(t))
    .join(" ")
    .trim();
};

const PRODUCTS_PER_PAGE = 12;
const RINGS_PER_PAGE = 12;

const getStockStatus = (stockKg) => {
  const stock = Number(stockKg) || 0;
  const MAX = 15000;
  const pct = Math.max(0, Math.min(100, (stock / MAX) * 100));

  let label = "Full";
  let textColor = "text-green-600";
  let barColor = "bg-green-500";

  if (stock <= 0) {
    label = "Out of Stock";
    textColor = "text-red-600";
    barColor = "bg-red-600";
  } else if (stock <= 5000) {
    label = "Low";
    textColor = "text-red-500";
    barColor = "bg-red-400";
  } else if (stock <= 15000) {
    label = "Medium";
    textColor = "text-yellow-600";
    barColor = "bg-yellow-400";
  }

  return { label, textColor, barColor, pct };
};

const isAfter630IST = () => {
  const parts = new Intl.DateTimeFormat("en-IN", {
    timeZone: "Asia/Kolkata",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(new Date());

  const hh = Number(parts.find((p) => p.type === "hour")?.value || "0");
  const mm = Number(parts.find((p) => p.type === "minute")?.value || "0");
  return hh > 18 || (hh === 18 && mm >= 30);
};

/* =========================
   TMT (6MM) OPTIONS
========================= */
const TMT6_OPTIONS = [
  { key: "mix", label: "Mix Size Booking", parityPerMT: 0 },
  { key: "30-40", label: "30 to 40", parityPerMT: 0 },
];

const parityMTtoKg = (parityPerMT) => (Number(parityPerMT) || 0) / 1000;

/* =========================
   BINDING WIRE SIZE OPTIONS
   (25 KGS is SIZE, not qty)
========================= */
// Binding Wire variants — MS & GI. Stock STOCK sheet (PRPDUCT=BW, SIZE=MS/GI)
// se aata hai; rate dono ke liye same (dealer BINDING WIRE rate).
const BW_OPTIONS = [
  { key: "ms", label: "MS" },
  { key: "gi", label: "GI" },
];

/* =========================
   CART PERSIST HELPERS
========================= */
const safeJsonParse = (s, fallback) => {
  try {
    return JSON.parse(s);
  } catch {
    return fallback;
  }
};

const normalizePhone10 = (v) => {
  const digits = String(v || "").replace(/\D/g, "");
  if (!digits) return "";
  return digits.length > 10 ? digits.slice(-10) : digits;
};

const getDealerKey = (dealerObj) => {
  const profile = dealerObj?.profile || dealerObj || {};
  const gst = String(profile?.["GSTIN/UIN"] || profile?.GSTIN || "").trim();
  const phone = String(
    profile?.phone || profile?.["Contact No"] || dealerObj?.phone || "",
  ).trim();
  return gst || phone || "guest";
};

const cartStorageKey = (dealerKey) => `cart:v1:${dealerKey}`;

/* =========================
   DEALER RATE FETCH HELPERS
========================= */
const extractRows = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (payload?.data && typeof payload.data === "object") return [payload.data];
  if (typeof payload === "object") return [payload];
  return [];
};

const pickFirstMatchByGstOrPhone = (rows, gst, phone10) => {
  const gstNorm = String(gst || "")
    .trim()
    .toUpperCase();
  const phoneNorm = normalizePhone10(phone10);

  if (!rows.length) return null;

  const byGst = gstNorm
    ? rows.find((r) => {
        const rg = String(
          r?.["GSTIN/UIN"] || r?.GSTIN || r?.gst || r?.Gst || "",
        )
          .trim()
          .toUpperCase();
        return rg && rg === gstNorm;
      })
    : null;

  if (byGst) return byGst;

  const byPhone = phoneNorm
    ? rows.find((r) => {
        const rp = normalizePhone10(
          r?.phone || r?.["Contact No"] || r?.Mobile || r?.mobile || r?.Phone,
        );
        return rp && rp === phoneNorm;
      })
    : null;

  return byPhone || null;
};

// ✅ column finder (handles different sheet headers)
const getNum = (obj, keys) => {
  for (const k of keys) {
    const v = obj?.[k];
    if (v !== undefined && v !== null && String(v).trim() !== "") {
      const n = Number(String(v).replace(/,/g, "").trim());
      if (!Number.isNaN(n)) return n;
    }
  }
  return 0;
};

const getRank = (obj, keys) => {
  const raw = getNum(obj, keys);
  if (!raw || Number.isNaN(raw)) return Number.POSITIVE_INFINITY;
  return Number(raw);
};

/* =========================
   RATE NORMALIZERS
========================= */
const toKgRateFromMaybeMT = (value) => {
  const n = Number(value || 0);
  if (!n || Number.isNaN(n)) return 0;
  // If number looks like MT-rate/parity (>=500) convert to kg.
  if (n >= 500) return n / 1000;
  return n;
};

/* =========================
   SMALL COMPONENTS
========================= */
function Tabs({
  activeSection,
  setActiveSection,
  loading,
  onTop,
  onDownloadForm,
}) {
  // ✅ Tabs ALWAYS visible when not loading (regardless of per-section booking status)
  if (loading) return null;

  const topClass =
    activeSection === "Structure"
      ? "top-[144px] md:top-[110px]"
      : "top-[100px] md:top-[110px]";

  // "Order Here →" hint shows only when nothing is selected yet — once
  // the dealer clicks any tab, the arrow disappears (they've found it).
  const showOrderHereHint = !activeSection;

  return (
    <>
      <style>{`
        @keyframes sotaOrderHerePulse {
          0%, 100% { transform: translateX(0)    scale(1);    }
          25%      { transform: translateX(-4px) scale(1.06); }
          50%      { transform: translateX(0)    scale(1);    }
          75%      { transform: translateX(-2px) scale(1.03); }
        }
        @keyframes sotaTabPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(250, 204, 21, 0.6); }
          50%      { box-shadow: 0 0 0 10px rgba(250, 204, 21, 0); }
        }
        .sota-order-here-pulse {
          animation: sotaOrderHerePulse 1.4s ease-in-out infinite;
          will-change: transform;
        }
        .sota-tab-pulse {
          animation: sotaTabPulse 1.6s ease-in-out infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .sota-order-here-pulse, .sota-tab-pulse { animation: none; }
        }
      `}</style>

      <div className={`fixed left-0 right-0 z-[45] ${topClass}`}>
        <div className="px-4 overflow-x-auto">
          <div className={`${showOrderHereHint ? "ml-auto" : "mx-auto"} w-max flex items-center gap-2 md:gap-3 max-md:gap-1.5`}>
            {showOrderHereHint && (
              <button
                type="button"
                onClick={() => {
                  setActiveSection("Structure");
                  onTop?.();
                }}
                className="sota-order-here-pulse hidden sm:inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-yellow-400 to-orange-500 text-black font-extrabold text-sm px-5 py-2.5 shadow-lg border border-yellow-500 hover:shadow-xl hover:from-yellow-500 hover:to-orange-600 active:scale-95"
                title="Tap a section to start ordering"
              >
                Order Here
                <span aria-hidden className="text-lg leading-none">→</span>
              </button>
            )}
            {/* Home + Tabs + Order Request Form — sirf "Order Here" page pe
                dikhein (jab koi section active ho). Dashboard pe sirf
                "Order Here →" button dikhta hai. */}
            {!showOrderHereHint && (
              <>
                {/* Home — back to welcome dashboard (clears activeSection) */}
                <button
                  type="button"
                  onClick={() => {
                    setActiveSection("");
                    onTop?.();
                  }}
                  className="inline-flex items-center gap-1 rounded-full bg-white text-gray-800 font-semibold text-xs md:text-sm px-3 md:px-4 py-2 shadow border border-gray-200 hover:bg-gray-50 active:scale-95 shrink-0"
                  title="Back to home"
                >
                  <span aria-hidden>←</span>
                  <span className="hidden sm:inline">Home</span>
                </button>

                <div className="rounded-full bg-white/95 backdrop-blur shadow-lg border flex px-2 py-2 gap-2 max-md:px-1 max-md:py-1 max-md:gap-1 shrink-0">
                  {["Structure", "TMT", "Binding Wire", "Rings"].map((label) => {
                    const active = activeSection === label;
                    return (
                      <button
                        key={label}
                        onClick={() => {
                          setActiveSection(label);
                          onTop?.();
                        }}
                        className={`whitespace-nowrap rounded-full min-md:font-bold text-sm border transition px-4 py-2 max-md:px-2.5 max-md:py-1 max-md:text-[11px] ${
                          active
                            ? "bg-black text-yellow-300 border-black shadow-md"
                            : "bg-white text-gray-800 border-gray-200 hover:bg-gray-50"
                        }`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>

                {/* Download / send the Order Request Form */}
                <button
                  type="button"
                  onClick={() => onDownloadForm?.()}
                  className="inline-flex items-center gap-1 rounded-full bg-blue-600 text-white font-bold text-xs md:text-sm shadow hover:bg-blue-700 active:scale-95 whitespace-nowrap shrink-0 px-3 md:px-4 py-2 max-md:px-2.5 max-md:py-1"
                  title="Download / send order request form"
                >
                  <span aria-hidden>📄</span>
                  <span className="hidden sm:inline">Order Request Form</span>
                  <span className="sm:hidden">Form</span>
                </button>
              </>
            )}
          </div>

          {/* Smaller pulsing badge on mobile (placed underneath because
              horizontal space is tight) */}
          {showOrderHereHint && (
            <div className="sm:hidden mt-2 flex justify-center">
              <button
                type="button"
                onClick={() => {
                  setActiveSection("Structure");
                  onTop?.();
                }}
                className="sota-order-here-pulse inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-yellow-400 to-orange-400 text-black font-extrabold text-xs px-4 py-2 shadow border border-yellow-500"
              >
                Order Here →
              </button>
            </div>
          )}
        </div>
      </div>

      <div className={`${showOrderHereHint ? "h-[6px] md:h-[66px]" : "h-[15px] md:h-[66px]"}`} />
    </>
  );
}

function CartDropdown({
  cart,
  cartTotal,
  isCartOpen,
  setIsCartOpen,
  onRemove,
  cartRef,
}) {
  return (
    <div className="relative md:self-auto self-end" ref={cartRef}>
      <button
        onClick={() => setIsCartOpen((prev) => !prev)}
        className="inline-flex items-center gap-2 px-2 py-1 max-md:mb-[10px] rounded-full bg-yellow-500 text-black font-semibold shadow-md hover:bg-yellow-400 text-sm"
      >
        🛒 Cart
        <span className="px-2 py-0.5 text-xs bg-black/10 rounded-full">
          {cart.length}
        </span>
      </button>

      {isCartOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-2xl shadow-2xl border border-gray-200 p-4 max-h-[30vh] overflow-y-auto z-40">
          <h2 className="text-sm font-bold mb-3 flex items-center justify-between">
            Your Cart
            <span className="text-[11px] font-medium text-gray-500">
              {cart.length} item{cart.length !== 1 ? "s" : ""}
            </span>
          </h2>

          {cart.length === 0 ? (
            <p className="text-xs text-gray-500">No items added yet.</p>
          ) : (
            <>
              <div className="space-y-3 mb-3">
                {cart.map((item, idx) => (
                  <div
                    key={`${item.name}-${idx}`}
                    className="border border-gray-100 rounded-2xl px-3 py-2"
                  >
                    <div className="flex justify-between items-start gap-2">
                      <p className="text-sm font-semibold text-gray-800 line-clamp-2 flex-1">
                        {item.name}
                      </p>
                      <button
                        onClick={() => onRemove(item.name)}
                        className="text-[11px] text-red-500 font-semibold hover:underline"
                      >
                        Remove
                      </button>
                    </div>

                    <div className="mt-1 flex justify-between items-center text-[11px] text-gray-600">
                      <span>
                        Qty: <strong>{item.qty} Kg</strong>
                      </span>
                      <span>
                        Rate:{" "}
                        <strong>₹{Number(item.rate).toFixed(2)}/kg</strong>
                      </span>
                    </div>

                    <p className="mt-1 text-sm font-bold text-gray-900">
                      Subtotal: ₹
                      {Number(item.subtotal || 0).toLocaleString("en-IN")}
                    </p>
                  </div>
                ))}
              </div>

              <div className="border-t pt-2 flex justify-between items-center">
                <span className="text-sm font-semibold text-gray-700">
                  Total
                </span>
                <span className="text-lg font-bold text-gray-900">
                  ₹{cartTotal.toLocaleString("en-IN")}
                </span>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* ===========================================================
   Welcome / Dashboard view — shown on the products page when
   no section tab is selected yet. Surfaces:
     • Pending orders count + Total orders count
     • Big "Order Here →" hint pointing up to the tabs
     • Feedback CTA opening a popup with a Google Form link
   ============================================================ */
/* Helpers shared by dashboard cards and popups */
const last10Phone = (v) => String(v ?? "").replace(/\D/g, "").slice(-10);
const ORDER_SHEET_KEYS = ["structure", "tmt", "rings", "binding"];
const ORDER_LABEL = {
  structure: "Structure",
  tmt: "TMT",
  rings: "Rings",
  binding: "Binding Wire",
};

const formatRs = (n) => {
  const x = Number(n);
  if (!Number.isFinite(x)) return "—";
  return "₹" + x.toLocaleString("en-IN");
};

/* Pull the first non-empty value matching any key in `keys`. Matches
   OrderStatusModal's extraction logic so the same Apps Script row
   shape works here. */
const pickField = (row, keys) => {
  if (!row || typeof row !== "object") return "";
  for (const k of keys) {
    const v = row?.[k];
    if (v !== undefined && v !== null && String(v).trim() !== "") {
      return String(v).trim();
    }
  }
  return "";
};

/* Field accessors — same key variants as OrderStatusModal */
const ORDER_KEYS = {
  date: ["Timestamp", "timestamp", "Created At", "Date", "DATE", "Order Date"],
  firm: ["Firm Name", "Firm Name ", "Second Firm Name", "User Name", "User", "SFirm Name"],
  product: [
    "Item", "Item ", "Size", "Product", "Particular", "Particulars",
    "Description", "Product Name",
  ],
  qty: [
    "Qty", "Qty ", "Quantity", "QTY", "Weight", "Weight(KGS)",
    "Qty(KGS)", "Qty(PCS)",
  ],
  status: ["Order Status", "ORDER STATUS", "Status", "status"],
  phone: [
    "Contact No", "Contact No ", "Contact Number", "Phone", "Mobile",
    "Userid", "GID", "Contact", "Mobile No", "Mobile Number",
    "Contact No.", "Contact Number ", "NUMBER", "Number",
  ],
};

const parseDateMs = (v) => {
  if (!v) return 0;
  const s = String(v).trim();
  // Try direct Date.parse first (covers ISO + most US formats)
  const direct = new Date(s).getTime();
  if (!isNaN(direct)) return direct;
  // DD/MM/YYYY or DD-MM-YYYY (Indian)
  const m = s.match(/^(\d{1,2})[/\-](\d{1,2})[/\-](\d{2,4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?/);
  if (m) {
    let day = +m[1], mon = +m[2] - 1, yr = +m[3];
    if (yr < 100) yr += 2000;
    const hh = +(m[4] || 0), mm = +(m[5] || 0), ss = +(m[6] || 0);
    const d = new Date(yr, mon, day, hh, mm, ss);
    if (!isNaN(d.getTime())) return d.getTime();
  }
  return 0;
};

const formatRowDate = (v) => {
  const ms = parseDateMs(v);
  if (!ms) return String(v || "—");
  const d = new Date(ms);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yy = d.getFullYear();
  return `${dd}/${mm}/${yy}`;
};

function WelcomeDashboard({
  dealerPhone,
  onFeedbackClick,
  onPendingClick,
  onTrackingClick,
  onOutstandingClick,
  onDispatchClick,
  onPreDispatchClick,
}) {
  const navigate = useNavigate();
  const [pendingCount, setPendingCount] = useState(null);
  const [totalCount, setTotalCount] = useState(null);
  const [dispatchCount, setDispatchCount] = useState(null);
  const [preDispatchCount, setPreDispatchCount] = useState(null);
  const [outstanding, setOutstanding] = useState(null);
  const [creditLimit, setCreditLimit] = useState(null);

  // Monthly ring-sales achievement (Jan–Sep) from the RING SALES REPORT sheet,
  // matched by the logged-in dealer's GST. Values in KG (targets are in MT).
  const [ringSales, setRingSales] = useState(null);
  useEffect(() => {
    if (!dealerPhone) return;
    let cancelled = false;
    // Dealers with no sheet data still see the graph — flat at 0 (Jan–Sep).
    const ZERO_MONTHS = [
      "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep",
    ].map((l) => ({ label: l, mt: 0 }));
    const zeroData = {
      success: true,
      found: false,
      firmName: "",
      status: "",
      months: ZERO_MONTHS,
    };
    (async () => {
      const gsts = (await loadDealerFirms(dealerPhone)).map((fm) => fm.gst).filter(Boolean);
      if (cancelled) return;
      if (!gsts.length) {
        setRingSales(zeroData);
        return;
      }
      const res = await fetch(
        `${API_BASE}/api/sheets/ring-sales?gst=${encodeURIComponent(gsts.join(","))}`,
        { cache: "no-store" }
      );
      const d = await res.json().catch(() => null);
      if (!cancelled) setRingSales(d?.success && d.found ? d : zeroData);
    })().catch(() => {
      if (!cancelled) setRingSales(zeroData);
    });

    return () => {
      cancelled = true;
    };
  }, [dealerPhone]);

  /* Ajay's Jalsa Tour runs in Saurashtra + Kutch only, so the card is shown
     to those dealers alone (districts / areas from the "saurastra district
     area wise" sheet; Rajkot's own areas are covered by the district). */
  const [ajayArea, setAjayArea] = useState(null); // null = still loading
  useEffect(() => {
    if (!dealerPhone) return;
    let cancelled = false;
    fetch(SHEETS.dealers(dealerPhone), { cache: "no-store" })
      .then((r) => r.json())
      .then((rows) => {
        if (cancelled) return;
        const ok = (Array.isArray(rows) ? rows : []).some((r) =>
          ["District", "DISTRICT", "Area", "AREA", "TALUKA", "Taluka"].some((k) =>
            AJAY_TOUR_AREAS.has(String(r?.[k] || "").trim().toUpperCase())
          )
        );
        setAjayArea(ok);
      })
      .catch(() => {
        if (!cancelled) setAjayArea(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dealerPhone]);

  // Ajay "Jalsa Tour 2026" (May–Dec) from the AJAYJABYSYSTEM sheet, matched
  // by the dealer's GSTINs. Months come back in MT; Slab = the sheet's status.
  const [ajayJalsa, setAjayJalsa] = useState(null);
  useEffect(() => {
    if (!dealerPhone) return;
    let cancelled = false;
    const zeroData = {
      found: false,
      firmName: "",
      slab: "",
      achievedMt: 0,
      months: ["May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"].map((l) => ({ label: l, mt: 0 })),
    };
    (async () => {
      const gsts = (await loadDealerFirms(dealerPhone)).map((f) => f.gst).filter(Boolean);
      if (cancelled) return;
      if (!gsts.length) {
        setAjayJalsa(zeroData);
        return;
      }
      const res = await fetch(
        `${API_BASE}/api/sheets/ajay-jalsa?gst=${encodeURIComponent(gsts.join(","))}`,
        { cache: "no-store" }
      );
      const d = await res.json().catch(() => null);
      if (!cancelled) setAjayJalsa(d?.success && d.found ? d : zeroData);
    })().catch(() => {
      if (!cancelled) setAjayJalsa(zeroData);
    });
    return () => {
      cancelled = true;
    };
  }, [dealerPhone]);

  // Order counts. Pending is now keyed by the dealer's GSTINs (not
  // phone) since the new pending-orders sheet has no phone column.
  // We pull the GSTs from the firms cache Login pre-warmed.
  useEffect(() => {
    if (!dealerPhone) return;
    let cancelled = false;

    let dealerGsts = [];
    try {
      const raw = localStorage.getItem(`firmsCache:v1:${dealerPhone}`);
      if (raw) {
        const parsed = JSON.parse(raw);
        dealerGsts = (parsed?.payload?.firms || [])
          .map((f) => String(f.gst || "").trim().toUpperCase())
          .filter(Boolean);
      }
    } catch {
      /* ignore */
    }

    if (dealerGsts.length > 0) {
      const allowedGsts = new Set(dealerGsts);
      // Use the SAME helper the popups use, so counts always match
      // what the popup actually shows.
      (async () => {
        const [arr, historyArr] = await Promise.all([
          fetchCombinedOrders(dealerGsts),
          fetchDealerOrders(dealerGsts, "history").catch(() => []),
        ]);
        if (cancelled) return;
        const mapped = arr
          .map((raw) => parseOrderRow(raw))
          .filter((r) => !isHeaderRow(r))
          .filter((r) => allowedGsts.has(r.gst));
        // Pending popup = truly open orders only:
        //   pending > 0 AND no closure marker (95% Disp Close / Force
        //   Close) AND not Rejected.
        const pendingRows = mapped.filter(
          (r) =>
            !r.raw?.__historyOnly &&
            Number(r.pending || 0) > 0 &&
            !String(r.dispClose || "").trim() &&
            !String(r.forceClose || "").trim() &&
            !/reject|close|forced|cancel/.test(String(r.status || "").toLowerCase())
        );
        // Order History = the order sheets only (same rows as the popup).
        const completedRows = historyArr
          .map((raw) => parseOrderRow(raw))
          .filter((r) => !isHeaderRow(r) && allowedGsts.has(r.gst));
        setPendingCount(pendingRows.reduce((s, r) => s + (Number(r.pending) || 0), 0));
        setTotalCount(completedRows.reduce((s, r) => s + (Number(r.quantity) || 0), 0));
      })().catch(() => {
        if (cancelled) return;
        setPendingCount(0);
        setTotalCount(0);
      });
    } else {
      setPendingCount(0);
      setTotalCount(0);
    }

    return () => {
      cancelled = true;
    };
  }, [dealerPhone]);

  // Structure Dispatch / Pre Dispatch counts — same GST-filtered endpoints
  // the popups use, so the card number matches the popup.
  useEffect(() => {
    if (!dealerPhone) return;
    let cancelled = false;
    (async () => {
      const gsts = (await loadDealerFirms(dealerPhone)).map((f) => f.gst);
      const [dispatch, bills, preDispatch] = await Promise.all([
        fetchStrRows("dispatch", gsts),
        fetchStrRows("bills", gsts),
        fetchStrRows("predispatch", gsts),
      ]);
      if (cancelled) return;
      setDispatchCount(sumQty(mergeDispatchWithBills(groupByInvoice(groupLoadingSlips(dispatch)), bills)));
      setPreDispatchCount(sumQty(groupLoadingSlips(preDispatch)));
    })().catch(() => {
      if (cancelled) return;
      setDispatchCount(0);
      setPreDispatchCount(0);
    });
    return () => {
      cancelled = true;
    };
  }, [dealerPhone]);

  // Credit Limit — sums from the dealer's row(s) in the dealers sheet.
  // (Outstanding is now sourced from the dedicated outstanding sheet
  // below so the dashboard total matches the popup total exactly.)
  useEffect(() => {
    if (!dealerPhone) return;
    let cancelled = false;
    fetch(SHEETS.dealers(dealerPhone), { cache: "no-store" })
      .then((r) => r.json())
      .then((rows) => {
        if (cancelled || !Array.isArray(rows)) return;
        let creditTotal = 0;
        rows.forEach((row) => {
          const cLakh = Number(
            String(row?.["Credit Limit In Lakh"] ?? 0)
              .toString()
              .replace(/,/g, "")
          );
          if (Number.isFinite(cLakh)) creditTotal += cLakh * 100000;
        });
        setCreditLimit(creditTotal);
      })
      .catch(() => {
        if (cancelled) return;
        setCreditLimit(0);
      });
    return () => {
      cancelled = true;
    };
  }, [dealerPhone]);

  // Outstanding — sum from the dedicated outstanding sheet, filtered to
  // the dealer's registered GSTs. SAME data source as OutstandingPopup
  // so the card number always matches the popup total.
  useEffect(() => {
    if (!dealerPhone) return;
    let cancelled = false;
    (async () => {
      try {
        // 1. Build the dealer's GST set from the firms cache (fast) or
        //    a phone-filtered dealers fetch (fallback).
        let dealerGsts = new Set();
        try {
          const raw = localStorage.getItem(`firmsCache:v1:${dealerPhone}`);
          if (raw) {
            const parsed = JSON.parse(raw);
            (parsed?.payload?.firms || []).forEach((f) => {
              const g = String(f?.gst || "").trim().toUpperCase();
              if (g) dealerGsts.add(g);
            });
          }
        } catch {
          /* fall through to dealers fetch */
        }
        if (dealerGsts.size === 0) {
          const res = await fetch(SHEETS.dealers(dealerPhone), {
            cache: "no-store",
          });
          const data = await res.json().catch(() => []);
          (Array.isArray(data) ? data : []).forEach((row) => {
            const g = String(
              row?.["GSTIN/UIN"] ||
                row?.["GSTIN/UIN "] ||
                row?.["GST No"] ||
                row?.["GST No "] ||
                row?.GST ||
                row?.gst ||
                ""
            )
              .trim()
              .toUpperCase();
            if (g) dealerGsts.add(g);
          });
        }
        if (cancelled) return;

        // 2. Outstanding — same source as the popup: OUTSTANDING FOR ALL
        //    PLANT, one block per plant, already filtered to these GSTs.
        const res = await fetch(
          `${API_BASE}/api/sheets/outstanding?gst=${encodeURIComponent(
            [...dealerGsts].join(",")
          )}`,
          { cache: "no-store" }
        );
        const rows = await res.json().catch(() => []);
        if (cancelled) return;
        const total = (Array.isArray(rows) ? rows : []).reduce(
          (sum, r) => sum + sectionTotal(parseOutstandingBlock(r.template, r.plant)),
          0
        );
        setOutstanding(total);
      } catch {
        if (!cancelled) setOutstanding(0);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dealerPhone]);

  // Compact cards, one shared layout: icon → label → value → hint, so every
  // card lines up in the row.
  const CARD_BASE =
    "flex flex-col rounded-xl p-3 md:p-4 shadow-sm border-2 transition min-h-[112px] md:min-h-[124px] text-left w-full";
  const CARD_CLICKABLE = "cursor-pointer hover:shadow-md hover:-translate-y-0.5 active:translate-y-0";
  // Credit limit crossed? (both numbers must have loaded, and a limit of 0
  // means "not set", not "everything is over the limit".)
  const overLimit =
    outstanding != null && creditLimit != null && creditLimit > 0 && outstanding > creditLimit;

  const Card = ({ label, value, color, onClick, clickHint, icon, small, qty }) => {
    const inner = (
      <>
        {icon && <div className="text-xl md:text-2xl leading-none mb-1.5 opacity-80">{icon}</div>}
        <p className="text-[10px] md:text-[11px] font-bold uppercase tracking-wider opacity-75 leading-tight">
          {label}
        </p>
        <p
          className={`mt-1 font-extrabold leading-tight break-words ${
            small ? "text-sm md:text-base" : qty ? "text-base md:text-lg" : "text-xl md:text-2xl"
          }`}
        >
          {value == null ? "…" : value}
        </p>
        {clickHint && (
          <p className="mt-auto pt-1.5 text-[10px] md:text-[11px] font-semibold opacity-70">
            tap to view →
          </p>
        )}
      </>
    );
    const cls = `${CARD_BASE} ${color} ${onClick ? CARD_CLICKABLE : ""}`;
    return onClick ? (
      <button type="button" onClick={onClick} className={cls}>
        {inner}
      </button>
    ) : (
      <div className={cls}>{inner}</div>
    );
  };

  return (
    // Full-width container fills the viewport instead of being capped
    // at max-w-5xl. Cards stretch evenly so the dashboard visually
    // covers the page below the tabs.
    <div className="mt-4 w-full px-3 md:px-6 pb-12">
      {/* 7 stat cards — 2 columns on phone, 4 on laptop, all 7 in one row on wide screens */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-2.5 md:gap-3 lg:gap-4">
        <Card
          icon="📈"
          label="Order History"
          value={totalCount == null ? null : kg(totalCount)}
          qty
          color="bg-emerald-50 border-emerald-300 text-emerald-900"
          onClick={onTrackingClick}
          clickHint
        />
        <Card
          icon="📦"
          label="Pending Orders"
          value={pendingCount == null ? null : kg(pendingCount)}
          qty
          color="bg-blue-50 border-blue-300 text-blue-900"
          onClick={onPendingClick}
          clickHint
        />
        <Card
          icon="📋"
          label="Pre Dispatch"
          value={preDispatchCount == null ? null : kg(preDispatchCount)}
          qty
          color="bg-orange-50 border-orange-300 text-orange-900"
          onClick={onPreDispatchClick}
          clickHint
        />
        <Card
          icon="🚚"
          label="Dispatch"
          value={dispatchCount == null ? null : kg(dispatchCount)}
          qty
          color="bg-indigo-50 border-indigo-300 text-indigo-900"
          onClick={onDispatchClick}
          clickHint
        />
        {/* Outstanding + Credit Limit — merged into one card; over the
            limit it turns red so the dealer cannot miss it. */}
        <button
          type="button"
          onClick={onOutstandingClick}
          className={`${CARD_BASE} ${CARD_CLICKABLE} ${
            overLimit
              ? "bg-red-100 border-red-500 text-red-900 ring-2 ring-red-400"
              : "bg-rose-50 border-rose-300 text-rose-900"
          }`}
        >
          {/* Same layout as the other cards: icon → label → value, credit limit below */}
          <div className="text-xl md:text-2xl leading-none mb-1.5 opacity-80">
            {overLimit ? "⚠️" : "💰"}
          </div>
          <p className="text-[10px] md:text-[11px] font-bold uppercase tracking-wider opacity-75 leading-tight">
            Outstanding
          </p>
          <p
            className="mt-1 text-lg md:text-xl font-extrabold leading-tight whitespace-nowrap overflow-hidden text-ellipsis"
            title={formatRs(outstanding)}
          >
            {outstanding == null ? "…" : formatRs(outstanding)}
          </p>
          <p className="mt-0.5 text-[10px] md:text-[11px] font-semibold leading-tight whitespace-nowrap overflow-hidden text-ellipsis">
            <span className="uppercase tracking-wider opacity-75">Credit limit </span>
            <span className="font-extrabold text-amber-800">
              {creditLimit == null ? "…" : formatRs(creditLimit)}
            </span>
          </p>
          {overLimit && (
            <p className="mt-0.5 text-[10px] md:text-[11px] font-extrabold leading-tight text-red-700">
              Over limit by {formatRs(outstanding - creditLimit)}
            </p>
          )}
          <p className="mt-auto pt-1.5 text-[10px] md:text-[11px] font-semibold opacity-70">
            tap to view →
          </p>
        </button>
        <Card
          icon="🤝"
          label="My Customers"
          value="Customers"
          color="bg-yellow-50 border-yellow-400 text-yellow-900"
          onClick={() => navigate("/vyapar-sales")}
          clickHint
          small
        />
        <Card
          icon="📣"
          label="Marketing Collaterals"
          value="Creatives"
          color="bg-purple-50 border-purple-300 text-purple-900"
          onClick={() => navigate("/marketing-collators")}
          clickHint
          small
        />
      </div>

      {/* ── Schemes: Rings Jalsa (left) + Ajay Jalsa Tour (right) ── */}
      <div className="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-5 items-start max-w-6xl">
      {/* Monthly Achievement (Jan–Sep) from RING SALES REPORT */}
      {ringSales && (() => {
        const MONTH_TARGET = 1.5; // MT minimum monthly
        const TOTAL_TARGET = 12; // MT for Jan–Sep
        const months = ringSales.months || []; // [{ label, mt }]
        // Drop trailing (future / empty) months — stop the line where the
        // latest data actually is.
        const lastIdx = months.reduce((acc, m, i) => (m.mt > 0 ? i : acc), -1);
        const chartMonths = lastIdx >= 0 ? months.slice(0, lastIdx + 1) : months;
        // Total counts ONLY months that met the 1.5 MT monthly target.
        // Below-target months still appear on the graph but not in the total.
        const belowMonths = months.filter((m) => m.mt > 0 && m.mt < MONTH_TARGET);
        const totalMt =
          Math.round(
            months
              .filter((m) => m.mt >= MONTH_TARGET)
              .reduce((s, m) => s + m.mt, 0) * 1000
          ) / 1000;
        const pct = Math.min(100, (totalMt / TOTAL_TARGET) * 100);
        const statusRaw = String(ringSales.status || "").trim();
        const qualified = /qualif/i.test(statusRaw);
        const fmt = (v) => Number(v || 0).toFixed(2);
        return (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-md p-4 w-full min-w-0">
            <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
              <h3 className="font-extrabold text-gray-800 flex items-center gap-2 text-base">
                🎉 Rings - Jalsa with Friends
              </h3>
              <div className="flex items-center gap-2 flex-wrap">
                <a
                  href="/jalsa-with-friends-2026.pdf"
                  download="Jalsa with Friends - MS Rings (2026).pdf"
                  className="inline-flex items-center gap-1 text-xs font-bold bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-full transition"
                  title="Download scheme PDF"
                >
                  ⬇ PDF
                </a>
                <span
                  className={`text-xs sm:text-sm font-extrabold px-3 py-1.5 rounded-full ${
                    qualified
                      ? "bg-emerald-100 text-emerald-700"
                      : "bg-gray-100 text-gray-500"
                  }`}
                >
                  {statusRaw
                    ? qualified
                      ? "✅ " + statusRaw
                      : statusRaw
                    : "Not Qualified"}
                </span>
              </div>
            </div>

            {/* Line chart — Achieved vs Target (two lines) */}
            {(() => {
              const W = 720, H = 150, padL = 30, padR = 16, padT = 18, padB = 22;
              const iw = W - padL - padR;
              const ih = H - padT - padB;
              const n = chartMonths.length;
              const maxY = Math.max(MONTH_TARGET, ...chartMonths.map((m) => m.mt)) * 1.2 || 1;
              const xAt = (i) => padL + (n <= 1 ? iw / 2 : (i / (n - 1)) * iw);
              const yAt = (v) => padT + ih - (Math.max(0, v) / maxY) * ih;
              const achLine = chartMonths.map((m, i) => `${xAt(i)},${yAt(m.mt)}`).join(" ");
              const tY = yAt(MONTH_TARGET);
              return (
                <div className="mt-3 overflow-x-auto">
                  <svg viewBox={`0 0 ${W} ${H}`} className="w-full min-w-[420px]">
                    {/* baseline */}
                    <line x1={padL} y1={padT + ih} x2={padL + iw} y2={padT + ih} stroke="#e5e7eb" strokeWidth="1" />
                    {/* Target line (flat 1.5 MT) */}
                    <line x1={padL} y1={tY} x2={padL + iw} y2={tY} stroke="#f43f5e" strokeWidth="2" strokeDasharray="6 5" />
                    <text x={padL + iw} y={tY - 5} textAnchor="end" fontSize="11" fontWeight="700" fill="#f43f5e">1.5 MT</text>
                    {/* Achievement line */}
                    <polyline points={achLine} fill="none" stroke="#059669" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
                    {chartMonths.map((m, i) => (
                      <g key={m.label}>
                        <circle cx={xAt(i)} cy={yAt(m.mt)} r="3.5" fill="#059669" />
                        <text x={xAt(i)} y={yAt(m.mt) - 8} textAnchor="middle" fontSize="10" fontWeight="700" fill="#065f46">
                          {m.mt ? fmt(m.mt) : ""}
                        </text>
                        <text x={xAt(i)} y={H - 8} textAnchor="middle" fontSize="11" fontWeight="600" fill="#6b7280">
                          {m.label}
                        </text>
                      </g>
                    ))}
                  </svg>
                </div>
              );
            })()}
            {/* legend */}
            <div className="flex items-center gap-4 mt-1 text-[11px] font-semibold text-gray-600">
              <span className="flex items-center gap-1.5">
                <span className="inline-block w-5 h-[3px] rounded bg-emerald-600" /> Achieved
              </span>
              <span className="flex items-center gap-1.5">
                <span className="inline-block w-5 border-t-2 border-dashed border-rose-500" /> Target (1.5 MT/mo)
              </span>
            </div>

            {/* Totals */}
            <div className="mt-2 grid grid-cols-2 gap-3">
              <div className="bg-emerald-50 rounded-xl py-1.5 text-center">
                <p className="text-[10px] font-bold text-emerald-700 uppercase">
                  Total Achieved
                </p>
                <p className="text-lg font-extrabold text-emerald-800">
                  {fmt(totalMt)} <span className="text-xs">MT</span>
                </p>
              </div>
              <div className="bg-blue-50 rounded-xl py-1.5 text-center">
                <p className="text-[10px] font-bold text-blue-700 uppercase">
                  Target (Jan–Sep)
                </p>
                <p className="text-lg font-extrabold text-blue-800">
                  12 <span className="text-xs">MT</span>
                </p>
              </div>
            </div>
            <div className="mt-3 w-full h-3 rounded-full bg-gray-200 overflow-hidden">
              <div
                className="h-full bg-emerald-500 transition-all"
                style={{ width: `${pct}%` }}
              />
            </div>
            <p className="mt-1.5 text-xs text-gray-500">
              {fmt(totalMt)} / 12 MT ({Math.round(pct)}%)
              {ringSales.firmName ? ` · ${ringSales.firmName}` : ""}
            </p>

            {/* Why some months aren't in the total */}
            {belowMonths.length > 0 && (
              <div className="mt-3 rounded-xl bg-amber-50 border border-amber-200 px-3 py-2 text-[11px] text-amber-800">
                ⚠️ <b>{belowMonths.map((m) => `${m.label} (${fmt(m.mt)} MT)`).join(", ")}</b>{" "}
                — below the 1.5 MT monthly target, so not counted in <b>Total Achieved</b>{" "}
                (still shown on the graph). Only months with 1.5 MT or more are added to the total.
              </div>
            )}
          </div>
        );
      })()}

      {/* Ajay Jalsa Tour 2026 (May–Dec) from AJAYJABYSYSTEM — Saurashtra + Kutch only */}
      {ajayJalsa && ajayArea && (() => {
        const months = ajayJalsa.months || []; // [{ label, mt }]
        const lastIdx = months.reduce((acc, m, i) => (m.mt > 0 ? i : acc), -1);
        const chartMonths = lastIdx >= 0 ? months.slice(0, lastIdx + 1) : months;
        const totalMt = Number(ajayJalsa.achievedMt) || months.reduce((s, m) => s + m.mt, 0);
        const slab = String(ajayJalsa.slab || "").trim();
        const notQualified = !slab || /not\s*qualif/i.test(slab);
        const slabText = slab.replace(/^[❌✅✔️\s]+/, "") || "Not Qualified";
        const fmt = (v) => Number(v || 0).toFixed(2);
        // Slabs from the scheme PDF (annual target MT, destination, TOD, min monthly MT).
        const SLABS = [
          { mt: 120, dest: "Goa Tour (Single)", tod: "Rs. 75 PMT", monthly: 8 },
          { mt: 200, dest: "Thailand (Single)", tod: "Rs. 100 PMT", monthly: 15 },
          { mt: 350, dest: "Vietnam (Single)", tod: "Rs. 125 PMT", monthly: 25 },
          { mt: 600, dest: "Singapore (Couple)", tod: "Rs. 150 PMT", monthly: 40 },
        ];
        // Qualified slab = worked out from achieved MT (PDF rules). The sheet's
        // Slab column (filled for a few dealers only) is their chosen target.
        const reached = SLABS.filter((sl) => totalMt >= sl.mt).pop() || null;
        const next = SLABS.find((sl) => totalMt < sl.mt) || null;
        const sheetTarget = SLABS.find((sl) => slab.toLowerCase().includes(sl.dest.split(" ")[0].toLowerCase())) || null;
        const target = sheetTarget && totalMt < sheetTarget.mt ? sheetTarget : next || SLABS[SLABS.length - 1];
        const pct = Math.min(100, (totalMt / target.mt) * 100);
        const monthTarget = target.monthly;
        const qualifiedText = reached ? reached.dest : "Not Qualified";
        return (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-md p-4 w-full min-w-0">
            <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
              <h3 className="font-extrabold text-gray-800 flex items-center gap-2 text-base">
                ✈️ Ajay - Jalsa Tour 2026
              </h3>
              <div className="flex items-center gap-2 flex-wrap">
                <a
                  href="/jalsa-tour-friends-steel-tech-2026.pdf"
                  download="Jalsa Tour Friends Steel Tech 01-05-2026 to 31-12-2026.pdf"
                  className="inline-flex items-center gap-1 text-xs font-bold bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-full transition"
                  title="Download scheme PDF"
                >
                  ⬇ PDF
                </a>
                <span
                  className={`text-xs sm:text-sm font-extrabold px-3 py-1.5 rounded-full ${
                    reached ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"
                  }`}
                >
                  {reached ? "✅ " + qualifiedText : qualifiedText}
                </span>
              </div>
            </div>
            <p className="text-[11px] text-gray-500 font-semibold">01 May 2026 – 31 Dec 2026</p>

            {(() => {
              const W = 720, H = 150, padL = 30, padR = 16, padT = 18, padB = 22;
              const iw = W - padL - padR;
              const ih = H - padT - padB;
              const n = chartMonths.length;
              const maxY = Math.max(monthTarget, ...chartMonths.map((m) => m.mt)) * 1.2;
              const xAt = (i) => padL + (n <= 1 ? iw / 2 : (i / (n - 1)) * iw);
              const yAt = (v) => padT + ih - (Math.max(0, v) / maxY) * ih;
              const line = chartMonths.map((m, i) => `${xAt(i)},${yAt(m.mt)}`).join(" ");
              const tY = yAt(monthTarget);
              return (
                <div className="mt-3 overflow-x-auto">
                  <svg viewBox={`0 0 ${W} ${H}`} className="w-full min-w-[420px]">
                    <line x1={padL} y1={padT + ih} x2={padL + iw} y2={padT + ih} stroke="#e5e7eb" strokeWidth="1" />
                    {/* Minimum monthly quantity for the next slab */}
                    <line x1={padL} y1={tY} x2={padL + iw} y2={tY} stroke="#f43f5e" strokeWidth="2" strokeDasharray="6 5" />
                    <text x={padL + iw} y={tY - 5} textAnchor="end" fontSize="11" fontWeight="700" fill="#f43f5e">
                      {monthTarget} MT
                    </text>
                    <polyline points={line} fill="none" stroke="#2563eb" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
                    {chartMonths.map((m, i) => (
                      <g key={m.label}>
                        <circle cx={xAt(i)} cy={yAt(m.mt)} r="3.5" fill="#2563eb" />
                        <text x={xAt(i)} y={yAt(m.mt) - 8} textAnchor="middle" fontSize="10" fontWeight="700" fill="#1e3a8a">
                          {m.mt ? fmt(m.mt) : ""}
                        </text>
                        <text x={xAt(i)} y={H - 8} textAnchor="middle" fontSize="11" fontWeight="600" fill="#6b7280">
                          {m.label}
                        </text>
                      </g>
                    ))}
                  </svg>
                </div>
              );
            })()}
            <div className="flex items-center gap-4 mt-1 text-[11px] font-semibold text-gray-600">
              <span className="flex items-center gap-1.5">
                <span className="inline-block w-5 h-[3px] rounded bg-blue-600" /> Monthly purchase (MT)
              </span>
              <span className="flex items-center gap-1.5">
                <span className="inline-block w-5 border-t-2 border-dashed border-rose-500" /> Min. monthly ({monthTarget} MT)
              </span>
            </div>

            <div className="mt-2 grid grid-cols-2 gap-3">
              <div className="bg-blue-50 rounded-xl py-1.5 text-center">
                <p className="text-[10px] font-bold text-blue-700 uppercase">Total Achieved</p>
                <p className="text-lg font-extrabold text-blue-800">
                  {fmt(totalMt)} <span className="text-xs">MT</span>
                </p>
              </div>
              <div className={`rounded-xl py-1.5 text-center ${reached ? "bg-emerald-50" : "bg-gray-50"}`}>
                <p className={`text-[10px] font-bold uppercase ${reached ? "text-emerald-700" : "text-gray-500"}`}>
                  Qualified Slab
                </p>
                <p className={`text-sm font-extrabold mt-0.5 ${reached ? "text-emerald-800" : "text-gray-600"}`}>
                  {qualifiedText}
                </p>
                {!notQualified && sheetTarget && (
                  <p className="text-[10px] text-gray-500">Target: {slabText}</p>
                )}
              </div>
            </div>
            <div className="mt-3 w-full h-3 rounded-full bg-gray-200 overflow-hidden">
              <div className="h-full bg-blue-500 transition-all" style={{ width: `${pct}%` }} />
            </div>
            <p className="mt-1.5 text-xs text-gray-500">
              {fmt(totalMt)} / {target.mt} MT ({Math.round(pct)}%)
              {totalMt < target.mt
                ? ` · ${fmt(target.mt - totalMt)} MT more for ${target.dest}`
                : " · Top slab reached"}
              {ajayJalsa.firmName ? ` · ${ajayJalsa.firmName}` : ""}
            </p>

            {/* Slabs (scheme PDF) */}
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
              {SLABS.map((sl) => {
                const done = totalMt >= sl.mt;
                const isNext = target === sl && !done;
                return (
                  <div
                    key={sl.mt}
                    className={`rounded-lg border px-2 py-1.5 text-center ${
                      done
                        ? "bg-emerald-50 border-emerald-300"
                        : isNext
                          ? "bg-blue-50 border-blue-300"
                          : "bg-gray-50 border-gray-200"
                    }`}
                  >
                    <p className="text-sm font-extrabold text-gray-800">{sl.mt} MT</p>
                    <p className="text-[11px] font-semibold text-gray-700 leading-tight">{sl.dest}</p>
                    <p className="text-[10px] text-gray-500">TOD {sl.tod}</p>
                    <p className="text-[10px] text-gray-500">Min {sl.monthly} MT/month</p>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}
      </div>

      {/* Feedback CTA — small floating pill in the BOTTOM-LEFT corner
          (bottom-right already has the WhatsApp button). */}
      <button
        type="button"
        onClick={onFeedbackClick}
        title="Share your feedback"
        className="fixed left-3 bottom-3 z-40 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white font-bold shadow-lg text-xs sm:text-sm transition active:scale-95"
      >
        💬 <span>Feedback</span>
      </button>
    </div>
  );
}

// One Apps Script endpoint PER product type. We fetch all four in
// parallel and tag each row with its `type` so the tabs filter
// reliably (no need to guess type from the order-number prefix).
// ORDER_SOURCES lives in utils/orders.js so the cart can reuse it.

// Outstanding (dealer-wise) — single endpoint that returns one row per
// GST with: GST | Template (multi-line invoice + delay-charge text) |
// Outstanding (₹). Filtered client-side by the logged-in dealer's GSTs.
const OUTSTANDING_DIRECT_URL =
  "https://script.google.com/macros/s/AKfycbx1rC5DE6enjBU2zhhGuV-2dIQ_CZU6F0fA0yJlRt6fNdwsON5-yYqHijbuMubGYszb/exec";
const OUTSTANDING_CACHE_KEY = "outstanding:v1";
const OUTSTANDING_CACHE_TTL_MS = 5 * 60 * 1000;
let _outstandingMemCache = null;
let _outstandingInFlight = null;

function readOutstandingCache() {
  if (_outstandingMemCache) return _outstandingMemCache;
  try {
    const raw = localStorage.getItem(OUTSTANDING_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.rows || !parsed?.ts) return null;
    _outstandingMemCache = parsed;
    return parsed;
  } catch {
    return null;
  }
}

function writeOutstandingCache(rows) {
  const payload = { rows, ts: Date.now() };
  _outstandingMemCache = payload;
  try {
    localStorage.setItem(OUTSTANDING_CACHE_KEY, JSON.stringify(payload));
  } catch {
    /* quota — non-fatal */
  }
}

function fetchAndCacheOutstanding() {
  if (_outstandingInFlight) return _outstandingInFlight;
  _outstandingInFlight = (async () => {
    try {
      const res = await fetch(OUTSTANDING_DIRECT_URL, { cache: "no-store" });
      const data = await res.json();
      const arr = Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
          ? data.data
          : [];
      writeOutstandingCache(arr);
      return arr;
    } catch (e) {
      console.warn("[outstanding] fetch failed:", e?.message);
      return [];
    } finally {
      _outstandingInFlight = null;
    }
  })();
  return _outstandingInFlight;
}

async function fetchOutstanding() {
  const cached = readOutstandingCache();
  const now = Date.now();
  const fresh = cached && now - cached.ts < OUTSTANDING_CACHE_TTL_MS;
  if (cached) {
    if (!fresh) fetchAndCacheOutstanding();
    return cached.rows;
  }
  return await fetchAndCacheOutstanding();
}

// In-memory + localStorage warm cache so the popup opens INSTANTLY.
// Strategy:
//   1. Dashboard pre-fetches the combined orders sheet at mount.
//   2. Result is stored both in module memory and localStorage.
//   3. A background poller (every 60s while dashboard is open) keeps
//      the cache fresh.
//   4. The Pending / History popups read this cache on open and render
//      instantly; they also kick a fresh fetch in the background so
//      a stale row never sticks around for long.
// v2: combined data from 4 product-type endpoints (Structure, 6mm,
// Binding, Rings). Bumped from v1 to wipe any stale Structure-only
// caches stored before the multi-source rollout.
const ORDERS_CACHE_KEY = "ordersCombined:v2";
const ORDERS_CACHE_TTL_MS = 5 * 60 * 1000; // 5 min — cache stays warm
let _ordersMemCache = null; // { rows, ts }

function readOrdersCache() {
  if (_ordersMemCache) return _ordersMemCache;
  try {
    const raw = localStorage.getItem(ORDERS_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.rows || !parsed?.ts) return null;
    _ordersMemCache = parsed;
    return parsed;
  } catch {
    return null;
  }
}

function writeOrdersCache(rows) {
  const payload = { rows, ts: Date.now() };
  _ordersMemCache = payload;
  try {
    localStorage.setItem(ORDERS_CACHE_KEY, JSON.stringify(payload));
  } catch {
    /* quota errors are non-fatal */
  }
}

// Single in-flight promise — multiple components can call this in
// parallel and they'll all share one network request. The Apps Script
// endpoints are hit in PARALLEL (one per product type) and each row is
// tagged with `__type` so the tabs filter reliably regardless of how
// the order-number column is formatted.
let _ordersInFlight = null;
function fetchAndCacheOrders() {
  if (_ordersInFlight) return _ordersInFlight;
  _ordersInFlight = (async () => {
    try {
      const results = await Promise.all(
        ORDER_SOURCES.map(async (src) => {
          try {
            const res = await fetch(src.url, { cache: "no-store" });
            const data = await res.json();
            const arr = Array.isArray(data)
              ? data
              : Array.isArray(data?.data)
                ? data.data
                : [];
            console.log(
              `[orders] ${src.type}: ${arr.length} rows`,
              arr[0] ? `sample keys: ${Object.keys(arr[0]).join(", ")}` : ""
            );
            // Tag each row with its source product type
            return arr.map((row) =>
              row && typeof row === "object" ? { ...row, __type: src.type } : row
            );
          } catch (e) {
            console.warn(`[orders] ${src.type} fetch failed:`, e?.message);
            return [];
          }
        })
      );
      const merged = results.flat();
      console.log(`[orders] total merged rows: ${merged.length}`);
      if (merged.length > 0) {
        writeOrdersCache(merged);
        return merged;
      }
      return [];
    } catch (e) {
      console.warn("[orders] fetch failed:", e?.message);
      return [];
    } finally {
      _ordersInFlight = null;
    }
  })();
  return _ordersInFlight;
}

// Loose key matcher — handles whitespace, case, punctuation differences
// in Apps Script JSON exports (e.g. "Quantity(kg)" vs "Quantity (Kg)").
const normKey = (k) => String(k).toLowerCase().replace(/[\s.()_\-/]/g, "");
const pickLoose = (r, candidates) => {
  if (!r || typeof r !== "object") return undefined;
  for (const k of candidates) {
    const v = r[k];
    if (v !== undefined && v !== null && v !== "") return v;
  }
  const wanted = candidates.map(normKey);
  for (const key of Object.keys(r)) {
    if (wanted.includes(normKey(key))) {
      const v = r[key];
      if (v !== undefined && v !== null && v !== "") return v;
    }
  }
  return undefined;
};

// Fetches the combined orders sheet — uses the warm cache when fresh,
// otherwise refetches. Always returns immediately if any cache exists
// (even if stale); a background refresh runs in parallel so the next
// caller gets fresh data.
// TMT orders are built by the backend from TMT ORDER DISPATCH SYSTEM and
// come back in /dealer-orders with the other products.
async function fetchCombinedOrders(gsts) {
  return fetchCombinedOrdersBase(gsts);
}

async function fetchCombinedOrdersBase(gsts) {
  // Backend: only this dealer's rows from each product's order sheet.
  try {
    return await fetchDealerOrders(gsts);
  } catch (e) {
    console.warn("[orders] backend dealer-orders failed, using sheet cache:", e?.message);
  }
  const cached = readOrdersCache();
  const now = Date.now();
  const fresh = cached && now - cached.ts < ORDERS_CACHE_TTL_MS;

  if (cached) {
    // We have SOMETHING — return it instantly. If stale, also kick
    // off a background refresh so subsequent opens are fresh.
    if (!fresh) fetchAndCacheOrders();
    return cached.rows;
  }

  // No cache yet — must wait for first fetch.
  const arr = await fetchAndCacheOrders();
  if (arr && arr.length > 0) return arr;

  // Last-ditch fallback: backend cache (uses sheetSync data)
  try {
    const res = await fetch(SHEETS.pendingOrders(gsts), { cache: "no-store" });
    const data = await res.json();
    return Array.isArray(data) ? data : data?.data || [];
  } catch {
    return [];
  }
}

// Parse one raw Apps Script row into the stable shape both popups need.
function parseOrderRow(r) {
  const orderNo =
    pickLoose(r, [
      "ORDER NO.",
      "Order No",
      "ORDER NO",
      "Order Number",
      "orderNo",
      "order",
    ]) || "";
  return {
    raw: r,
    // Source product type (set by fetchAndCacheOrders when merging the
    // 4 Apps Script endpoints). Falls back to "" so the consumer can
    // call detectOrderType() if missing (e.g. backend cache fallback).
    sourceType: r && typeof r === "object" ? r.__type || "" : "",
    // __gst = the dealer GSTIN the backend validated this row against.
    gst: String(
      r?.__gst ||
      pickLoose(r, [
        "GSTIN",
        "GSTIN ",
        "GST IN",
        "GST No",
        "GST NO",
        "GST",
        "GSTIN/UIN",
        "gst",
      ]) || ""
    )
      .trim()
      .toUpperCase(),
    orderNo: String(orderNo || "").trim(),
    quantity: Number(
      pickLoose(r, [
        "Quantity(kg)",
        "Quantity (kg)",
        "Quantity (Kg)",
        "Quantity",
        "Qty",
        "Qty(kg)",
        "Qty (kg)",
        "QTY",
        // 6mm + Binding Wire sheets use "Total Qty"
        "Total Qty",
        "Total Qty(kg)",
        "Total Qty (kg)",
        "TotalQty",
        "Total Quantity",
        // Rings sheet uses "Total Kgs"
        "Total Kgs",
        "Total Kg",
        "TotalKgs",
        "Total kgs",
      ]) || 0
    ),
    dispatchDetail: String(
      pickLoose(r, [
        "Dispatch Detail",
        "Dispatch Details",
        "DispatchDetail",
      ]) || ""
    ).trim(),
    totalDispatch: Number(
      pickLoose(r, [
        "Total Dispatch",
        "Total Dispatched",
        "TotalDispatch",
        "Total Disp",
      ]) || 0
    ),
    pending: Number(
      pickLoose(r, ["Pending", "Pending Qty", "PendingQty", "PENDING"]) || 0
    ),
    dispClose: String(
      pickLoose(r, [
        "95% Disp Close",
        "95 Disp Close",
        "95% Disp. Close",
        "Disp Close",
      ]) || ""
    ).trim(),
    forceClose: String(
      pickLoose(r, [
        "Force Close",
        "ForceClose",
        "FORCE CLOSE",
        "Force-Close",
      ]) || ""
    ).trim(),
    // NEW: Status column added in the 6mm / binding / rings sheets.
    // Falls back to "" for Structure sheet (which doesn't ship it yet).
    status: String(
      pickLoose(r, [
        "Status",
        "STATUS",
        "status",
        "Order Status",
        "Tracking Status",
        "Current Status",
        "Status ",
      ]) || ""
    ).trim(),
  };
}

function isHeaderRow(r) {
  const o = String(r.orderNo || "").trim().toUpperCase();
  return (
    !o ||
    o === "—" ||
    o === "ORDER NO." ||
    o === "ORDER NO" ||
    o === "ORDER NUMBER"
  );
}

function detectOrderType(orderNo) {
  const o = String(orderNo || "").toLowerCase();
  if (o.includes("6mm")) return "6mm";
  if (o.includes("bw")) return "binding";
  if (o.includes("r/")) return "rings";
  return "structure";
}

/* ─── Structure Dispatch / Pre Dispatch ─────────────────────────────
   Dispatch     = STRUCTURE ORDER DISPATCH SYSTEM (RESPONSES tab): one row
                  per dispatch with invoice, vehicle and driver.
   Pre Dispatch = STR LOADING SLIP (DATABASE tab): loading slips.
   Both come from apps-script/StructureDispatch.gs via the backend cache,
   filtered server-side by the dealer's GSTINs. */

// GSTIN validation (format + checksum). Look-alike Greek/Cyrillic letters
// and spaces are cleaned first — same rules as the backend.
const GST_LOOKALIKE = {
  Α: "A", Β: "B", Ε: "E", Ζ: "Z", Η: "H", Ι: "I", Κ: "K", Μ: "M", Ν: "N", Ο: "O", Ρ: "P", Τ: "T", Υ: "Y", Χ: "X",
  А: "A", В: "B", Е: "E", К: "K", М: "M", Н: "H", О: "O", Р: "P", С: "C", Т: "T", Х: "X",
};
const GST_CHARS = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ";
const cleanGstin = (v) =>
  String(v ?? "")
    .toUpperCase()
    .replace(/[-￿]/g, (ch) => GST_LOOKALIKE[ch] || ch)
    .replace(/[^0-9A-Z]/g, "");
const isValidGstin = (g) => {
  if (!/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/.test(g)) return false;
  let sum = 0;
  for (let i = 0; i < 14; i++) {
    const v = GST_CHARS.indexOf(g[i]) * (i % 2 ? 2 : 1);
    sum += Math.floor(v / 36) + (v % 36);
  }
  return GST_CHARS[(36 - (sum % 36)) % 36] === g[14];
};

// The dealer's firms { firmName, gst } with a valid GSTIN — firms cache first
// (only if it has usable GSTs), otherwise a phone-filtered dealers fetch.
async function loadDealerFirms(dealerPhone) {
  const firms = await loadDealerFirmsRaw(dealerPhone);
  return firms
    .map((f) => ({ ...f, gst: cleanGstin(f.gst) }))
    .filter((f) => isValidGstin(f.gst));
}

async function loadDealerFirmsRaw(dealerPhone) {
  try {
    const raw = localStorage.getItem(`firmsCache:v1:${dealerPhone}`);
    if (raw) {
      const firms = (JSON.parse(raw)?.payload?.firms || [])
        .map((f) => ({
          firmName: String(f?.firmName || "").trim(),
          gst: String(f?.gst || "").trim().toUpperCase(),
        }))
        .filter((f) => f.firmName && f.gst);
      if (firms.length) return firms;
    }
  } catch {
    /* fall through */
  }
  try {
    const res = await fetch(SHEETS.dealers(dealerPhone), { cache: "no-store" });
    const data = await res.json().catch(() => []);
    return (Array.isArray(data) ? data : [])
      .map((r) => ({
        firmName: String(
          r?.Particulars || r?.["Firm Name"] || r?.["Firm Name "] || r?.["Company Name"] || ""
        ).trim(),
        gst: String(
          r?.["GSTIN/UIN"] || r?.["GSTIN/UIN "] || r?.["GST No"] || r?.["GST No "] || r?.GST || r?.gst || ""
        )
          .trim()
          .toUpperCase(),
      }))
      .filter((f) => f.firmName && f.gst);
  } catch {
    return [];
  }
}

// Never called without GSTs — the backend returns nothing for an empty filter.
async function fetchStrRows(feed, gsts) {
  const list = (gsts || []).filter(Boolean);
  if (!list.length) return [];
  const path = { dispatch: "dispatch", predispatch: "pre-dispatch", bills: "bills", tmtorders: "tmt-orders" }[feed];
  const res = await fetch(
    `${API_BASE}/api/sheets/${path}?gst=${encodeURIComponent(list.join(","))}`,
    { cache: "no-store" }
  );
  const data = await res.json().catch(() => []);
  return Array.isArray(data) ? data : [];
}

/* Kilos of a parsed order row: what is still pending when the sheet has a
   pending column, else the ordered quantity. */
const QTY_OF = (r) => Number(r?.pending) || Number(r?.quantity) || 0;

// Sheet dates are DD/MM/YYYY [HH:MM:SS] — parse day-first.
const parseDmyMs = (v) => {
  const m = String(v || "").trim().match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2,4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?/);
  if (!m) return 0;
  let yr = +m[3];
  if (yr < 100) yr += 2000;
  const d = new Date(yr, +m[2] - 1, +m[1], +(m[4] || 0), +(m[5] || 0), +(m[6] || 0));
  return isNaN(d.getTime()) ? 0 : d.getTime();
};

/* "saurastra district area wise" sheet — the districts (and Rajkot's areas)
   Ajay Steel's Jalsa Tour is open to. */
const AJAY_TOUR_AREAS = new Set([
  "BHAVNAGAR", "AMRELI", "JUNAGADH", "JAMNAGAR", "PORBANDAR", "GIR SOMNATH",
  "DWARKA", "JASDAN", "ATKOT", "SURENDRANAGAR", "BOTAD", "KUTCH", "RAJKOT",
  "MORBI", "GONDAL", "JETPUR", "DHORAJI", "UPLETA", "JAMKANDORNA", "NIKAVA",
  "LODHIKA", "RAVKI", "SHAPAR", "DERDI", "METODA", "KUVADVA", "SARDHAR",
  "LOTHADA", "TRAMBA",
]);

const DISPATCH_PRODUCT_TABS = [
  { key: "tmt", label: "TMT" },
  { key: "structure", label: "Structure" },
  { key: "6mm", label: "6mm TMT" },
  { key: "binding", label: "Binding Wire" },
  { key: "rings", label: "Rings" },
  { key: "ajaytmt", label: "Ajay TMT" },
  { key: "tmtubs", label: "TMT UBS" },
  { key: "vbrs", label: "VBRS" },
  { key: "fspl", label: "FSPL" },
  { key: "fencing", label: "Fencing" },
];
const DISPATCH_PRODUCT_LABEL = {
  ...Object.fromEntries(DISPATCH_PRODUCT_TABS.map((t) => [t.key, t.label])),
  other: "Other",
};

// Plant-bill ITEM NAME → product tab.
const billProduct = (row) => {
  const item = String(pickLoose(row, ["ITEM NAME"]) || "").toUpperCase();
  const order = String(pickLoose(row, ["ORDER NO.", "Order No"]) || "").toUpperCase().trim();
  if (/^6\s*MM/.test(order)) return "6mm";
  if (item.includes("STRUCT")) return "structure";
  if (item.includes("6MM")) return "6mm";
  if (item.includes("TMT")) return "tmt";
  if (item.includes("BW") || item.includes("BINDING")) return "binding";
  if (item.includes("RING")) return "rings";
  return "other";
};

// Column sets per sheet. Keys are matched loosely (case / space / punctuation).
const col = (label, keys, opts = {}) => ({ label, keys, ...opts });
const C_PRODUCT = col("Product", ["__product"], { product: true });
const C_DATE = col("Date", ["TIMESTAMP", "Time Stamp", "Timestamp", "TimeSatmp"], { date: true });
// Dispatch rows show one date only: the invoice date (sheet timestamp when the
// sheet has no invoice date column).
const C_INV_DATE = col("Date", ["Inv Date", "Invoice Date", "Date of Invoice", "INVOICE DATE", ...C_DATE.keys], { date: true });
const C_ORDER = col("Order No", ["__orderNo", "ORDER NO.", "Order No", "orderno"], { mono: true, pre: true });
const C_VEHICLE = col("Vehicle No", ["VEHICLE NO.", "Vehicle No", "vehiclenumber"], { mono: true });
const C_DRIVER = col("Driver", ["DRIVER MOBILE NO.", "driverno", "Driver No"]);
const C_SIZE = col("Size", ["Size"]);
// Binding Wire's dispatch sheet keeps Through in the unnamed column V.
const C_THROUGH = col("Through", ["through", "Through", "THROUGH", "COL_V"]);
const C_COMPANY = col("Company", ["COMPANY NAME", "Company", "Plant", "PLANT", "plant", "PRODUCT PLANT"]);
// Rate comes from the order sheet (the backend joins it on as __rate).
const C_RATE = col("Basic Rate", ["__rate", "BASIC RATE", "Basic Rate 1", "Basic Rate", "basicrate", "basic", "Rate Basic"], { num: true });
const C_NET_RATE = col("Net Rate", ["__netRate", "netbilling", "Net Billing", "Net Billing Rate", "netbasicbilling", "NET RATE", "Net Rate", "netrate"], { num: true });
const C_DRIVER_NAME = col("Driver", ["Driver Name", "drivername", "DRIVER NAME", "driver name"]);
const C_DRIVER_NO = col("Driver No", ["driverno", "Driver No", "DRIVER MOBILE NO.", "Driver Number", "driver no"], { mono: true });
const C_PAYTERM = col("Pay Terms", ["__payTerms", "paymentTerms", "Payment Terms", "Pay Term", "Pay Terms", "payterms", "Paymentterms"]);
const C_DELIVERY = col("Delivery Area", ["deliveryarea", "Delivery Area", "DELIVRY LOCATION", "Delivery Location", "Delivery location", "Delivery Areas"]);
const C_TRANSPORT = col("Transport", ["transportername", "Transporter Name", "Transport", "TRANSPORTER", "transporter", "Transportation"]);

const C_PLANT = col("Plant", ["PLANT", "Plant"]);
// TMT dispatch sheet keeps one column per size.
const TMT_SIZE_KEYS = ["8 MM", "10 MM", "12 MM", "16MM", "20MM", "25 MM", "32 MM", "(12/16/25MM)"];
const C_SLIP = col("Loading Slip", ["__slip", "Link"], { link: true, linkText: "View slip" });
// The invoice no itself links to the bill PDF (no separate Bill column).
const BILL_LINK_KEYS = ["__bill", "File Copy", "Invoice Copy"];
const C_INVOICE = col(
  "Invoice No",
  ["INVOICE NO.", "Invoice No", "Inv No.", "Invoice no.", "Invoice Number", "invoicenumber"],
  { mono: true, linkFrom: BILL_LINK_KEYS }
);

// Bill lookup: same GST + invoice no (+ company when both sides have it).
const INVOICE_KEYS = ["INVOICE NO.", "Invoice No", "Inv No.", "Invoice no.", "Invoice Number"];
const COMPANY_KEYS = ["COMPANY NAME", "COMPANY", "Company"];
/* "T10/09/26/53848" / "BW-377" → "53848" / "377": the running number both
   the plant bill and the dispatch sheet share. */
const orderTail = (v) =>
  (String(v ?? "")
    .trim()
    // bills append the date ("R-1048 - 13/03/2026") — drop it first
    .replace(/\d{1,2}[-/]\d{1,2}[-/]\d{4}\s*$/, "")
    .replace(/[-\s]+$/, "")
    .match(/(\d+)$/) || ["", ""])[1];
/* Same order no + same vehicle, or + same quantity: plant bills sometimes
   mistype the vehicle (GJ27TJ8889 for GJ27TG8889) and leave the invoice no
   blank, so one key alone is not enough. */
const QTY_MATCH_KEYS = ["CURRENT QTY.", "Current Dispt Qty", "Quantity (Kgs)", "Disp Qty", "DISP QTY", "Invoice Qty"];
const orderKeys = (row) => {
  const tail = orderTail(pickLoose(row, C_ORDER.keys));
  if (!tail) return [];
  const gst = String(row.__gst || "").toUpperCase();
  const vehicle = String(pickLoose(row, C_VEHICLE.keys) || "").replace(/\s+/g, "").toUpperCase();
  const qty = Number(String(pickLoose(row, QTY_MATCH_KEYS) ?? "").replace(/,/g, ""));
  return [
    vehicle ? `${gst}|${tail}|V${vehicle}` : "",
    Number.isFinite(qty) && qty > 0 ? `${gst}|${tail}|Q${qty}` : "",
  ].filter(Boolean);
};

const billKey = (row, withCompany) => {
  const inv = String(pickLoose(row, INVOICE_KEYS) || "").trim().toUpperCase();
  if (!inv) return "";
  const gst = String(row.__gst || "").trim().toUpperCase();
  const company = withCompany ? String(pickLoose(row, COMPANY_KEYS) || "").trim().toUpperCase() : "";
  return `${gst}|${inv}|${company}`;
};

/* The plant bill sheet sometimes holds the same dispatch twice — once with
   the invoice no and once without. Keep the fuller row. */
function dedupeBills(bills) {
  const byKey = new Map();
  const out = [];
  bills.forEach((b) => {
    const tail = orderTail(pickLoose(b, C_ORDER.keys));
    const vehicle = String(pickLoose(b, C_VEHICLE.keys) || "").replace(/\s+/g, "").toUpperCase();
    const qty = String(pickLoose(b, QTY_MATCH_KEYS) ?? "").replace(/,/g, "").trim();
    if (!tail || !vehicle || !qty) {
      out.push(b);
      return;
    }
    const key = `${String(b.__gst || "").toUpperCase()}|${tail}|${vehicle}|${qty}`;
    const seen = byKey.get(key);
    if (!seen) {
      byKey.set(key, b);
      out.push(b);
      return;
    }
    // Later row wins only when it carries an invoice no the first one lacks.
    const has = (r) => String(pickLoose(r, INVOICE_KEYS) || "").trim();
    if (!has(seen) && has(b)) out[out.indexOf(seen)] = b;
  });
  return out;
}

/* Every sheet names its quantity differently; Binding Wire even splits it
   into GI + MS. Kg for every product (Rings' pcs column is ignored). */
const QTY_BY_PRODUCT = {
  structure: ["CURRENT QTY."],
  tmt: ["Current Dispt Qty", "CURRENT QTY."],
  "6mm": ["Quantity (Kgs)"],
  binding: ["GI Quantity (Kgs)", "MS Quantity (Kgs)"],
  rings: ["Disp Qty (In Kg)", "T Qty"],
  vbrs: ["Quantity (Kgs)"],
  fspl: ["Invoice Qty", "DISP QTY"],
  ajaytmt: ["Total Quantity"],
  tmtubs: ["loadedqty"],
  fencing: ["Qty (kg)", "Inv Qty"],
  bills: ["CURRENT QTY."],
};
const NUM = (v) => {
  const n = Number(String(v ?? "").replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
};
function rowQty(row) {
  const keys = QTY_BY_PRODUCT[row.__product];
  if (row.__totalQty) return NUM(row.__totalQty);
  if (!keys) return NUM(pickLoose(row, ["Total Qty", "Qty", "QTY", "Quantity (Kgs)"]));
  // Binding Wire: both columns add up; the others: first one that has a value.
  if (row.__product === "binding") return keys.reduce((s, k) => s + NUM(pickLoose(row, [k])), 0);
  return NUM(pickLoose(row, keys));
}
const sumQty = (rows) => (rows || []).reduce((s, r) => s + rowQty(r), 0);
const kg = (n) => `${Math.round(n).toLocaleString("en-IN")} kg`;

/* One truck against two orders is written as two rows sharing an invoice
   no, vehicle and date. Show it as the one dispatch it is: order numbers
   listed together, quantities and sizes added up, the invoice amount kept as
   it is (it is the invoice's own total, repeated on each row). */
const AMOUNT_KEYS = ["AMOUNT", "Amount", "Bill Amount", "Invoice Amount", "Inv Amount", "amount"];
function groupByInvoice(rows) {
  const byKey = new Map();
  const out = [];
  const num = (v) => Number(String(v ?? "").replace(/,/g, "")) || 0;
  for (const row of rows) {
    const inv = String(pickLoose(row, INVOICE_KEYS) || "").trim().toUpperCase();
    const vehicle = String(pickLoose(row, C_VEHICLE.keys) || "").replace(/\s+/g, "").toUpperCase();
    const date = String(pickLoose(row, C_INV_DATE.keys) || "").trim().split(/\s+/)[0];
    const key = inv && vehicle ? `${row.__product}|${row.__gst}|${inv}|${vehicle}|${date}` : "";
    const first = key && byKey.get(key);
    if (!first) {
      const copy = { ...row };
      if (key) byKey.set(key, copy);
      out.push(copy);
      continue;
    }
    // order numbers, one per line
    const orderKeyName = Object.keys(first).find((k) => C_ORDER.keys.some((c) => normKey(c) === normKey(k)));
    if (orderKeyName) {
      const seen = String(first[orderKeyName] || "").split("\n");
      const add = String(row[orderKeyName] || "").trim();
      if (add && !seen.includes(add)) first[orderKeyName] = [...seen, add].filter(Boolean).join("\n");
    }
    // quantities and per-size columns add up
    for (const k of [...(QTY_BY_PRODUCT[row.__product] || []), ...TMT_SIZE_KEYS, "Total Qty."]) {
      const name = Object.keys(row).find((x) => normKey(x) === normKey(k));
      if (!name || !num(row[name])) continue;
      first[name] = num(first[name]) + num(row[name]);
    }
    // the invoice amount is the same on both rows — keep the larger
    const amtKey = Object.keys(first).find((k) => AMOUNT_KEYS.some((a) => normKey(a) === normKey(k)));
    if (amtKey) first[amtKey] = Math.max(num(first[amtKey]), num(row[amtKey]));
  }
  return out;
}

// Dispatch list = dispatch-sheet rows (each with its bill PDF when found) +
// plant bills that have no dispatch row (many dispatches are only billed),
// tagged with the product from the bill's ITEM NAME. Used by the card count
// and the popup so both show the same number.
function mergeDispatchWithBills(dispatchRows, allBills) {
  const bills = dedupeBills(allBills);
  const billByKey = new Map();
  bills.forEach((b) => {
    [billKey(b, true), billKey(b, false), ...orderKeys(b)].forEach(
      (k) => k && !billByKey.has(k) && billByKey.set(k, b)
    );
  });
  // Full order no per bill order no, so a bill listed on its own still
  // shows "T10/09/26/53848" and not just "53848".
  const fullOrderNo = new Map();
  dispatchRows.forEach((r) => {
    const order = String(pickLoose(r, C_ORDER.keys) || "").trim();
    const tail = orderTail(order);
    if (!tail || order.length === tail.length) return;
    const key = `${r.__product}|${tail}`;
    if (fullOrderNo.has(key) && fullOrderNo.get(key) !== order) fullOrderNo.set(key, "");
    else if (!fullOrderNo.has(key)) fullOrderNo.set(key, order);
  });
  const used = new Set();
  const rows = dispatchRows.map((r) => {
    const bill =
      billByKey.get(billKey(r, true)) ||
      billByKey.get(billKey(r, false)) ||
      orderKeys(r).map((k) => billByKey.get(k)).find(Boolean);
    if (!bill) return r;
    used.add(bill);
    const url = String(pickLoose(bill, ["File Copy"]) || "").trim();
    return url ? { ...r, __bill: url } : r;
  });
  const billedOnly = bills
    .filter((b) => !used.has(b))
    .map((b) => {
      const product = billProduct(b);
      const full = fullOrderNo.get(`${product}|${orderTail(pickLoose(b, C_ORDER.keys))}`);
      return {
        ...b,
        __product: product,
        __fromBill: true,
        ...(full ? { __orderNo: full } : {}),
      };
    });
  return rows.concat(billedOnly);
}

const STR_VIEWS = {
  dispatch: {
    title: "Dispatch",
    empty: "No dispatches yet.",
    columns: {
      // A company tab mixes products, so this set works for all of them.
      all: [
        C_PRODUCT,
        C_INV_DATE,
        C_ORDER,
        col("Qty (kg)", [], { qty: true, num: true, strong: true }),
        C_INVOICE,
        C_VEHICLE,
        C_DRIVER_NO,
        col("Amount (₹)", ["AMOUNT", "Amount", "Bill Amount", "Invoice Amount", "amount", "Inv Amount"], { num: true }),
        C_RATE,
        C_THROUGH,
        C_COMPANY,
      ],
      structure: [
        C_INV_DATE,
        C_ORDER,
        col("Order Qty (kg)", ["ORDER. QTY(KG)"], { num: true }),
        col("Dispatched (kg)", ["CURRENT QTY."], { num: true, strong: true }),
        col("Balance (kg)", ["BAL QTY"], { num: true }),
        C_INVOICE,
        C_VEHICLE,
        C_DRIVER,
        col("Amount (₹)", ["AMOUNT"], { num: true }),
        C_COMPANY,
        C_RATE,
        C_THROUGH,
        C_NET_RATE,
      ],
      bills: [
        C_INV_DATE,
        C_INVOICE,
        col("Consignee", ["CONSINGEE FIRM NAME", "CONSIGNEE FIRM NAME"]),
        C_ORDER,
        col("Qty (kg)", ["CURRENT QTY."], { num: true, strong: true }),
        C_VEHICLE,
        col("Amount (₹)", ["AMOUNT"], { num: true }),
        C_COMPANY,
        C_RATE,
        C_THROUGH,
        C_NET_RATE,
      ],
      // BINDING WIRE ORDER VS DISP → DISP
      binding: [
        C_INV_DATE,
        C_ORDER,
        col("Qty (kg)", [], { sum: ["GI Quantity (Kgs)", "MS Quantity (Kgs)", "Quantity (Kgs)"], num: true, strong: true }),
        C_INVOICE,
        col("Bill Amount (₹)", ["Bill Amount", "AMOUNT"], { num: true }),
        C_VEHICLE,
        C_DRIVER_NO,
        C_PAYTERM,
        C_PLANT,
        C_RATE,
        C_THROUGH,
        C_NET_RATE,
      ],
      // 6 mm ORDER VS DISP → DISP
      "6mm": [
        C_INV_DATE,
        C_ORDER,
        col("Qty (kg)", ["Quantity (Kgs)", "CURRENT QTY.", "Qty"], { num: true, strong: true }),
        C_INVOICE,
        col("Bill Amount (₹)", ["Bill Amount", "AMOUNT"], { num: true }),
        C_VEHICLE,
        C_DRIVER_NO,
        C_PAYTERM,
        C_PLANT,
        C_COMPANY,
        C_RATE,
        C_THROUGH,
        C_NET_RATE,
      ],
      tmt: [
        C_INV_DATE,
        C_ORDER,
        col("Qty (kg)", ["CURRENT QTY.", "Current Dispt Qty", "Qty", "Total Qty"], { num: true, strong: true }),
        col("Sizes", [], { sizes: TMT_SIZE_KEYS, pre: true }),
        C_INVOICE,
        C_VEHICLE,
        C_DRIVER_NO,
        col("Amount (₹)", ["AMOUNT"], { num: true }),
        C_RATE,
        C_THROUGH,
        C_DELIVERY,
        col("Remarks", ["Remarks", "REMARKS"]),
        C_NET_RATE,
      ],
      // TMT UBS DISPATCH → DATABASE
      tmtubs: [
        C_INV_DATE,
        C_ORDER,
        col("Sizes", ["Desc"], { pre: true }),
        col("Loaded Qty", ["loadedqty"], { num: true, strong: true }),
        C_INVOICE,
        col("Amount (₹)", ["amount"], { num: true }),
        C_VEHICLE,
        C_DRIVER_NO,
        C_COMPANY,
        C_RATE,
        C_THROUGH,
        C_PAYTERM,
        C_DELIVERY,
      ],
      // FSPL SALES PURHCASE NEW SYSTEM → DISPATCH
      fspl: [
        C_INV_DATE,
        C_ORDER,
        C_COMPANY,
        col("Qty (kg)", ["Invoice Qty", "DISP QTY"], { num: true, strong: true }),
        C_INVOICE,
        col("Amount (₹)", ["Invoice Amount"], { num: true }),
        col("Vehicle No", ["Vehiicle Number", "Vehicle Number"], { mono: true }),
        C_DRIVER_NO,
        C_RATE,
        C_THROUGH,
      ],
      // RAJKOT UNIT TMT ORDER VS DISP → DISP
      ajaytmt: [
        C_INV_DATE,
        col("Order No", ["Order No.1", "ORDER NO.", "Order No"], { mono: true }),
        C_COMPANY,
        col("Sizes", ["Size-wise Quantity"], { pre: true }),
        col("Total Qty (kg)", ["Total Quantity", "Order Qty1"], { num: true, strong: true }),
        C_INVOICE,
        col("Amount (₹)", ["Invoice Amount"], { num: true }),
        C_VEHICLE,
        col("Driver No", ["Driver No."], { mono: true }),
        C_RATE,
        col("Pay Terms", ["Payterm1", "__payTerms"]),
        col("Consignee", ["Consingnee Name", "Cosignee", "Consignee"]),
      ],

      // VBRS ORDER VS DISPATCH → DISPATCH
      vbrs: [
        C_INV_DATE,
        C_ORDER,
        col("Qty (kg)", ["Quantity (Kgs)"], { num: true, strong: true }),
        C_INVOICE,
        col("Bill Amount (₹)", ["Bill Amount"], { num: true }),
        C_PAYTERM,
        C_COMPANY,
        C_RATE,
        C_THROUGH,
        C_NET_RATE,
      ],
      // FENCING ORDER → DISPATCH (Invoice Copy links the invoice no to the bill)
      fencing: [
        C_INV_DATE,
        C_ORDER,
        col("Product", ["Product"]),
        col("Make", ["Material Make"]),
        col("Dia / Gauge", ["Dia / Gauge"]),
        col("Mesh Size", ["Mesh Size"]),
        col("Height (ft)", ["Height (ft)"]),
        col("Roll Length (ft)", ["Roll Length (ft)"]),
        col("Pieces", ["Pieces"], { num: true }),
        col("Piece Length", ["Piece Length"]),
        col("Item Detail", ["Item Detail"]),
        col("Bundles", ["Bundles"], { num: true }),
        col("Wt/Bundle (kg)", ["Wt/Bundle (kg)"], { num: true }),
        col("Qty (kg)", ["Qty (kg)", "Inv Qty"], { num: true, strong: true }),
        C_INVOICE,
        col("Amount (₹)", ["Invoice Amount"], { num: true }),
        col("Vehicle No", ["Vehicle Number", "Vehicle No"], { mono: true }),
        col("Base Rate/MT", ["Base Rate/MT"], { num: true }),
        C_PAYTERM,
        col("Special Disc", ["Special Disc"], { num: true }),
        col("Net Rate/MT", ["Net Rate/MT"], { num: true }),
        C_THROUGH,
        C_DELIVERY,
        col("Distributor", ["Distributor"]),
        col("Remarks", ["Remarks"]),
      ],
      // RING ORDER DISPATCH → DATABASE
      rings: [
        C_INV_DATE,
        C_ORDER,
        col("Sizes", ["__sizes"], { pre: true }),
        col("Qty (pcs)", ["Disp Qty(PCS)"], { num: true, strong: true }),
        col("Qty (kg)", ["Disp Qty (In Kg)", "T Qty"], { num: true }),
        C_INVOICE,
        col("Inv Amount (₹)", ["Inv Amount", "AMOUNT"], { num: true }),
        C_VEHICLE,
        C_DRIVER_NO,
        C_COMPANY,
        C_RATE,
        C_THROUGH,
        C_NET_RATE,
      ],
    },
  },
  predispatch: {
    title: "Pre Dispatch",
    empty: "No loading slips yet.",
    columns: {
      all: [C_PRODUCT, C_DATE, C_ORDER, col("Sizes", ["__sizes"], { pre: true }), col("Total Qty", ["__totalQty"], { num: true, strong: true }), C_VEHICLE, C_DRIVER_NO, C_THROUGH, C_PAYTERM, C_DELIVERY, C_TRANSPORT, C_SLIP],
      structure: [
        C_DATE,
        C_ORDER,
        col("Sizes", ["__sizes"], { pre: true }),
        col("Total Qty", ["__totalQty"], { num: true, strong: true }),
        col("Rate (₹)", ["Rate"], { num: true }),
        C_VEHICLE,
        C_DRIVER_NAME,
        C_DRIVER_NO,
        C_COMPANY,
        C_THROUGH,
        C_PAYTERM,
        C_DELIVERY,
        C_TRANSPORT,
        C_SLIP,
        col("Company", ["Company"]),
      ],
      // TMT LOADING SLIP 3.0 → DATABASE
      tmt: [
        C_DATE,
        C_ORDER,
        col("Material", ["materialtype", "Material Type"]),
        col("Position", ["Main Position"]),
        col("Sizes", ["__sizes"], { pre: true }),
        col("Total Qty", ["__totalQty"], { num: true, strong: true }),
        col("Net Billing (₹)", ["netbilling"], { num: true }),
        C_VEHICLE,
        C_DRIVER_NAME,
        C_DRIVER_NO,
        C_COMPANY,
        C_THROUGH,
        C_PAYTERM,
        C_DELIVERY,
        C_TRANSPORT,
        C_SLIP,
      ],
      // Ajay Steel Loading Slip → REQUEST
      ajaytmt: [
        C_DATE,
        C_ORDER,
        col("Sizes", ["__sizes"], { pre: true }),
        col("Total Qty", ["__totalQty"], { num: true, strong: true }),
        col("Rate", ["Rate"], { num: true }),
        col("Position", ["Position"]),
        C_VEHICLE,
        C_DRIVER_NAME,
        C_DRIVER_NO,
        C_COMPANY,
        C_THROUGH,
        C_PAYTERM,
        C_DELIVERY,
        C_TRANSPORT,
        C_SLIP,
        col("Consignee", ["Consignee"]),
      ],
      // BINDING WIRE loading slip → DATABASE
      binding: [
        C_DATE,
        C_ORDER,
        col("Sizes", ["__sizes"], { pre: true }),
        col("Total Qty", ["__totalQty"], { num: true, strong: true }),
        col("Rate", ["Rate"], { num: true }),
        C_VEHICLE,
        C_DRIVER_NAME,
        C_DRIVER_NO,
        C_COMPANY,
        C_THROUGH,
        C_PAYTERM,
        C_DELIVERY,
        C_TRANSPORT,
        C_SLIP,
        C_PLANT,
      ],
      // VBRS LOADING SLIP → DATABASE
      vbrs: [
        C_DATE,
        C_ORDER,
        col("Sizes", ["__sizes"], { pre: true }),
        col("Total Qty", ["__totalQty"], { num: true, strong: true }),
        col("Basic", ["basic"], { num: true }),
        col("Net Billing", ["netbasicbilling", "netbilling"], { num: true }),
        C_VEHICLE,
        C_DRIVER_NO,
        C_COMPANY,
        C_THROUGH,
        C_PAYTERM,
        C_DELIVERY,
        C_TRANSPORT,
        C_SLIP,
      ],
      rings: [
        C_DATE,
        C_ORDER,
        col("Sizes", ["__sizes"], { pre: true }),
        col("Total Qty", ["__totalQty"], { num: true, strong: true }),
        col("Rate (₹)", ["Net Billing Rate"], { num: true }),
        C_VEHICLE,
        C_DRIVER_NAME,
        C_DRIVER_NO,
        C_COMPANY,
        C_THROUGH,
        C_PAYTERM,
        C_DELIVERY,
        C_TRANSPORT,
        C_SLIP,
      ],
      // 6 mm loading slip → DATABASE
      "6mm": [
        C_DATE,
        C_ORDER,
        col("Sizes", ["__sizes"], { pre: true }),
        col("Total Qty", ["__totalQty"], { num: true, strong: true }),
        col("Rate", ["Rate"], { num: true }),
        C_VEHICLE,
        C_DRIVER_NAME,
        C_DRIVER_NO,
        C_COMPANY,
        C_THROUGH,
        C_PAYTERM,
        C_DELIVERY,
        C_TRANSPORT,
        C_SLIP,
        C_PLANT,
      ],
    },
  },
};

const FIRM_KEYS = ["CLIENT FIRM_NAME", "Client Firm_Name", "firmName", "Firm Name", "Firm name"];

const statusPill = (s) => {
  const v = String(s || "").toLowerCase();
  if (/approv|close|complete|done|deliver/.test(v)) return "bg-emerald-100 text-emerald-800";
  if (/reject|cancel|force/.test(v)) return "bg-red-100 text-red-800";
  return "bg-amber-100 text-amber-800";
};

// Sheets use "-----" / "....." as a placeholder — treat that as empty.
const isBlankVal = (v) => {
  const t = String(v ?? "").trim();
  return !t || /^[-–—_.]+$/.test(t.replace(/s+/g, ""));
};

/* A column's raw value. `sum` adds several columns together (Binding Wire
   keeps GI and MS quantity apart), otherwise the first key that has data. */
const cellValue = (c, row) => {
  // qty: each product keeps its quantity in its own column
  if (c.qty) return rowQty(row) || "";
  // sizes: one line per size column that has a quantity ("16MM : 2,585")
  if (c.sizes) {
    return c.sizes
      .map((s) => [s, Number(String(pickLoose(row, [s]) ?? "").replace(/,/g, ""))])
      .filter(([, n]) => Number.isFinite(n) && n > 0)
      .map(([s, n]) => `${s} : ${n.toLocaleString("en-IN")}`)
      .join("\n");
  }
  if (!c.sum) return pickLoose(row, c.keys);
  const parts = c.sum
    .map((k) => Number(String(pickLoose(row, [k]) ?? "").replace(/,/g, "")))
    .filter((n) => Number.isFinite(n) && n !== 0);
  return parts.length ? parts.reduce((a, b) => a + b, 0) : "";
};

/* Bill and loading-slip links open in a viewer on this page. Drive's
   "/view" page cannot be framed — "/preview" can. */
const DocPreviewContext = createContext(null);
const previewUrl = (url) => {
  const m = String(url).match(/\/d\/([\w-]{20,})|[?&]id=([\w-]{20,})/);
  const id = m && (m[1] || m[2]);
  return id ? `https://drive.google.com/file/d/${id}/preview` : String(url);
};

function DocPreview({ url, title, onClose }) {
  useEffect(() => {
    const esc = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose]);
  return (
    <div
      className="fixed inset-0 z-[400] bg-black/70 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-2.5 border-b">
          <h4 className="font-bold text-gray-900">{title || "Bill"}</h4>
          <div className="flex items-center gap-4">
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-700 text-sm font-semibold hover:underline"
            >
              Open in new tab
            </a>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-900 text-2xl leading-none"
              aria-label="Close"
            >
              &times;
            </button>
          </div>
        </div>
        <iframe
          src={previewUrl(url)}
          title={title || "Bill"}
          className="flex-1 w-full"
          allow="autoplay"
        />
      </div>
    </div>
  );
}

function StrCell({ c, row }) {
  const v = c.product ? DISPATCH_PRODUCT_LABEL[row.__product] || row.__product : cellValue(c, row);
  // A row can have the bill but no invoice no — still show the link.
  if (isBlankVal(v) && !(c.linkFrom && pickLoose(row, c.linkFrom))) return "";
  if (c.link) return <LinkCell url={String(v)} c={c} />;
  if (c.linkFrom) {
    const doc = pickLoose(row, c.linkFrom);
    if (doc) return <LinkCell url={String(doc)} c={c} text={isBlankVal(v) ? "" : String(v)} />;
  }
  if (c.status) {
    return (
      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${statusPill(v)}`}>
        {String(v)}
      </span>
    );
  }
  if (c.date) return String(v).trim().split(/\s+/)[0];
  if (c.num) {
    const n = Number(String(v).replace(/,/g, ""));
    return Number.isFinite(n) ? n.toLocaleString("en-IN") : String(v);
  }
  return String(v);
}

function LinkCell({ url, c, text }) {
  const openDoc = useContext(DocPreviewContext);
  if (!openDoc) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        className="text-blue-700 font-semibold hover:underline"
      >
        {text || c.linkText || "View"}
      </a>
    );
  }
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        openDoc({ url, title: c.linkFrom ? "Bill" : c.label });
      }}
      className="text-blue-700 font-semibold hover:underline"
    >
      {text || c.linkText || "View"}
    </button>
  );
}

/* Pre Dispatch sheets have one row per SIZE of a loading slip. Group them
   into one row per slip (Loading Id, else order no + vehicle + date) and put
   every size in a single cell: "Fe 550 32 mm-40 : 3,000". */
const SIZE_KEYS = ["Size", "SIZE", "Size With Qty"];
const QTY_KEYS = ["Qty", "QTY", "Disp Qty(PCS)", "Qty(PCS)"];
function groupLoadingSlips(rows) {
  const num = (v) => Number(String(v ?? "").replace(/,/g, "")) || 0;
  const out = [];
  const byKey = new Map();
  for (const raw of rows) {
    const slip =
      String(pickLoose(raw, ["Loading Id", "LOADING ID", "Loading ID"]) || "").trim() ||
      [pickLoose(raw, C_ORDER.keys), pickLoose(raw, C_VEHICLE.keys), pickLoose(raw, C_DATE.keys)]
        .map((v) => String(v ?? "").trim())
        .join("|");
    const size = String(pickLoose(raw, SIZE_KEYS) ?? "").trim();
    const qty = num(pickLoose(raw, QTY_KEYS));
    const hit = byKey.get(slip);
    if (!hit) {
      const row = { ...raw, __sizes: [], __totalQty: 0 };
      byKey.set(slip, row);
      out.push(row);
      if (size) row.__sizes.push(`${size} : ${qty.toLocaleString("en-IN")}`);
      row.__totalQty = qty;
      continue;
    }
    if (size) hit.__sizes.push(`${size} : ${qty.toLocaleString("en-IN")}`);
    hit.__totalQty += qty;
  }
  return out.map((r) => ({ ...r, __sizes: r.__sizes.join("\n") }));
}

function StrDispatchPopup({ open, onClose, dealerPhone, feed }) {
  const view = STR_VIEWS[feed];
  const [rows, setRows] = useState(null);
  const [dealerFirms, setDealerFirms] = useState([]);
  const [selectedGst, setSelectedGst] = useState("");
  const [activeTab, setActiveTab] = useState("tmt");
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(null);
  // Bill / loading-slip shown on this page.
  const [doc, setDoc] = useState(null);

  useEffect(() => {
    if (!open || !dealerPhone) return;
    let cancelled = false;
    setRows(null);
    setSelectedGst("");
    setActiveTab("tmt");
    setSearch("");
    setExpanded(null);
    (async () => {
      const firms = await loadDealerFirms(dealerPhone);
      if (cancelled) return;
      setDealerFirms(firms);
      const gsts = firms.map((f) => f.gst);
      const [main, bills] = await Promise.all([
        fetchStrRows(feed, gsts),
        feed === "dispatch" ? fetchStrRows("bills", gsts) : Promise.resolve([]),
      ]);
      if (cancelled) return;
      // Dispatches (+ billed-only dispatches); every bill also in the Bills tab.
      const list =
        feed === "dispatch"
          ? mergeDispatchWithBills(groupByInvoice(groupLoadingSlips(main)), bills)
          : groupLoadingSlips(main);
      // Newest first; rows without a date (e.g. 6mm dispatch) keep sheet order, latest first.
      const withMeta = list.map((raw, idx) => ({
        raw,
        idx,
        gst: String(raw.__gst || pickLoose(raw, ["GSTIN", "GST", "gst"]) || "").trim().toUpperCase(),
        ms: parseDmyMs(pickLoose(raw, C_DATE.keys)),
      }));
      withMeta.sort((a, b) => b.ms - a.ms || b.idx - a.idx);
      setRows(withMeta);
    })().catch(() => {
      if (!cancelled) setRows([]);
    });
    return () => {
      cancelled = true;
    };
  }, [open, dealerPhone, feed]);

  // Every row shows its data in columns — no details dropdown.
  const canExpand = false;
  const firmByGst = useMemo(() => new Map(dealerFirms.map((f) => [f.gst, f.firmName])), [dealerFirms]);
  const multiFirm = dealerFirms.length > 1;

  const scoped = useMemo(() => {
    if (!rows) return null;
    return selectedGst ? rows.filter((r) => r.gst === selectedGst) : rows;
  }, [rows, selectedGst]);

  const tabCounts = useMemo(() => {
    const counts = { all: 0 };
    (scoped || []).forEach((r) => {
      counts[r.raw.__product] = (counts[r.raw.__product] || 0) + 1;
      if (r.raw.__product !== "bills") counts.all++;
    });
    return counts;
  }, [scoped]);

  // Same split, but in kilos — that is what the tabs show.
  const tabQty = useMemo(() => {
    const qty = {};
    (scoped || []).forEach((r) => {
      qty[r.raw.__product] = (qty[r.raw.__product] || 0) + rowQty(r.raw);
    });
    return qty;
  }, [scoped]);

  // "All" = every product's dispatches; bills only in their own tab.
  const tabs = useMemo(() => DISPATCH_PRODUCT_TABS, []);

  // No "All" tab — open on the first product that has rows.
  useEffect(() => {
    if (!scoped || (tabCounts[activeTab] || 0) > 0) return;
    const first = tabs.find((t) => (tabCounts[t.key] || 0) > 0);
    if (first) setActiveTab(first.key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scoped, tabCounts]);

  const filtered = useMemo(() => {
    if (!scoped) return null;
    let out = scoped.filter((r) => r.raw.__product === activeTab);
    const q = search.trim().toLowerCase();
    if (q) {
      out = out.filter((r) =>
        Object.values(r.raw).some((v) => String(v || "").toLowerCase().includes(q))
      );
    }
    return out;
  }, [scoped, activeTab, search]);

  // Hide a column when every row of this tab is empty for it.
  const columns = useMemo(() => {
    const all = view.columns[activeTab] || view.columns.all;
    return all.filter(
      (c) =>
        c.product ||
        (filtered || []).some(
          (r) => !isBlankVal(cellValue(c, r.raw)) || (c.linkFrom && pickLoose(r.raw, c.linkFrom))
        )
    );
  }, [view, activeTab, filtered]);
  const shownKeys = useMemo(
    () =>
      new Set(
        columns
          .flatMap((c) => c.keys)
          .concat(FIRM_KEYS, ["GSTIN", "GST", "gst", "__product", "__gst", "__bill"])
          .map(normKey)
      ),
    [columns]
  );

  if (!open) return null;
  const colCount = columns.length;
  return (
    <DocPreviewContext.Provider value={setDoc}>
    {doc && <DocPreview url={doc.url} title={doc.title} onClose={() => setDoc(null)} />}
    <div className="fixed inset-0 z-[300] bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[1440px] max-h-[92vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <h3 className="font-bold text-gray-900 text-lg">{view.title}</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-900 text-2xl leading-none"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="px-4 sm:px-6 pt-4 pb-3 border-b bg-gray-50/50">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <p className="text-sm text-gray-600">
              {filtered == null
                ? "Loading…"
                : `${filtered.length} ${filtered.length === 1 ? "entry" : "entries"} · ${kg(
                    sumQty(filtered.map((r) => r.raw))
                  )}`}
            </p>
            {multiFirm ? (
              <div className="w-full md:w-[420px]">
                <select
                  value={selectedGst}
                  onChange={(e) => setSelectedGst(e.target.value)}
                  className="w-full bg-white border border-gray-300 rounded-xl px-4 py-2 text-sm font-semibold text-gray-800 focus:outline-none focus:ring-2 focus:ring-yellow-400"
                >
                  <option value="">All Firms ({dealerFirms.length})</option>
                  {dealerFirms.map((f) => (
                    <option key={f.gst} value={f.gst}>
                      {f.firmName}
                    </option>
                  ))}
                </select>
              </div>
            ) : dealerFirms[0] ? (
              <div className="md:text-right">
                <p className="text-sm font-bold text-gray-900">{dealerFirms[0].firmName}</p>
              </div>
            ) : null}
          </div>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search order no, vehicle, invoice, size…"
            className="mt-3 w-full bg-white border border-gray-300 rounded-xl px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
          />
          <div className="flex gap-2 mt-3 overflow-x-auto">
            {tabs.map((tab) => {
              const isActive = activeTab === tab.key;
              if (!tabCounts[tab.key] && !isActive) return null;
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => {
                    setActiveTab(tab.key);
                    setExpanded(null);
                  }}
                  className={`px-4 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap transition ${
                    isActive
                      ? "bg-yellow-500 text-white shadow"
                      : "bg-white text-gray-700 border border-gray-200 hover:bg-gray-100"
                  }`}
                >
                  {tab.label} ({kg(tabQty[tab.key] || 0)})
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex-1 overflow-auto px-4 pb-4 min-h-0">
          {filtered == null ? (
            <p className="text-center text-gray-500 py-8">Loading…</p>
          ) : filtered.length === 0 ? (
            <p className="text-center text-gray-500 py-8">{view.empty}</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0 z-20 shadow-[inset_0_-1px_0_rgb(229,231,235)]">
                <tr className="text-left text-gray-700 font-bold">
                  {columns.map((c) => (
                    <th key={c.label} className={`px-3 py-2 whitespace-nowrap ${c.num ? "text-right" : ""}`}>
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, i) => {
                  const isOpen = canExpand && expanded === i;
                  const extra = Object.entries(r.raw).filter(
                    ([k, v]) => String(v || "").trim() && !shownKeys.has(normKey(k))
                  );
                  return (
                    <React.Fragment key={`${r.idx}-${i}`}>
                      <tr
                        onClick={canExpand ? () => setExpanded(isOpen ? null : i) : undefined}
                        className={`border-t border-gray-100 align-top ${canExpand ? "cursor-pointer" : ""} ${
                          i % 2 ? "bg-gray-50" : "bg-white"
                        } hover:bg-yellow-50`}
                        title={canExpand ? "Click for all details" : undefined}
                      >
                        {columns.map((c) => (
                          <td
                            key={c.label}
                            className={`px-3 py-1.5 align-top ${
                              c.pre
                                ? "whitespace-pre-line text-[11px] leading-tight min-w-[150px] max-w-[210px]"
                                : c.num || c.mono
                                  ? "whitespace-nowrap"
                                  : "max-w-[190px] break-words"
                            } ${c.num ? "text-right" : ""} ${c.mono ? "font-mono text-xs" : ""} ${
                              c.strong ? "font-bold text-blue-700" : "text-gray-800"
                            }`}
                          >
                            <StrCell c={c} row={r.raw} />
                          </td>
                        ))}
                      </tr>
                      {isOpen && extra.length > 0 && (
                        <tr className="bg-yellow-50/60">
                          <td colSpan={colCount} className="px-3 py-3">
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1 text-xs">
                              {extra.map(([k, v]) => (
                                <div key={k} className="flex gap-2">
                                  <span className="text-gray-500 shrink-0">{k}:</span>
                                  <span className="text-gray-900 font-semibold break-words">{String(v)}</span>
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
    </DocPreviewContext.Provider>
  );
}

// Everything the sheets know about one order — shown when a Pending / History
// row is clicked: the order row itself, then whatever the order-request,
// loading-slip, dispatch and bill sheets have for the same order no.
const _orderDetailsCache = new Map();

// noDispatch (Order History): hide every dispatch field / section.
const DISPATCH_KEY_RE = /dispatch|pending/i;
const STATUS_KEY_RE = /status|gst/i;
// hideKeys: fields already shown in the table row (not repeated below).
const looseKeyFE = (k) => String(k).toLowerCase().replace(/[^a-z0-9]/g, "");
function OrderDetailsRow({ raw, colSpan, gst, orderNo, noDispatch, hideKeys }) {
  const [more, setMore] = useState(null);

  useEffect(() => {
    const g = cleanGstin(gst);
    if (!orderNo || !isValidGstin(g)) {
      setMore({});
      return;
    }
    const key = `${g}|${orderNo}`;
    if (_orderDetailsCache.has(key)) {
      setMore(_orderDetailsCache.get(key));
      return;
    }
    let cancelled = false;
    fetch(
      `${API_BASE}/api/sheets/order-details?gst=${encodeURIComponent(g)}&order=${encodeURIComponent(orderNo)}`,
      { cache: "no-store" }
    )
      .then((r) => r.json())
      .then((d) => {
        const v = d && typeof d === "object" ? d : {};
        _orderDetailsCache.set(key, v);
        if (!cancelled) setMore(v);
      })
      .catch(() => {
        if (!cancelled) setMore({});
      });
    return () => {
      cancelled = true;
    };
  }, [gst, orderNo]);

  const sections = [
    { title: "Order", rows: [withoutKeys(noDispatch ? stripDispatch(raw) : raw, hideKeys)] },
    { title: "Order Request", rows: (more?.orders || []).map((o) => withoutKeys(o, hideKeys)) },
    ...(noDispatch
      ? []
      : [
          { title: "Loading Slips (Pre Dispatch)", rows: more?.preDispatch || [] },
          { title: "Dispatch", rows: more?.dispatch || [] },
        ]),
    { title: "Bills", rows: more?.bills || [] },
  ].filter((s) => s.rows.some(hasDetailFields)); // no empty headings

  return (
    <tr className="bg-yellow-50/60">
      <td colSpan={colSpan} className="px-3 py-3">
        {/* w-0 min-w-full: details never widen the table columns */}
        <div className="space-y-3 w-0 min-w-full">
          {sections.map((s) => (
            <div key={s.title}>
              <p className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-1">
                {s.title}
                {s.rows.length > 1 ? ` (${s.rows.length})` : ""}
              </p>
              <div className="space-y-2">
                {s.rows.map((row, i) => (
                  <DetailFields key={i} row={row} boxed={s.title !== "Order"} />
                ))}
              </div>
            </div>
          ))}
          {more == null && <p className="text-xs text-gray-500">Loading more details…</p>}
        </div>
      </td>
    </tr>
  );
}

function withoutKeys(row, keys) {
  if (!keys || !keys.length) return row;
  const drop = new Set(keys.map(looseKeyFE));
  return Object.fromEntries(Object.entries(row || {}).filter(([k]) => !drop.has(looseKeyFE(k))));
}

function stripDispatch(row) {
  return Object.fromEntries(Object.entries(row || {}).filter(([k]) => !DISPATCH_KEY_RE.test(k)));
}

const detailEntries = (row) =>
  Object.entries(row || {}).filter(
    ([k, v]) => !k.startsWith("__") && !STATUS_KEY_RE.test(k) && String(v ?? "").trim()
  );
const hasDetailFields = (row) => detailEntries(row).length > 0;

function DetailFields({ row, boxed }) {
  const entries = detailEntries(row);
  if (!entries.length) return null;
  return (
    <div
      className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1 text-xs ${
        boxed ? "bg-white/80 border border-yellow-200 rounded-lg p-2" : ""
      }`}
    >
      {entries.map(([k, v]) => {
        const text = String(v).trim();
        const isLink = /^https?:\/\//i.test(text);
        return (
          <div key={k} className="flex gap-2">
            <span className="text-gray-500 shrink-0">{k.trim()}:</span>
            {isLink ? (
              <a
                href={text}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="text-blue-700 font-semibold hover:underline"
              >
                Open
              </a>
            ) : (
              <span className="text-gray-900 font-semibold break-words whitespace-pre-line">{text}</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ─── Pending orders popup ──────────────────────────────────────────
   New schema (driven by GSTIN, not phone):
     GSTIN | ORDER NO. | Quantity(kg) | Dispatch Detail | Total Dispatch
     | Pending | 95% Disp Close | Force Close
   The dealer's GSTINs are looked up from the firms cache the
   CreativesFiltersContext / Login pre-populates, then we filter the
   pending sheet by those GSTs server-side. Firm dropdown shows
   "Firm Name — GSTIN" so the dealer picks a specific firm if they
   own multiple. */
// Pending Orders columns for products read from a REPORT sheet:
// TMT PENDING REMINDER / STRUCTURE ORDER VS DISPATCH (+ their order-capture sheet).
const PEND_QTY_COLS = [
  { key: "Quantity(kg)", label: "Total Qty", num: true },
  { key: "Total Dispatch", label: "Dispatched", num: true },
  { key: "Pending", label: "Pending", num: true, strong: true },
  { key: "Ageing (days)", label: "Ageing (days)", num: true },
];
const PEND_SHEET_COLS = {
  tmt: [
    { key: "Date of Order Recd.", label: "Date of Order" },
    { key: "ORDER NO.", label: "Order No", mono: true },
    ...PEND_QTY_COLS,
    { key: "Rate Basic", label: "Rate", num: true },
    { key: "Rate Type", label: "Rate Type" },
    { key: "Payment Terms", label: "Pay Terms" },
    { key: "Through", label: "Through" },
    { key: "Comments", label: "Comments" },
    { key: "Remarks", label: "Remarks" },
  ],
  structure: [
    { key: "Date of Order Recd.", label: "Date of Order" },
    { key: "ORDER NO.", label: "Order No", mono: true },
    ...PEND_QTY_COLS,
    { key: "Rate Basic", label: "Rate", num: true },
    { key: "Rate Patti Basic", label: "Rate Patti Basic", num: true },
    { key: "Payment Terms", label: "Pay Terms" },
    { key: "Through", label: "Through" },
    { key: "Comments", label: "Comments" },
    { key: "Remarks", label: "Remarks" },
  ],
  ajaytmt: [
    { key: "Date of Order Recd.", label: "Date of Order" },
    { key: "ORDER NO.", label: "Order No", mono: true },
    ...PEND_QTY_COLS,
    { key: "Rate Basic", label: "Rate", num: true },
    { key: "Rate Type", label: "Type" },
    { key: "Pay Terms", label: "Pay Terms" },
    { key: "Through", label: "Through" },
  ],
  fspl: [
    { key: "Date of Order Recd.", label: "Date of Order" },
    { key: "ORDER NO.", label: "Order No", mono: true },
    { key: "Product", label: "Product" },
    ...PEND_QTY_COLS,
    { key: "Rate Basic", label: "Rate", num: true },
    { key: "Net Rate", label: "Net Rate", num: true },
    { key: "Pay Terms", label: "Pay Terms" },
    { key: "Through", label: "Through" },
  ],
  tmtubs: [
    { key: "Date of Order Recd.", label: "Date of Order" },
    { key: "ORDER NO.", label: "Order No", mono: true },
    ...PEND_QTY_COLS,
    { key: "Rate Basic", label: "Rate", num: true },
    { key: "Net Rate", label: "Net Rate", num: true },
    { key: "Pay Terms", label: "Pay Terms" },
    { key: "Through", label: "Through" },
  ],
  vbrs: [
    { key: "Date of Order Recd.", label: "Date of Order" },
    { key: "ORDER NO.", label: "Order No", mono: true },
    ...PEND_QTY_COLS,
    { key: "Rate Basic", label: "Rate", num: true },
    { key: "Net Rate", label: "Net Rate", num: true },
    { key: "Pay Terms", label: "Pay Terms" },
    { key: "Through", label: "Through" },
  ],
  rings: [
    { key: "Date of Order Recd.", label: "Date of Order" },
    { key: "ORDER NO.", label: "Order No", mono: true },
    ...PEND_QTY_COLS,
    { key: "Rate Basic", label: "Rate", num: true },
    { key: "Net Rate", label: "Net Rate", num: true },
    { key: "Pay Terms", label: "Pay Terms" },
    { key: "Through", label: "Through" },
  ],
  binding: [
    { key: "Date of Order Recd.", label: "Date of Order" },
    { key: "ORDER NO.", label: "Order No", mono: true },
    ...PEND_QTY_COLS,
    { key: "Rate Basic", label: "Rate", num: true },
    { key: "Net Rate", label: "Net Rate", num: true },
    { key: "Pay Terms", label: "Pay Terms" },
    { key: "Through", label: "Through" },
  ],
  "6mm": [
    { key: "Date of Order Recd.", label: "Date of Order" },
    { key: "ORDER NO.", label: "Order No", mono: true },
    ...PEND_QTY_COLS,
    { key: "Rate Basic", label: "Rate", num: true },
    { key: "Net Rate", label: "Net Rate", num: true },
    { key: "Pay Terms", label: "Pay Terms" },
    { key: "Through", label: "Through" },
    { key: "Plant", label: "Plant" },
    { key: "Comments", label: "Comments" },
  ],
};

function PendingOrdersPopup({ open, onClose, dealerPhone }) {
  const [rows, setRows] = useState(null);
  const [dealerFirms, setDealerFirms] = useState([]); // [{ firmName, gst }, ...]
  const [selectedGst, setSelectedGst] = useState(""); // "" = all firms
  // Type tabs match the Profile → My Pending Orders page exactly.
  const [activeTab, setActiveTab] = useState("tmt");
  const [search, setSearch] = useState("");
  // Products read from a REPORT sheet get their own columns (no dropdown).
  // Every product reads its own pending sheet — columns come from it.
  const allSheetCols = PEND_SHEET_COLS[activeTab] || [];
  const TABS = useMemo(
    () => [
      { key: "tmt", label: "TMT" },
      { key: "structure", label: "Structure" },
      { key: "6mm", label: "6mm TMT" },
      { key: "binding", label: "Binding Wire" },
      { key: "rings", label: "Rings" },
      { key: "ajaytmt", label: "Ajay TMT" },
      { key: "vbrs", label: "VBRS" },
      { key: "tmtubs", label: "TMT UBS" },
      { key: "fspl", label: "FSPL" },
    ],
    []
  );

  // Detect order type from the ORDER NO. prefix — same rules as the
  // Profile page so the tabs behave identically.
  const detectType = (orderNo) => {
    const o = String(orderNo || "").toLowerCase();
    if (o.includes("6mm")) return "6mm";
    if (o.includes("bw")) return "binding";
    if (o.includes("r/")) return "rings";
    return "structure";
  };

  // Pull the dealer's firms (name + GSTIN) for THIS phone. The cache
  // may have firms with empty GSTs from older code — if so we must
  // re-fetch from the backend, otherwise we'd send an empty GST filter
  // and the server would return EVERY dealer's pending rows (very bad).
  useEffect(() => {
    if (!open || !dealerPhone) return;
    let cancelled = false;

    const mapDealerRowToFirm = (r) => ({
      firmName: String(
        r?.Particulars ||
          r?.["Firm Name"] ||
          r?.["Firm Name "] ||
          r?.["Company Name"] ||
          ""
      ).trim(),
      gst: String(
        r?.["GSTIN/UIN"] ||
          r?.["GSTIN/UIN "] ||
          r?.["GST No"] ||
          r?.["GST No "] ||
          r?.GST ||
          r?.gst ||
          ""
      )
        .trim()
        .toUpperCase(),
    });

    const loadFirms = async () => {
      // 1. Try cache, but ONLY trust it if every firm has a non-empty GST.
      try {
        const raw = localStorage.getItem(`firmsCache:v1:${dealerPhone}`);
        if (raw) {
          const parsed = JSON.parse(raw);
          const firms = (parsed?.payload?.firms || [])
            .map((f) => ({
              firmName: String(f?.firmName || "").trim(),
              gst: String(f?.gst || "").trim().toUpperCase(),
            }))
            .filter((f) => f.firmName && f.gst);
          if (firms.length && !cancelled) {
            setDealerFirms(firms);
            return;
          }
        }
      } catch {
        /* fall through */
      }
      // 2. Cache missing or had no usable GSTs — fetch dealers fresh
      //    (server-side phone filter, so this is small + fast).
      try {
        const res = await fetch(SHEETS.dealers(dealerPhone), {
          cache: "no-store",
        });
        const data = await res.json().catch(() => []);
        const firms = (Array.isArray(data) ? data : [])
          .map(mapDealerRowToFirm)
          .filter((f) => f.firmName && f.gst);
        if (!cancelled) setDealerFirms(firms);
      } catch {
        if (!cancelled) setDealerFirms([]);
      }
    };
    loadFirms();
    return () => {
      cancelled = true;
    };
  }, [open, dealerPhone]);

  // Once we know the dealer's GSTs, fetch the pending sheet filtered
  // by all of them. CRITICAL: never send an empty gst param — the
  // backend would interpret that as "no filter" and dump every
  // dealer's pending rows. If we have zero valid GSTs we show the
  // empty state instead.
  useEffect(() => {
    if (!open) return;
    if (dealerFirms.length === 0) {
      setRows(null);
      return;
    }
    setSelectedGst("");
    const gsts = dealerFirms
      .map((f) => String(f.gst || "").trim().toUpperCase())
      .filter(Boolean);
    if (gsts.length === 0) {
      // No valid GSTs for this dealer — render empty rather than
      // accidentally exposing other dealers' data.
      setRows([]);
      return;
    }
    setRows(null);
    (async () => {
      const arr = await fetchCombinedOrders(gsts);
      // Log the very first row so we can see what the Apps Script
      // actually returns. Helps debug field-name mismatches.
      if (arr.length > 0 && typeof arr[0] === "object") {
        console.log("[pending-orders] first row sample:", arr[0]);
      }
      const allowed = new Set(gsts);
      const parsed = arr.map((raw) => {
        const r = parseOrderRow(raw);
        // Prefer the source tag set by fetchAndCacheOrders (one Apps
        // Script URL per product type) — that's authoritative. Only
        // fall back to order-no sniffing if the row came from the
        // backend cache and has no __type.
        r.type = r.sourceType || detectOrderType(r.orderNo);
        return r;
      });
      const afterHeader = parsed.filter((r) => !isHeaderRow(r));
      const afterGst = afterHeader.filter((r) => allowed.has(r.gst));
      // Pending tab = truly open orders only:
      //   pending > 0 AND 95% Disp Close blank AND Force Close blank
      //   AND not Rejected.
      // Any closure marker (95% close / force close / rejected) → History.
      const mapped = afterGst.filter(
        (r) =>
          !r.raw?.__historyOnly &&
          Number(r.pending || 0) > 0 &&
          !String(r.dispClose || "").trim() &&
          !String(r.forceClose || "").trim() &&
          !/reject|close|forced|cancel/.test(String(r.status || "").toLowerCase())
      );
      // Diagnostic: shows where rows are getting dropped. If `afterGst`
      // is 0 the dealer's GSTs aren't matching what's in the sheets.
      console.log("[pending] dealer GSTs:", Array.from(allowed));
      console.log(
        `[pending] parsed=${parsed.length} afterHeader=${afterHeader.length} afterGst=${afterGst.length} final=${mapped.length}`
      );
      if (afterHeader.length > 0 && afterGst.length === 0) {
        const sheetGsts = Array.from(
          new Set(afterHeader.map((r) => r.gst).filter(Boolean))
        ).slice(0, 8);
        console.warn(
          "[pending] no GST match. Sheet has these GSTs (first 8):",
          sheetGsts
        );
      }
      mapped.sort((a, b) => {
        if (b.pending !== a.pending) return b.pending - a.pending;
        return String(a.orderNo).localeCompare(String(b.orderNo));
      });
      setRows(mapped);
    })();
  }, [open, dealerFirms]);

  // Lookup table from GSTIN → Firm Name so each pending row can show
  // its firm. Built from the full list of the dealer's firms (one entry
  // per GST). Lets us render "Firm Name" next to each row's GSTIN.
  const firmByGst = useMemo(() => {
    const m = new Map();
    dealerFirms.forEach((f) => {
      if (f.gst) m.set(f.gst, f.firmName || f.gst);
    });
    return m;
  }, [dealerFirms]);

  // Firm dropdown shows ALL the dealer's firms (every GST associated
  // with their phone), not just the ones with pending rows. Matches
  // the Profile → My Pending Orders UX where users always see all
  // their firms in the filter.
  const firmOptions = useMemo(
    () =>
      dealerFirms
        .filter((f) => f.gst)
        .map((f) => ({
          label: f.firmName || "Unknown",
          value: f.gst,
        })),
    [dealerFirms]
  );

  // Apply firm + type-tab + search filters together.
  const filtered = useMemo(() => {
    if (!rows) return null;
    let out = rows;
    if (selectedGst) out = out.filter((r) => r.gst === selectedGst);
    out = out.filter((r) => r.type === activeTab);
    const q = search.trim().toLowerCase();
    if (q) {
      out = out.filter(
        (r) =>
          [r.orderNo, r.gst, firmByGst.get(r.gst), r.dispatchDetail, r.status, r.product]
            .some((v) => String(v || "").toLowerCase().includes(q)) ||
          Object.values(r.raw || {}).some((v) => String(v ?? "").toLowerCase().includes(q))
      );
    }
    return out;
  }, [rows, selectedGst, activeTab, search, firmByGst]);

  // Hide a column when every row of this tab is empty for it.
  const sheetCols = useMemo(
    () =>
      allSheetCols.filter((c) => (filtered || []).some((r) => !isBlankVal(r.raw?.[c.key]))),
    [allSheetCols, filtered]
  );

  // Per-type counts for the tab badges (e.g., "Structure (12)")
  const tabCounts = useMemo(() => {
    if (!rows) return {};
    const scope = selectedGst
      ? rows.filter((r) => r.gst === selectedGst)
      : rows;
    const counts = { all: scope.length, structure: 0, tmt: 0, "6mm": 0, binding: 0, rings: 0 };
    scope.forEach((r) => {
      counts[r.type] = (counts[r.type] || 0) + 1;
    });
    return counts;
  }, [rows, selectedGst]);

  // The tabs show kilos; counts stay for deciding which tabs to show.
  const tabQty = useMemo(() => {
    const scope = selectedGst ? (rows || []).filter((r) => r.gst === selectedGst) : rows || [];
    const qty = {};
    scope.forEach((r) => {
      qty[r.type] = (qty[r.type] || 0) + (Number(QTY_OF(r)) || 0);
      qty.all = (qty.all || 0) + (Number(QTY_OF(r)) || 0);
    });
    return qty;
  }, [rows, selectedGst]);

  // No "All" tab — open on the first product that has orders.
  useEffect(() => {
    if (!rows || (tabCounts[activeTab] || 0) > 0) return;
    const first = TABS.find((tab) => (tabCounts[tab.key] || 0) > 0);
    if (first) setActiveTab(first.key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, tabCounts]);

  // Single-firm dealers don't need the firm dropdown or the Firm/GSTIN
  // table columns — they'd just repeat the same value on every row.
  const multiFirm = firmOptions.length > 1;

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[300] bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[1440px] max-h-[92vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <h3 className="font-bold text-gray-900 text-lg">My Pending Orders</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-900 text-2xl leading-none"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* Filter strip — firm dropdown + type tabs, identical layout to
            the Profile → My Pending Orders page. */}
        <div className="px-4 sm:px-6 pt-4 pb-3 border-b bg-gray-50/50">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <p className="text-sm text-gray-600">
              {filtered == null
                ? "Loading…"
                : `${filtered.length} ${filtered.length === 1 ? "order" : "orders"} shown`}
            </p>
            {multiFirm ? (
              <div className="w-full md:w-[420px]">
                <select
                  value={selectedGst}
                  onChange={(e) => setSelectedGst(e.target.value)}
                  className="w-full bg-white border border-gray-300 rounded-xl px-4 py-2 text-sm font-semibold text-gray-800 focus:outline-none focus:ring-2 focus:ring-yellow-400"
                >
                  <option value="">All Firms ({firmOptions.length})</option>
                  {firmOptions.map((f) => (
                    <option key={f.value} value={f.value}>
                      {f.label}
                    </option>
                  ))}
                </select>
              </div>
            ) : dealerFirms[0] ? (
              // Single firm — no dropdown needed, just show the firm
              // name + GST once at the top instead.
              <div className="md:text-right">
                <p className="text-sm font-bold text-gray-900">
                  {dealerFirms[0].firmName}
                </p>
              </div>
            ) : null}
          </div>
          {/* Tabs row — Structure / 6mm TMT / Binding Wire / Rings.
              Counts in the label reflect the currently-selected firm. */}
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search order no, dispatch, status…"
            className="mt-3 w-full bg-white border border-gray-300 rounded-xl px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
          />
          <div className="flex gap-2 mt-3 overflow-x-auto">
            {TABS.map((tab) => {
              const isActive = activeTab === tab.key;
              const count = tabCounts[tab.key] ?? 0;
              if (!count && !isActive) return null;
              const label = kg(tabQty[tab.key] || 0);
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveTab(tab.key)}
                  className={`px-4 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap transition ${
                    isActive
                      ? "bg-yellow-500 text-white shadow"
                      : "bg-white text-gray-700 border border-gray-200 hover:bg-gray-100"
                  }`}
                >
                  {tab.label} ({label})
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex-1 overflow-auto px-4 pb-4 min-h-0">
          {filtered == null ? (
            <p className="text-center text-gray-500 py-8">Loading…</p>
          ) : filtered.length === 0 ? (
            <p className="text-center text-gray-500 py-8">
              No pending orders right now.
            </p>
          ) : (
            <div className="min-w-full">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 sticky top-0 z-20 shadow-[inset_0_-1px_0_rgb(229,231,235)]">
                  <tr className="text-left text-gray-700 font-bold">
                    {sheetCols.map((c) => (
                      <th key={c.key} className={`px-3 py-2 whitespace-nowrap ${c.num ? "text-right" : ""}`}>{c.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r, i) => {
                    return (
                      <React.Fragment key={i}>
                      <tr
                        className={`border-t border-gray-100 align-top ${
                          i % 2 ? "bg-gray-50" : "bg-white"
                        } hover:bg-yellow-50`}
                      >
                        {sheetCols.map((c) => (
                          <td
                            key={c.key}
                            className={`px-3 py-2 text-gray-700 ${
                              c.num
                                ? "whitespace-nowrap text-right"
                                : c.mono
                                  ? "font-mono text-xs whitespace-nowrap"
                                  : "min-w-[110px] max-w-[220px] break-words"
                            } ${c.strong ? "font-bold text-blue-700" : ""}`}
                          >
                            {histCell(r.raw?.[c.key], c.num)}
                          </td>
                        ))}
                      </tr>
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Order History popup ───────────────────────────────────────────
   Same data source as PendingOrdersPopup (one combined sheet), but
   shows rows where Pending === 0 — those are completed/dispatched
   orders. Design mirrors the pending popup exactly: title + firm
   dropdown row, type tabs row, then the table.                       */
// Order History extra columns per product. Structure → STRUCTURE ORDER
// CAPTURE SYSTEM fields the backend joins in by ORDER NO. (first = date).
// History cell: numbers in one format ("54300" / "56,800" → "54,300" / "56,800").
const histCell = (v, num) => {
  const t = String(v ?? "").trim();
  if (isBlankVal(t)) return "";
  if (!num) return t;
  const n = Number(t.replace(/[,₹\s]/g, ""));
  return Number.isFinite(n) ? n.toLocaleString("en-IN") : t;
};

const HIST_EXTRA_COLS = {
  structure: [
    { key: "Date of Order Recd.", label: "Date of Order" },
    { key: "Through", label: "Through" },
    { key: "Rate Basic", label: "Rate Basic", num: true },
    { key: "Rate Patti Basic", label: "Rate Patti Basic", num: true },
    { key: "Payment Terms", label: "Payment Terms" },
    { key: "Special Discount If any", label: "Special Discount", num: true },
    { key: "Remarks", label: "Remarks" },
  ],
  // TMT → TMT ORDER CAPTURE SYSTEM fields (backend builds TMT orders from it).
  tmt: [
    { key: "Date of Order Recd.", label: "Date of Order" },
    { key: "Rate Basic", label: "Rate Basic", num: true },
    { key: "Special Discount If any", label: "Special Discount", num: true },
    { key: "Rate Type", label: "Rate Type" },
    { key: "Payment Terms", label: "Payment Terms" },
    { key: "Through", label: "Through" },
    { key: "Delivery Area", label: "Delivery Area" },
    { key: "Remarks", label: "Remarks" },
  ],
  // 6mm → 6 mm ORDER (RESPONSE), Approved rows only.
  "6mm": [
    { key: "Date of Order Recd.", label: "Date of Order" },
    { key: "Through", label: "Through" },
    { key: "Basic Rate", label: "Basic Rate", num: true },
    { key: "Discount", label: "Discount", num: true },
    { key: "Net Billing", label: "Net Billing", num: true },
    { key: "Pay Term", label: "Pay Term" },
    { key: "Transportation", label: "Transportation" },
    { key: "Plant", label: "Plant" },
    { key: "Delivery Area", label: "Delivery Area" },
    { key: "Remarks", label: "Remarks", pre: true },
  ],
  // Binding Wire → BINDING WIRE ORDER (RESPONSE), Approved rows only.
  binding: [
    { key: "Date of Order Recd.", label: "Date of Order" },
    { key: "Through", label: "Through" },
    { key: "Basic Rate", label: "Basic Rate", num: true },
    { key: "Discount", label: "Discount", num: true },
    { key: "Net Billing", label: "Net Billing", num: true },
    { key: "Pay Term", label: "Pay Term" },
    { key: "Rate Type", label: "Rate Type" },
    { key: "Transportation", label: "Transportation" },
    { key: "Delivery Area", label: "Delivery Area" },
    { key: "Plant", label: "Plant" },
    { key: "Desc", label: "Size - Qty - Rate", pre: true },
    { key: "Remarks", label: "Remarks", pre: true },
  ],
  // Ajay TMT → RAJKOT UNIT TMT ORDER SYSTEM (DATABASE), Approved rows only.
  ajaytmt: [
    { key: "Date of Order Recd.", label: "Date of Order" },
    { key: "Basic", label: "Basic", num: true },
    { key: "Net Billing", label: "Net Billing", num: true },
    { key: "materialtype", label: "Material" },
    { key: "ratetype", label: "Rate Type" },
    { key: "Paymentterms", label: "Payment Terms" },
    { key: "Through", label: "Through" },
    { key: "Delivery Area", label: "Delivery Area" },
    { key: "Remarks", label: "Remarks", pre: true },
  ],
  // VBRS → VBRS ORDER SYSTEM (DATABASE), Approved rows only.
  vbrs: [
    { key: "Date of Order Recd.", label: "Date of Order" },
    { key: "Basic Channel", label: "Basic", num: true },
    { key: "Net Billing Channel", label: "Net Billing", num: true },
    { key: "Through", label: "Through" },
    { key: "Transportation", label: "Transportation" },
    { key: "Remarks", label: "Remarks" },
  ],
  // TMT UBS ORDER → DATABASE
  tmtubs: [
    { key: "Date of Order Recd.", label: "Date of Order" },
    { key: "Through", label: "Through" },
    { key: "Rate Basic", label: "Basic", num: true },
    { key: "Net Billing", label: "Net Billing", num: true },
    { key: "Rate Type", label: "Rate Type" },
    { key: "Payment Terms", label: "Payment Terms" },
    { key: "Delivery Area", label: "Delivery Area" },
    { key: "Desc", label: "Size - Qty", pre: true },
  ],
  // FSPL SALES PURHCASE NEW SYSTEM → ORDER tab (PIPE / VBRS).
  fspl: [
    { key: "Date of Order Recd.", label: "Date of Order" },
    { key: "Product", label: "Product" },
    { key: "Basic Rate", label: "Basic Rate", num: true },
    { key: "Qty Disc", label: "Qty Disc", num: true },
    { key: "Spl Disc", label: "Spl Disc", num: true },
    { key: "Net Rate", label: "Net Rate", num: true },
    { key: "Pay Terms", label: "Pay Terms" },
    { key: "Through", label: "Through" },
    { key: "Delivery Area", label: "Delivery Area" },
  ],
  // Rings → RING ORDER SYSTEM (DATABASE), Approved rows only.
  rings: [
    { key: "Date of Order Recd.", label: "Date of Order" },
    { key: "Through", label: "Through" },
    { key: "Basic Rate", label: "Basic Rate", num: true },
    { key: "Net Billing Rate", label: "Net Billing Rate", num: true },
    { key: "Pay Terms", label: "Pay Terms" },
    { key: "Delivery Period", label: "Delivery Period" },
    { key: "Transportation", label: "Transportation" },
    { key: "Total Pcs", label: "Total Pcs", num: true },
    { key: "Total Kgs", label: "Total Kgs", num: true },
    { key: "Desc", label: "Size - Qty - Rate", pre: true },
  ],
};

function OrderTrackingPopup({ open, onClose, dealerPhone }) {
  const [rows, setRows] = useState(null);
  const [dealerFirms, setDealerFirms] = useState([]);
  const [selectedGst, setSelectedGst] = useState("");
  const [activeTab, setActiveTab] = useState("tmt");
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(null); // row showing its extra sheet fields
  const TABS = useMemo(
    () => [
      { key: "tmt", label: "TMT" },
      { key: "structure", label: "Structure" },
      { key: "6mm", label: "6mm TMT" },
      { key: "binding", label: "Binding Wire" },
      { key: "rings", label: "Rings" },
      { key: "ajaytmt", label: "Ajay TMT" },
      { key: "vbrs", label: "VBRS" },
      { key: "tmtubs", label: "TMT UBS" },
      { key: "fspl", label: "FSPL" },
    ],
    []
  );

  const detectType = (orderNo) => {
    const o = String(orderNo || "").toLowerCase();
    if (o.includes("6mm")) return "6mm";
    if (o.includes("bw")) return "binding";
    if (o.includes("r/")) return "rings";
    return "structure";
  };

  // Load the dealer's firms (with GSTINs) — same logic as the pending
  // popup. Cache miss falls back to a phone-filtered dealers fetch.
  useEffect(() => {
    if (!open || !dealerPhone) return;
    let cancelled = false;
    const mapDealerRowToFirm = (r) => ({
      firmName: String(
        r?.Particulars ||
          r?.["Firm Name"] ||
          r?.["Firm Name "] ||
          r?.["Company Name"] ||
          ""
      ).trim(),
      gst: String(
        r?.["GSTIN/UIN"] ||
          r?.["GSTIN/UIN "] ||
          r?.["GST No"] ||
          r?.["GST No "] ||
          r?.GST ||
          r?.gst ||
          ""
      )
        .trim()
        .toUpperCase(),
    });
    const load = async () => {
      try {
        const raw = localStorage.getItem(`firmsCache:v1:${dealerPhone}`);
        if (raw) {
          const parsed = JSON.parse(raw);
          const firms = (parsed?.payload?.firms || [])
            .map((f) => ({
              firmName: String(f?.firmName || "").trim(),
              gst: String(f?.gst || "").trim().toUpperCase(),
            }))
            .filter((f) => f.firmName && f.gst);
          if (firms.length && !cancelled) {
            setDealerFirms(firms);
            return;
          }
        }
      } catch {
        /* fall through */
      }
      try {
        const res = await fetch(SHEETS.dealers(dealerPhone), {
          cache: "no-store",
        });
        const data = await res.json().catch(() => []);
        const firms = (Array.isArray(data) ? data : [])
          .map(mapDealerRowToFirm)
          .filter((f) => f.firmName && f.gst);
        if (!cancelled) setDealerFirms(firms);
      } catch {
        if (!cancelled) setDealerFirms([]);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [open, dealerPhone]);

  // Fetch rows where Pending === 0 (= completed/dispatched).
  useEffect(() => {
    if (!open) return;
    if (dealerFirms.length === 0) {
      setRows(null);
      return;
    }
    setSelectedGst("");
    const gsts = dealerFirms
      .map((f) => String(f.gst || "").trim().toUpperCase())
      .filter(Boolean);
    if (gsts.length === 0) {
      setRows([]);
      return;
    }
    setRows(null);
    (async () => {
      // Order History = the order sheets only (backend view=history).
      const arr = await fetchDealerOrders(gsts, "history").catch(() => []);
      const allowed = new Set(gsts);
      const mapped = arr
        .map((raw) => {
          const r = parseOrderRow(raw);
          r.type = r.sourceType || detectOrderType(r.orderNo);
          return r;
        })
        .filter((r) => !isHeaderRow(r))
        .filter((r) => allowed.has(r.gst))
        // History tab = any kind of closure:
        //   pending == 0 OR 95% Disp Close set OR Force Close set OR Rejected
        // Mirror of Pending filter — no row appears in both tabs.
        .filter(
          (r) =>
            r.raw?.__historyOnly ||
            Number(r.pending || 0) === 0 ||
            String(r.dispClose || "").trim() ||
            String(r.forceClose || "").trim() ||
            /reject|close|forced|cancel/.test(String(r.status || "").toLowerCase())
        );
      mapped.sort((a, b) =>
        String(b.orderNo).localeCompare(String(a.orderNo))
      );
      setRows(mapped);
    })();
  }, [open, dealerFirms]);

  // Firm name lookup (same as pending popup)
  const firmByGst = useMemo(() => {
    const m = new Map();
    dealerFirms.forEach((f) => {
      if (f.gst) m.set(f.gst, f.firmName || f.gst);
    });
    return m;
  }, [dealerFirms]);

  const firmOptions = useMemo(
    () =>
      dealerFirms
        .filter((f) => f.gst)
        .map((f) => ({
          label: f.firmName || "Unknown",
          value: f.gst,
        })),
    [dealerFirms]
  );

  const filtered = useMemo(() => {
    if (!rows) return null;
    let out = rows;
    if (selectedGst) out = out.filter((r) => r.gst === selectedGst);
    out = out.filter((r) => r.type === activeTab);
    const q = search.trim().toLowerCase();
    if (q) {
      out = out.filter(
        (r) =>
          [r.orderNo, r.gst, firmByGst.get(r.gst), r.status, r.product]
            .some((v) => String(v || "").toLowerCase().includes(q)) ||
          Object.values(r.raw || {}).some((v) => String(v ?? "").toLowerCase().includes(q))
      );
    }
    return out;
  }, [rows, selectedGst, activeTab, search, firmByGst]);

  const tabCounts = useMemo(() => {
    if (!rows) return {};
    const scope = selectedGst
      ? rows.filter((r) => r.gst === selectedGst)
      : rows;
    const counts = { all: scope.length, structure: 0, tmt: 0, "6mm": 0, binding: 0, rings: 0 };
    scope.forEach((r) => {
      counts[r.type] = (counts[r.type] || 0) + 1;
    });
    return counts;
  }, [rows, selectedGst]);

  // The tabs show kilos; counts stay for deciding which tabs to show.
  const tabQty = useMemo(() => {
    const scope = selectedGst ? (rows || []).filter((r) => r.gst === selectedGst) : rows || [];
    const qty = {};
    scope.forEach((r) => {
      qty[r.type] = (qty[r.type] || 0) + (Number(r.quantity) || 0);
      qty.all = (qty.all || 0) + (Number(r.quantity) || 0);
    });
    return qty;
  }, [rows, selectedGst]);

  // No "All" tab — jump to the first product that has orders.
  useEffect(() => {
    if (!rows || (tabCounts[activeTab] || 0) > 0) return;
    const first = TABS.find((tab) => (tabCounts[tab.key] || 0) > 0);
    if (first) setActiveTab(first.key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, tabCounts]);

  // When the dealer has only one firm the Firm dropdown + the Firm/GSTIN
  // table columns are redundant, so we hide them.
  const multiFirm = firmOptions.length > 1;

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[300] bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[1440px] max-h-[92vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <h3 className="font-bold text-gray-900 text-lg">Order History</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-900 text-2xl leading-none"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="px-4 sm:px-6 pt-4 pb-3 border-b bg-gray-50/50">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <p className="text-sm text-gray-600">
              {filtered == null
                ? "Loading…"
                : `${filtered.length} ${filtered.length === 1 ? "order" : "orders"} shown`}
            </p>
            {multiFirm ? (
              <div className="w-full md:w-[420px]">
                <select
                  value={selectedGst}
                  onChange={(e) => setSelectedGst(e.target.value)}
                  className="w-full bg-white border border-gray-300 rounded-xl px-4 py-2 text-sm font-semibold text-gray-800 focus:outline-none focus:ring-2 focus:ring-yellow-400"
                >
                  <option value="">All Firms ({firmOptions.length})</option>
                  {firmOptions.map((f) => (
                    <option key={f.value} value={f.value}>
                      {f.label}
                    </option>
                  ))}
                </select>
              </div>
            ) : dealerFirms[0] ? (
              // Single firm — no dropdown needed, just show the firm
              // name + GST once at the top instead.
              <div className="md:text-right">
                <p className="text-sm font-bold text-gray-900">
                  {dealerFirms[0].firmName}
                </p>
              </div>
            ) : null}
          </div>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search order no, status…"
            className="mt-3 w-full bg-white border border-gray-300 rounded-xl px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
          />
          <div className="flex gap-2 mt-3 overflow-x-auto">
            {TABS.map((tab) => {
              const isActive = activeTab === tab.key;
              const count = tabCounts[tab.key] ?? 0;
              if (!count && tab.key !== "all" && !isActive) return null;
              const label = kg(tabQty[tab.key] || 0);
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveTab(tab.key)}
                  className={`px-4 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap transition ${
                    isActive
                      ? "bg-emerald-500 text-white shadow"
                      : "bg-white text-gray-700 border border-gray-200 hover:bg-gray-100"
                  }`}
                >
                  {tab.label} ({label})
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex-1 overflow-auto px-4 pb-4 min-h-0">
          {filtered == null ? (
            <p className="text-center text-gray-500 py-8">Loading…</p>
          ) : filtered.length === 0 ? (
            <p className="text-center text-gray-500 py-8">
              No completed orders found yet.
            </p>
          ) : (
            <div className="min-w-full">
              <table className="w-auto text-sm">
                <thead className="bg-gray-50 sticky top-0 z-20 shadow-[inset_0_-1px_0_rgb(229,231,235)]">
                  <tr className="text-left text-gray-700 font-bold">
                    <th className="px-3 py-2 whitespace-nowrap">Order No</th>
                    {HIST_EXTRA_COLS[activeTab] && (
                      <th className="px-3 py-2 whitespace-nowrap">Date of Order</th>
                    )}
                    <th className="px-3 py-2 text-right whitespace-nowrap">Qty (kg)</th>
                    {(HIST_EXTRA_COLS[activeTab] || []).slice(1).map((c) => (
                      <th key={c.key} className={`px-3 py-2 whitespace-nowrap ${c.num ? "text-right" : ""}`}>{c.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r, i) => {
                    // Sheet fields that are not table columns → dropdown (only if any).
                    const extra = r.raw?.__sheet || {};
                    const hasExtra = hasDetailFields(extra);
                    const isOpen = expanded === r;
                    return (
                      <React.Fragment key={i}>
                      <tr
                        onClick={hasExtra ? () => setExpanded(isOpen ? null : r) : undefined}
                        title={hasExtra ? "Click for more details" : undefined}
                        className={`border-t border-gray-100 align-top ${hasExtra ? "cursor-pointer" : ""} ${
                          i % 2 ? "bg-gray-50" : "bg-white"
                        } hover:bg-yellow-50`}
                      >
                        <td className="px-3 py-2 text-gray-800 font-mono text-xs whitespace-nowrap">
                          {r.orderNo}
                        </td>
                        {HIST_EXTRA_COLS[activeTab] && (
                          <td className="px-3 py-2 text-gray-700 whitespace-nowrap">
                            {String(r.raw?.[HIST_EXTRA_COLS[activeTab][0].key] || "—")}
                          </td>
                        )}
                        <td className="px-3 py-2 text-right text-gray-800 whitespace-nowrap">
                          {Number(r.quantity || 0).toLocaleString("en-IN")}
                        </td>
                        {(HIST_EXTRA_COLS[activeTab] || []).slice(1).map((c) => (
                          <td key={c.key} className={`px-3 py-2 text-gray-700 ${
                            c.num
                              ? "whitespace-nowrap text-right"
                              : c.pre
                                ? "whitespace-pre-line text-xs min-w-[180px] max-w-[280px]"
                                : "min-w-[110px] max-w-[220px] break-words"
                          }`}>
                            {histCell(r.raw?.[c.key], c.num)}
                          </td>
                        ))}
                      </tr>
                      {isOpen && hasExtra && (
                        <tr className="bg-yellow-50/60">
                          <td colSpan={2 + (HIST_EXTRA_COLS[activeTab] || []).length} className="px-3 py-2">
                            {/* w-0 min-w-full: never widens the table */}
                            <div className="w-0 min-w-full">
                              <DetailFields row={extra} boxed />
                            </div>
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Outstanding popup ─────────────────────────────────────────────
   Shows the firm-wise outstanding breakdown for the logged-in dealer.
   Same data shape as the Google Sheet screenshot:
     GST | Template (multi-line invoice + delay-charge text) | Outstanding
   Driven by OUTSTANDING_DIRECT_URL. Filtered client-side by the dealer's
   GSTs so no one sees other dealers' data. Grand total shown at top. */
/* OUTSTANDING FOR ALL PLANT has one tab per plant; each row is one firm's
   text block: the plant's name, the firm, its delay charges per period, its
   unpaid invoices and that plant's grand total. */
const outNum = (v) => Number(String(v ?? "").replace(/[^\d.-]/g, "")) || 0;

function parseOutstandingBlock(text, plant) {
  const section = { plant, firm: "", delays: [], invoices: [], grandTotal: 0 };
  let started = false; // true once the first data line is seen
  for (const raw of String(text || "").split(/\r?\n/)) {
    const line = raw.trim();
    if (!line) continue;
    // " - " separates the columns; dates ("23-09-26") have no spaces around theirs.
    const parts = line.split(/\s+-\s+/).map((s) => s.trim());
    if (parts.length === 1) {
      // Plant name first, then the firm — keep the last one before the data.
      if (!started) section.firm = line;
      continue;
    }
    if (/^period$/i.test(parts[0]) || /^inv$/i.test(parts[0])) continue; // column headers
    started = true;
    if (/^grand total$/i.test(parts[0])) section.grandTotal = outNum(parts[1]);
    else if (parts.length >= 4) {
      section.invoices.push({
        inv: parts[0],
        date: parts[1],
        amount: outNum(parts[2]),
        terms: parts[3].replace(/[\u2757\uFE0F\u2755]/g, "").trim(),
      });
    } else if (parts.length >= 2 && outNum(parts[1])) {
      section.delays.push({ period: parts[0], charge: outNum(parts[1]) });
    }
  }
  return section;
}
const sectionTotal = (s) => Math.round(s.grandTotal + s.delays.reduce((t, d) => t + d.charge, 0));

function OutstandingPopup({ open, onClose, dealerPhone }) {
  const [rows, setRows] = useState(null);
  const [dealerFirms, setDealerFirms] = useState([]);
  const [selectedGst, setSelectedGst] = useState("");

  // Load dealer's firms (firmName + GSTIN) — same logic as Pending popup
  useEffect(() => {
    if (!open || !dealerPhone) return;
    let cancelled = false;
    const mapDealerRowToFirm = (r) => ({
      firmName: String(
        r?.Particulars ||
          r?.["Firm Name"] ||
          r?.["Firm Name "] ||
          r?.["Company Name"] ||
          ""
      ).trim(),
      gst: String(
        r?.["GSTIN/UIN"] ||
          r?.["GSTIN/UIN "] ||
          r?.["GST No"] ||
          r?.["GST No "] ||
          r?.GST ||
          r?.gst ||
          ""
      )
        .trim()
        .toUpperCase(),
    });
    const load = async () => {
      try {
        const raw = localStorage.getItem(`firmsCache:v1:${dealerPhone}`);
        if (raw) {
          const parsed = JSON.parse(raw);
          const firms = (parsed?.payload?.firms || [])
            .map((f) => ({
              firmName: String(f?.firmName || "").trim(),
              gst: String(f?.gst || "").trim().toUpperCase(),
            }))
            .filter((f) => f.firmName && f.gst);
          if (firms.length && !cancelled) {
            setDealerFirms(firms);
            return;
          }
        }
      } catch {
        /* fall through */
      }
      try {
        const res = await fetch(SHEETS.dealers(dealerPhone), {
          cache: "no-store",
        });
        const data = await res.json().catch(() => []);
        const firms = (Array.isArray(data) ? data : [])
          .map(mapDealerRowToFirm)
          .filter((f) => f.firmName && f.gst);
        if (!cancelled) setDealerFirms(firms);
      } catch {
        if (!cancelled) setDealerFirms([]);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [open, dealerPhone]);

  // Fetch outstanding rows and filter to dealer's GSTs
  useEffect(() => {
    if (!open) return;
    if (dealerFirms.length === 0) {
      setRows(null);
      return;
    }
    setSelectedGst("");
    const gsts = dealerFirms
      .map((f) => String(f.gst || "").trim().toUpperCase())
      .filter(Boolean);
    if (gsts.length === 0) {
      setRows([]);
      return;
    }
    setRows(null);
    const allowed = new Set(gsts);
    (async () => {
      const res = await fetch(
        `${API_BASE}/api/sheets/outstanding?gst=${encodeURIComponent([...allowed].join(","))}`,
        { cache: "no-store" }
      ).catch(() => null);
      const arr = res ? await res.json().catch(() => []) : [];
      const mapped = (Array.isArray(arr) ? arr : [])
        .map((r) => ({
          ...parseOutstandingBlock(r.template, r.plant),
          template: String(r.template || ""),
          gst: String(r.gst || "").trim().toUpperCase(),
        }))
        .filter((s) => sectionTotal(s) > 0);
      mapped.sort((a, b) => sectionTotal(b) - sectionTotal(a));
      setRows(mapped);
    })();
  }, [open, dealerFirms]);

  // Firm name lookup
  const firmByGst = useMemo(() => {
    const m = new Map();
    dealerFirms.forEach((f) => {
      if (f.gst) m.set(f.gst, f.firmName || f.gst);
    });
    return m;
  }, [dealerFirms]);

  const firmOptions = useMemo(
    () =>
      dealerFirms
        .filter((f) => f.gst)
        .map((f) => ({
          label: f.firmName || "Unknown",
          value: f.gst,
        })),
    [dealerFirms]
  );

  const filtered = useMemo(() => {
    if (!rows) return null;
    if (!selectedGst) return rows;
    return rows.filter((r) => r.gst === selectedGst);
  }, [rows, selectedGst]);

  const sections = useMemo(() => filtered || [], [filtered]);
  const totalOutstanding = useMemo(
    () => sections.reduce((sum, s) => sum + sectionTotal(s), 0),
    [sections]
  );
  const plantTotals = useMemo(() => {
    const t = new Map();
    sections.forEach((s) => t.set(s.plant, (t.get(s.plant) || 0) + sectionTotal(s)));
    return [...t.entries()].sort((a, b) => b[1] - a[1]);
  }, [sections]);
  const [plant, setPlant] = useState("");
  useEffect(() => {
    if (!plantTotals.length) return;
    if (!plantTotals.some(([k]) => k === plant)) setPlant(plantTotals[0][0]);
  }, [plantTotals, plant]);
  const shown = useMemo(() => sections.filter((s) => s.plant === plant), [sections, plant]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[300] bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[1440px] max-h-[92vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <h3 className="font-bold text-gray-900 text-lg">
            💰 Outstanding Details
          </h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-900 text-2xl leading-none"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* Filter strip + total */}
        <div className="shrink-0 px-4 sm:px-6 pt-4 pb-3 border-b bg-gradient-to-r from-rose-50 to-orange-50">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <p className="text-xs text-gray-600 font-semibold uppercase tracking-wider">
                Total Outstanding
              </p>
              <p className="text-3xl md:text-4xl font-extrabold text-rose-700 mt-1">
                {filtered == null
                  ? "…"
                  : `₹${totalOutstanding.toLocaleString("en-IN")}`}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {filtered == null
                  ? "Loading…"
                  : `Grand total across ${plantTotals.length} ${
                      plantTotals.length === 1 ? "plant" : "plants"
                    }`}
              </p>
            </div>
            {firmOptions.length > 0 && (
              <div className="w-full md:w-[420px]">
                <select
                  value={selectedGst}
                  onChange={(e) => setSelectedGst(e.target.value)}
                  className="w-full bg-white border border-gray-300 rounded-xl px-4 py-2 text-sm font-semibold text-gray-800 focus:outline-none focus:ring-2 focus:ring-rose-400"
                >
                  <option value="">All Firms ({firmOptions.length})</option>
                  {firmOptions.map((f) => (
                    <option key={f.value} value={f.value}>
                      {f.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>

        {/* One tab per plant, each with that plant's outstanding. */}
        {plantTotals.length > 0 && (
          <div className="shrink-0 flex gap-2 px-4 sm:px-6 py-3 border-b bg-white overflow-x-auto">
            {plantTotals.map(([key, total]) => (
              <button
                key={key}
                type="button"
                onClick={() => setPlant(key)}
                className={`px-4 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap transition ${
                  plant === key
                    ? "bg-rose-500 text-white shadow"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {key} (₹{total.toLocaleString("en-IN")})
              </button>
            ))}
          </div>
        )}

        {/* One card per plant section: its delay charges, its unpaid
            invoices and that section's total. */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 bg-gray-50">
          {filtered == null ? (
            <p className="text-center text-gray-500 py-12">Loading…</p>
          ) : shown.length === 0 ? (
            <p className="text-center text-gray-500 py-12">
              No outstanding amounts for your firms 🎉
            </p>
          ) : (
            <div className="space-y-4">
              {shown.map((s, i) => (
                <div
                  key={`${s.gst}-${s.plant}-${i}`}
                  className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-4 sm:px-5 py-3 bg-gradient-to-r from-rose-50 to-pink-50 border-b border-rose-100">
                    <div>
                      <p className="font-bold text-gray-900">{s.plant}</p>
                      {s.firm && <p className="text-xs text-gray-600">{s.firm}</p>}
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-600 font-semibold uppercase tracking-wider">
                        Outstanding
                      </p>
                      <p className="text-2xl font-extrabold text-rose-700">
                        ₹{sectionTotal(s).toLocaleString("en-IN")}
                      </p>
                    </div>
                  </div>
                  {/* Exactly as the sheet prints it. */}
                  <div className="p-4 sm:p-5">
                    <pre className="text-xs sm:text-sm text-gray-800 whitespace-pre-wrap font-mono leading-relaxed break-words">
                      {s.template.trim()}
                    </pre>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* In-app feedback form. For now submissions are stored to localStorage
   (under "feedback-submissions") and a success message is shown. Later
   this can POST to a Google Sheets webhook — replace the body of
   submitFeedback() with the actual fetch when the webhook URL is ready.
   The shape we save IS the shape we'd send, so no code change needed
   on the form side. */
const FEEDBACK_CATEGORIES = [
  "New Feature Request",
  "Bug / Page Slow / Crash",
  "Confusing UI / Order Flow",
  "New Product Category",
  "Other",
];

// TODO: paste the Apps Script webhook URL here when the Google Sheet is
// set up. The form will then POST to it instead of (or in addition to)
// the localStorage fallback. Suggested sheet columns:
//   Timestamp | Name | Mobile | Firm | Category | Message
const FEEDBACK_WEBHOOK_URL =
  "https://script.google.com/macros/s/AKfycbxHJvlQ3J_BnLo1iTe1v_OcMGp19cbJnjFfLCOQKRkAJO7XWBBfYgx3HxBIOy07d7yi/exec";

async function submitFeedback(payload) {
  // Always save locally so the dealer doesn't lose their note even if
  // the network is dead.
  try {
    const key = "feedback-submissions";
    const existing = JSON.parse(localStorage.getItem(key) || "[]");
    existing.push({ ts: new Date().toISOString(), ...payload });
    localStorage.setItem(key, JSON.stringify(existing));
  } catch {
    /* quota errors are non-fatal */
  }
  // If a webhook is configured, also push to the Google Sheet.
  if (FEEDBACK_WEBHOOK_URL) {
    try {
      await fetch(FEEDBACK_WEBHOOK_URL, {
        method: "POST",
        mode: "no-cors", // Apps Script web apps don't return CORS headers
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
    } catch {
      /* swallow — the localStorage copy is the safety net */
    }
  }
}

function FeedbackPopup({ open, onClose }) {
  // Pre-fill from logged-in dealer for convenience
  const dealer = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("dealer") || "{}");
    } catch {
      return {};
    }
  }, []);

  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [firm, setFirm] = useState("");
  const [firmOptions, setFirmOptions] = useState([]); // [{ firmName, gst }]
  const [category, setCategory] = useState(""); // no default — dealer must pick
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  // Load dealer's firms (firmName list) from the shared firmsCache.
  // Tries cache first, falls back to phone-filtered dealers fetch.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const phone = String(
      dealer?.phone || dealer?.["Contact No"] || ""
    )
      .replace(/\D/g, "")
      .slice(-10);
    if (!phone) return;

    const mapDealerRowToFirm = (r) => ({
      firmName: String(
        r?.Particulars ||
          r?.["Firm Name"] ||
          r?.["Firm Name "] ||
          r?.["Company Name"] ||
          ""
      ).trim(),
      gst: String(
        r?.["GSTIN/UIN"] ||
          r?.["GSTIN/UIN "] ||
          r?.["GST No"] ||
          r?.["GST No "] ||
          r?.GST ||
          r?.gst ||
          ""
      )
        .trim()
        .toUpperCase(),
    });

    (async () => {
      try {
        const raw = localStorage.getItem(`firmsCache:v1:${phone}`);
        if (raw) {
          const parsed = JSON.parse(raw);
          const firms = (parsed?.payload?.firms || [])
            .map((f) => ({
              firmName: String(f?.firmName || "").trim(),
              gst: String(f?.gst || "").trim().toUpperCase(),
            }))
            .filter((f) => f.firmName);
          if (firms.length && !cancelled) {
            setFirmOptions(firms);
            return;
          }
        }
      } catch {
        /* fall through */
      }
      try {
        const res = await fetch(SHEETS.dealers(phone), { cache: "no-store" });
        const data = await res.json().catch(() => []);
        const firms = (Array.isArray(data) ? data : [])
          .map(mapDealerRowToFirm)
          .filter((f) => f.firmName);
        if (!cancelled) setFirmOptions(firms);
      } catch {
        if (!cancelled) setFirmOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, dealer]);

  // Hydrate the form fields each time the popup opens (don't carry over
  // stale state from a previous open).
  useEffect(() => {
    if (!open) return;
    setName(dealer?.name || dealer?.["Contact Person Name"] || "");
    setMobile(
      String(dealer?.phone || dealer?.["Contact No"] || "")
        .replace(/\D/g, "")
        .slice(-10)
    );
    // Default firm: use first option once it loads, else dealer field
    setFirm(
      dealer?.firmName ||
        dealer?.["Particulars"] ||
        dealer?.["Firm Name"] ||
        ""
    );
    setCategory(""); // no default — dealer must pick
    setMessage("");
    setDone(false);
    setSubmitting(false);
  }, [open, dealer]);

  // Once firmOptions load, if firm isn't already one of them, pick the first
  useEffect(() => {
    if (!open || firmOptions.length === 0) return;
    setFirm((current) => {
      if (current && firmOptions.some((f) => f.firmName === current)) {
        return current;
      }
      return firmOptions[0].firmName;
    });
  }, [open, firmOptions]);

  const canSubmit = category && message.trim().length >= 5 && !submitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    await submitFeedback({
      name: name.trim(),
      mobile: mobile.trim(),
      firm: firm.trim(),
      category,
      message: message.trim(),
    });
    setSubmitting(false);
    setDone(true);
    // Auto-close after a moment so the dealer sees the success state.
    setTimeout(() => onClose?.(), 1600);
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[300] bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <h3 className="font-bold text-gray-900 text-lg">
            Share Your Feedback
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-900 text-2xl leading-none"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {done ? (
          // Success state — green checkmark + thank-you. Auto-closes.
          <div className="px-6 py-10 text-center">
            <div className="mx-auto w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center text-3xl">
              ✓
            </div>
            <p className="mt-4 text-lg font-bold text-gray-900">Thank you!</p>
            <p className="mt-1 text-sm text-gray-600">
              Your feedback has been recorded. We'll review it shortly.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-5 space-y-4">
            <p className="text-sm text-gray-700 leading-relaxed">
              We're currently improving the platform. Please share what
              you'd like to see — your input directly shapes the next
              release.
            </p>

            {/* Name + Mobile — locked from logged-in dealer profile.
                Read-only so feedback is always tied to verified account. */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">
                  Your Name
                </label>
                <input
                  type="text"
                  value={name}
                  readOnly
                  className="w-full px-3 py-2 rounded-lg border-2 border-gray-200 bg-gray-50 text-gray-700 outline-none text-sm cursor-not-allowed"
                  placeholder="Your name"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">
                  Mobile
                </label>
                <input
                  type="tel"
                  value={mobile}
                  readOnly
                  className="w-full px-3 py-2 rounded-lg border-2 border-gray-200 bg-gray-50 text-gray-700 outline-none text-sm font-mono cursor-not-allowed"
                  placeholder="10-digit mobile"
                />
              </div>
            </div>

            {/* Firm — dropdown of dealer's registered firms */}
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">
                Firm Name{" "}
                <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              {firmOptions.length > 1 ? (
                <select
                  value={firm}
                  onChange={(e) => setFirm(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border-2 border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none text-sm font-semibold text-gray-800 bg-white"
                >
                  {firmOptions.map((f, i) => (
                    <option key={`${f.firmName}-${i}`} value={f.firmName}>
                      {f.firmName}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  value={firm}
                  readOnly
                  className="w-full px-3 py-2 rounded-lg border-2 border-gray-200 bg-gray-50 text-gray-700 outline-none text-sm cursor-not-allowed"
                  placeholder="Your firm name"
                />
              )}
            </div>

            {/* Category — keeps the feedback organized */}
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">
                What's this about? <span className="text-red-500">*</span>
              </label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border-2 border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none text-sm font-semibold text-gray-800 bg-white"
              >
                <option value="" disabled>
                  Select an option…
                </option>
                {FEEDBACK_CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>

            {/* Message — the main field */}
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">
                Your Feedback <span className="text-red-500">*</span>
              </label>
              <textarea
                rows={5}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Tell us what you'd like to see, or what's not working…"
                className="w-full px-3 py-2 rounded-lg border-2 border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none text-sm resize-none"
              />
              <p className="mt-1 text-[11px] text-gray-500">
                {message.trim().length}/500 characters
              </p>
            </div>

            {/* Submit */}
            <div className="flex gap-2 pt-1">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2.5 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold text-sm transition"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!canSubmit}
                className={`flex-[2] px-4 py-2.5 rounded-xl font-bold text-sm shadow-md transition ${
                  canSubmit
                    ? "bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white active:scale-[0.98]"
                    : "bg-gray-300 text-gray-500 cursor-not-allowed"
                }`}
              >
                {submitting ? "Submitting…" : "Submit Feedback →"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function LoadingBlock() {
  return (
    <div className="flex justify-center py-32">
      <video
        src="/materialwaveloading.webm"
        autoPlay
        loop
        muted
        playsInline
        className="w-40 h-40 object-contain"
      />
    </div>
  );
}

function BookingClosedBlock({ sheetClosed, bookingClosedTitle, subtitle }) {
  return (
    <div className="text-center py-24">
      <img
        src="/images/booking-closed.jpg"
        alt="Booking Closed"
        className="w-56 md:w-72 mx-auto mb-6 opacity-90"
      />
      <h2
        className={`text-xl md:text-2xl font-bold mb-2 ${
          sheetClosed ? "text-yellow-700" : "text-red-700"
        }`}
      >
        {bookingClosedTitle}
      </h2>
      <p className="text-gray-600 text-sm md:text-base">
        {subtitle
          ? subtitle
          : sheetClosed
            ? "Please check back soon."
            : "Booking time is over for today."}
      </p>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="text-center py-32">
      <img
        src="/images/empty.jpg"
        alt="No products"
        className="w-48 mx-auto opacity-60 mb-8 rounded-2xl"
      />
      <h2 className="text-3xl font-bold text-gray-600">No Products Found</h2>
    </div>
  );
}

function ImagePreviewModal({ previewImage, setPreviewImage }) {
  if (!previewImage) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-95 z-[9999] flex items-center justify-center p-8"
      onClick={() => setPreviewImage(null)}
    >
      <motion.img
        initial={{ scale: 0.8 }}
        animate={{ scale: 1 }}
        src={previewImage}
        className="max-h-full max-w-full rounded-3xl"
        onClick={(e) => e.stopPropagation()}
      />
      <button
        onClick={() => setPreviewImage(null)}
        className="absolute top-8 right-8 text-white text-6xl hover:text-gray-400"
      >
        ×
      </button>
    </div>
  );
}

/* =========================
   SCAN ORDER MODAL
========================= */
function ScanOrderModal({
  open,
  onClose,
  cart,
  setCart,
  products,
  sixmmPerMT = 0,
  bindingWirePerKg = 0,
  bindingWireRateMapKg = {},
  ringsBaseRateKg = 0,
  tmt6StockMap = {},
  bindingWireStockMap = {},
  ringsStockMap = {},
  allRingsOptions = [],
  presetItems = null,
}) {
  const [scanPreviewUrl, setScanPreviewUrl] = useState(null);
  const [scanAnalyzing, setScanAnalyzing] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  // ✅ NEW: editable copy of scanResult so user can fix qty before adding to cart
  const [editableResults, setEditableResults] = useState([]);
  const [scanError, setScanError] = useState("");
  const [scanApplied, setScanApplied] = useState(false);

  const cameraRef = useRef(null);
  const driveRef = useRef(null);

  // Gemini runs through our backend proxy (key stays server-side). A key
  // hardcoded here previously shipped in the bundle and got auto-disabled.

  useEffect(() => {
    if (open) {
      setScanPreviewUrl(null);
      setScanResult(null);
      setEditableResults([]);
      setScanError("");
      setScanApplied(false);
      setScanAnalyzing(false);
    }
  }, [open]);

  const normalizeTextLocal = (v) =>
    String(v || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, " ")
      .replace(/kgs?/g, "kg");

  const normalizeSizeForMatch = (v) =>
    String(v || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "")
      .replace(/[×*]/g, "x")
      .replace(/kgs?/g, "kg");

  const normalizeRingText = (v) =>
    String(v || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, " ")
      .replace(/[×*]/g, "x");

  const safeNum = (v) => {
    const n = Number(
      String(v || "")
        .replace(/,/g, "")
        .trim(),
    );
    return Number.isFinite(n) ? n : 0;
  };

  const toKgRateFromMaybeMTLocal = (value) => {
    const n = Number(value || 0);
    if (!n || Number.isNaN(n)) return 0;
    if (n >= 500) return n / 1000;
    return n;
  };

  const resetScan = () => {
    setScanPreviewUrl(null);
    setScanResult(null);
    setEditableResults([]);
    setScanError("");
    setScanApplied(false);
    setScanAnalyzing(false);
    if (driveRef.current) driveRef.current.value = "";
    if (cameraRef.current) cameraRef.current.value = "";
  };

  const handleFile = (file) => {
    if (!file) return;
    setScanError("");
    setScanResult(null);
    setEditableResults([]);
    setScanApplied(false);
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target.result;
      setScanPreviewUrl(dataUrl);
      // Auto-analyse immediately after upload (no separate button click).
      handleAnalyse(dataUrl);
    };
    reader.onerror = () =>
      setScanError("Failed to read file. Please try again.");
    reader.readAsDataURL(file);
    if (driveRef.current) driveRef.current.value = "";
    if (cameraRef.current) cameraRef.current.value = "";
  };

  const preprocessImage = (dataUrl) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        try {
          // Smaller image → fewer vision tokens. The Groq free tier allows
          // only ~8000 tokens/min, and a big photo alone can blow that in one
          // call (→ 429). ~1000px keeps a scan to ~2–2.5k tokens.
          const maxSide = 1000;
          const ratio = Math.min(
            maxSide / img.width,
            maxSide / img.height,
            1, // never upscale
          );
          const width = Math.round(img.width * ratio);
          const height = Math.round(img.height * ratio);
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");
          canvas.width = width;
          canvas.height = height;
          ctx.fillStyle = "#ffffff";
          ctx.fillRect(0, 0, width, height);
          ctx.drawImage(img, 0, 0, width, height);
          const imageData = ctx.getImageData(0, 0, width, height);
          const data = imageData.data;
          for (let i = 0; i < data.length; i += 4) {
            data[i] = Math.min(255, data[i] * 1.08);
            data[i + 1] = Math.min(255, data[i + 1] * 1.08);
            data[i + 2] = Math.min(255, data[i + 2] * 1.08);
          }
          ctx.putImageData(imageData, 0, 0);
          resolve(canvas.toDataURL("image/jpeg", 0.65));
        } catch (err) {
          reject(err);
        }
      };
      img.onerror = reject;
      img.src = dataUrl;
    });

  const rotateImageDataUrl = (dataUrl, angle) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        try {
          const radians = (angle * Math.PI) / 180;
          const swapSides = Math.abs(angle) % 180 !== 0;
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");
          canvas.width = swapSides ? img.height : img.width;
          canvas.height = swapSides ? img.width : img.height;
          ctx.fillStyle = "#ffffff";
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.translate(canvas.width / 2, canvas.height / 2);
          ctx.rotate(radians);
          ctx.drawImage(img, -img.width / 2, -img.height / 2);
          resolve(canvas.toDataURL("image/jpeg", 0.65));
        } catch (err) {
          reject(err);
        }
      };
      img.onerror = reject;
      img.src = dataUrl;
    });

  const runGemini = async (base64Image, mimeType, promptText, _retried = false) => {
    // Backend proxies this to the vision model (key stays server-side).
    const response = await fetch(
      `${API_BASE}/api/ai/gemini`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          contents: [
            {
              role: "user",
              parts: [
                { text: promptText },
                {
                  inline_data: {
                    mime_type: mimeType || "image/jpeg",
                    data: base64Image,
                  },
                },
              ],
            },
          ],
          generationConfig: {
            temperature: 0,
            topK: 1,
            topP: 0.8,
            // Small arrays only — big output would eat the tight token budget.
            maxOutputTokens: 768,
            thinkingConfig: { thinkingBudget: 0 },
          },
        }),
      },
    );
    // Free-tier token/min limit → 429. The budget refills within seconds, so
    // wait briefly and try once more before giving up.
    if (response.status === 429 && !_retried) {
      await new Promise((r) => setTimeout(r, 5000));
      return runGemini(base64Image, mimeType, promptText, true);
    }
    if (!response.ok) {
      const txt = await response.text();
      throw new Error(`AI error: ${response.status} ${txt}`);
    }
    return response.json();
  };

  const getRawModelText = (data) =>
    data?.candidates?.[0]?.content?.parts
      ?.map((p) => p?.text || "")
      .join("\n")
      .trim() || "";

  const safeParseJSONArray = (rawText) => {
    try {
      const cleaned = String(rawText || "")
        .replace(/```json/gi, "")
        .replace(/```/g, "")
        .trim();
      const start = cleaned.indexOf("[");
      const end = cleaned.lastIndexOf("]");
      if (start === -1 || end === -1) return [];
      const jsonOnly = cleaned.slice(start, end + 1);
      const parsed = JSON.parse(jsonOnly);
      return Array.isArray(parsed) ? parsed : [];
    } catch (err) {
      console.error("JSON parse failed:", err);
      return [];
    }
  };

  const mapProductType = (type, rf, size = "") => {
    const t = normalizeTextLocal(type);
    const mode = normalizeTextLocal(rf);
    const s = normalizeTextLocal(size);
    if (t.includes("6mm") && t.includes("tmt")) return "6mm Tmt";
    if (t === "tmt" || t.includes("tmt")) return "TMT";
    if (t.includes("angle")) return "Angle";
    if (t.includes("flat")) return "Flat";
    if (t.includes("round")) return "Round";
    if (t.includes("square")) return "Square";
    if (t.includes("channel")) return "Channel";
    if (t.includes("binding")) return "Binding Wire";
    if (t.includes("wire")) return "Binding Wire";
    if (t.includes("ring")) return "Rings";
    if (mode === "r") return "Round";
    if (mode === "f") return "Flat";
    if (s.includes("mm") && !s.includes("x")) return "Round";
    return String(type || "").trim();
  };

  const findBestProductMatch = (item) => {
    const productType = normalizeTextLocal(item?.productType || "");
    const sizeNorm = normalizeSizeForMatch(item?.size || "");
    const joined = normalizeTextLocal(`${productType} ${item?.size || ""}`);
    let best = null;
    let bestScore = 0;
    for (const p of products || []) {
      const pname = String(p?.name || "").trim();
      if (!pname) continue;
      const pnameNorm = normalizeTextLocal(pname);
      const pnameSizeNorm = normalizeSizeForMatch(pname);
      let score = 0;
      if (joined && pnameNorm === joined) score += 120;
      if (sizeNorm && pnameSizeNorm.includes(sizeNorm)) score += 80;
      if (productType && pnameNorm.includes(productType)) score += 40;
      const joinedWords = joined.split(" ").filter(Boolean);
      const pWords = pnameNorm.split(" ").filter(Boolean);
      const common = joinedWords.filter((w) => pWords.includes(w)).length;
      score += common * 10;
      if (
        sizeNorm &&
        sizeNorm.includes("x") &&
        pnameSizeNorm.replace(/\./g, "") === sizeNorm.replace(/\./g, "")
      ) {
        score += 25;
      }
      if (score > bestScore) {
        best = p;
        bestScore = score;
      }
    }
    return bestScore >= 35 ? best : null;
  };

  const getSpecialCartName = (scanned) => {
    const type = String(scanned?.productType || "")
      .trim()
      .toLowerCase();
    const size = String(scanned?.size || "").trim();
    if (type.includes("binding")) return "Binding Wire - 25 KGS";
    if (type.includes("ring")) return size ? `Rings - ${size}` : "Rings";
    if (type.includes("tmt") || type.includes("6mm")) {
      if (
        size.toLowerCase().includes("30 to 40") ||
        size.toLowerCase().includes("30-40") ||
        size.toLowerCase().includes("30+40")
      ) {
        return "TMT - 30 to 40";
      }
      return "TMT - Mix Size Booking";
    }
    return "";
  };

  const findMatchingRingOption = (scanned) => {
    const type = String(scanned?.productType || "")
      .trim()
      .toLowerCase();
    if (!type.includes("ring")) return null;
    const scannedSize = normalizeRingText(scanned?.size || "");
    if (!scannedSize) return null;
    let best = null;
    let bestScore = 0;
    for (const opt of allRingsOptions || []) {
      const label = normalizeRingText(opt?.label || opt?.key || "");
      if (!label) continue;
      let score = 0;
      if (label === scannedSize) score += 100;
      if (label.includes(scannedSize) || scannedSize.includes(label))
        score += 60;
      const sWords = scannedSize.split(" ").filter(Boolean);
      const lWords = label.split(" ").filter(Boolean);
      const common = sWords.filter((w) => lWords.includes(w)).length;
      score += common * 10;
      if (score > bestScore) {
        best = opt;
        bestScore = score;
      }
    }
    return bestScore >= 20 ? best : null;
  };

  // ✅ FIXED: resolve stock for each product type correctly
  const getSpecialStockForScannedItem = (scanned) => {
    const type = String(scanned?.productType || "")
      .trim()
      .toLowerCase();
    const size = String(scanned?.size || "")
      .trim()
      .toLowerCase();

    if (type.includes("binding")) {
      return Number(bindingWireStockMap?.["25kg"] || 0);
    }

    if (type.includes("ring")) {
      // Try to find matching ring option first
      const ringOpt = findMatchingRingOption(scanned);
      if (ringOpt) {
        const stockByKey = Number(ringsStockMap?.[ringOpt.key] || 0);
        const stockByLabel = Number(ringsStockMap?.[ringOpt.label] || 0);
        if (stockByKey > 0) return stockByKey;
        if (stockByLabel > 0) return stockByLabel;
      }
      // Fallback: direct lookup by size string
      const direct = Number(ringsStockMap?.[scanned?.size] || 0);
      if (direct > 0) return direct;
      const foundKey = Object.keys(ringsStockMap || {}).find(
        (k) =>
          String(k || "")
            .trim()
            .toLowerCase() === size,
      );
      return Number((foundKey && ringsStockMap?.[foundKey]) || 0);
    }

    if (type.includes("tmt") || type.includes("6mm")) {
      if (
        size.includes("30 to 40") ||
        size.includes("30-40") ||
        size.includes("30+40")
      ) {
        return Number(tmt6StockMap?.["30-40"] || 0);
      }
      return Number(tmt6StockMap?.["mix"] || 0);
    }

    return 0;
  };

  const getRingFinalRateAndStock = (scanned) => {
    const ringOpt = findMatchingRingOption(scanned);
    if (!ringOpt) {
      return {
        rate: Number(ringsBaseRateKg || 0),
        stock: Number(getSpecialStockForScannedItem(scanned) || 0),
        meta: null,
      };
    }
    const parityKg = toKgRateFromMaybeMTLocal(ringOpt?.parity || 0);
    const baseKg = Number(ringsBaseRateKg || 0);
    const finalRateKg = baseKg + parityKg;
    const stock =
      Number(ringsStockMap?.[ringOpt?.key] || 0) ||
      Number(ringsStockMap?.[ringOpt?.label] || 0) ||
      Number(getSpecialStockForScannedItem(scanned) || 0);
    return {
      rate: Number(finalRateKg || 0),
      stock: Number(stock || 0),
      meta: {
        sizeName: ringOpt?.label || "",
        rank: Number.isFinite(ringOpt?.rank) ? ringOpt.rank : null,
        parityRaw: Number(ringOpt?.parity || 0),
        parityKg: Number(parityKg || 0),
        baseRateKg: Number(baseKg || 0),
        perPieceWt: Number(
          ringOpt?.perPieceWt || ringOpt?.raw?.["Per Piece Wt."] || 0,
        ),
      },
    };
  };

  const getSpecialRateForScannedItem = (scanned) => {
    const type = String(scanned?.productType || "")
      .trim()
      .toLowerCase();
    if (type.includes("binding")) {
      // MS / GI have separate rates; pick by scanned size, else default (MS).
      const size = String(scanned?.size || "").toLowerCase();
      if (size.includes("gi"))
        return Number(bindingWireRateMapKg?.gi || bindingWirePerKg || 0);
      return Number(bindingWireRateMapKg?.ms || bindingWirePerKg || 0);
    }
    if (type.includes("ring"))
      return Number(getRingFinalRateAndStock(scanned).rate || 0);
    if (type.includes("tmt") || type.includes("6mm"))
      return toKgRateFromMaybeMTLocal(sixmmPerMT || 0);
    return 0;
  };

  const getScannedCartName = (scanned) => {
    const special = getSpecialCartName(scanned);
    if (special) return special;
    if (scanned?.matchedProductName)
      return String(scanned.matchedProductName).trim();
    const type = String(scanned?.productType || "").trim();
    const size = String(scanned?.size || "").trim();
    return `${type}${size ? ` ${size}` : ""}`.trim();
  };

  const buildPrompts = (productCatalog) => {
    const prompt1 = `
You are reading a steel order request form image.

The image may be:
- rotated sideways or upside down
- handwritten
- a simple handwritten list, not a printed form
- missing table borders
- containing short columns like:
  Product Type | Size | Quantity in kg | Remarks

Your task:
Extract visible product rows and return ONLY a valid JSON array.

This may be our "ORDER REQUEST FORM" which uses CHECKBOXES instead of written words:
- The "Product Type" column has 5 checkboxes labelled A C F R S in each row.
  A TICKED box (has an X, tick, cross, scribble or filled mark) selects the type:
    A = Angle, C = Channel, F = Flat, R = Round, S = Square
  Read ONLY the ticked checkbox and put the FULL name in "productType"
  (e.g. if "A" is ticked, productType = "Angle"). If none is ticked, read any
  written product name instead.
- The "Random/Fix" column has 2 checkboxes labelled R F in each row:
    R = Random, F = Fix
  Put the ticked one's FULL word in "randomFix" ("Random" or "Fix"). Empty if none.
- Only include a row if it has a ticked product type OR a size OR a quantity.
  Skip completely blank rows.

Important rules:
- Return ONLY JSON array
- No explanation
- No markdown
- No code fences
- Quantity must be numeric
- Quantity must be in KG
- Convert MT to KG if needed
- Ignore unreadable rows
- Ignore completely empty rows (no ticked box, no size, no qty)
- Handwritten text is allowed
- A checkbox is "ticked" only if it clearly has a mark inside; an empty square is NOT ticked
- If the image looks rotated, mentally read it after rotating to the correct orientation
- confidence must be "high", "medium", or "low"

Fields:
- productType
- size
- randomFix
- qty
- remarks
- paymentTerm
- vehicleNo
- confidence

Form-level fields (read ONCE and repeat the SAME value on EVERY row object):
- "paymentTerm": the form has "Payment Terms" checkboxes — read the TICKED one
  and output its full text. Possible values:
  "100% RTGS Against Proforma Invoice", "Next Day RTGS",
  "10 days RTGS Rate +300", "15 days RTGS rate +450". "" if none ticked.
- "vehicleNo": the value written next to "Vehicle No". "" if blank.

If a field is not visible:
- use "" for randomFix, remarks, paymentTerm, vehicleNo

Known product names:
${productCatalog || "Various steel products"}

Return format:
[
  {
    "productType": "Angle",
    "size": "17 x 1.9",
    "randomFix": "R",
    "qty": 10000,
    "remarks": "",
    "paymentTerm": "Next Day RTGS",
    "vehicleNo": "GJ01AB1234",
    "confidence": "high"
  }
]
`;

    const prompt2 = `
You are reading a handwritten steel requirement note.

The note may contain columns or headings like:
- Product Type
- size
- Quantity in kg
- Remarks

Read the image carefully and extract each item row.

Return ONLY JSON array.

Rules:
- No explanation
- No markdown
- No code fences
- quantity must be numeric in KG
- keep best guess for handwritten text
- use empty string for missing fields
- confidence must be "high", "medium", or "low"

Examples:
[
  {
    "productType": "6mm Tmt",
    "size": "30+40",
    "randomFix": "",
    "qty": 1000,
    "remarks": "Trial quick check",
    "confidence": "high"
  },
  {
    "productType": "Rings",
    "size": "8mm 7x10",
    "randomFix": "",
    "qty": 2000,
    "remarks": "Trial required",
    "confidence": "medium"
  },
  {
    "productType": "Binding wire",
    "size": "25kg",
    "qty": 3000,
    "remarks": "Trial",
    "confidence": "medium"
  }
]
`;

    return [prompt1, prompt2];
  };

  // Map ONE raw item (from Gemini OR from the Order Request Form) into a
  // fully-enriched cart-ready row (matched product, rate, stock, etc.).
  const enrichScannedItem = (item) => {
    const scanBase = {
      productType:
        mapProductType(item?.productType, item?.randomFix, item?.size) ||
        String(item?.productType || "").trim(),
      size: String(item?.size || "").trim(),
    };
    const qty = safeNum(item?.qty);
    const matched = findBestProductMatch(scanBase);
    const confidence = String(item?.confidence || "low").trim().toLowerCase();
    const confidenceWeight =
      confidence === "high" ? 3 : confidence === "medium" ? 2 : 1;
    const specialRate = getSpecialRateForScannedItem(scanBase);
    const specialStock = getSpecialStockForScannedItem(scanBase);
    const resolvedStock = Number(
      matched?.availableStock ?? matched?.stock ?? specialStock ?? 0
    );
    return {
      productType: scanBase.productType,
      size: scanBase.size,
      randomFix: String(item?.randomFix || "").trim(),
      qty,
      remarks: String(item?.remarks || "").trim(),
      paymentTerm: String(item?.paymentTerm || "").trim(),
      vehicleNo: String(item?.vehicleNo || "").trim(),
      confidence,
      confidenceWeight,
      matchedProductName: matched?.name || "",
      matchedType: matched?.type || matched?.TYPE || "",
      matchedStandardName: matched?.standardName || matched?.name || "",
      matchedSize: matched?.size || matched?.name || "",
      rate: Number(matched?.rate || specialRate || 0),
      resolvedStock,
      stock: 0,
      basic: Number(
        matched?.basic ??
          matched?.baseRate ??
          matched?.basicRate ??
          matched?.rate ??
          specialRate ??
          0
      ),
      pattiBasic: Number(matched?.pattiBasic || 0),
    };
  };

  // When opened with preset items (from the Order Request Form's "Add to
  // Cart"), enrich them directly — no image, no Gemini — and show them in
  // the same review list so the dealer can confirm before adding.
  useEffect(() => {
    if (!open || !Array.isArray(presetItems) || presetItems.length === 0)
      return;
    const enriched = presetItems
      .map(enrichScannedItem)
      .filter((x) => x.qty > 0 && (x.productType || x.size || x.remarks))
      .map(({ confidenceWeight, ...rest }) => rest);
    setScanResult(enriched);
    setEditableResults(enriched.map((x) => ({ ...x })));
    setScanError("");
    setScanApplied(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, presetItems]);

  const handleAnalyse = async (imgUrl) => {
    const sourceUrl = typeof imgUrl === "string" ? imgUrl : scanPreviewUrl;
    if (!sourceUrl) return;
    setScanAnalyzing(true);
    setScanError("");
    setScanResult(null);
    setEditableResults([]);

    try {
      const processedDataUrl = await preprocessImage(sourceUrl);
      const productCatalog = (products || [])
        .map((p) => p?.name)
        .filter(Boolean)
        .slice(0, 300)
        .join(", ");

      const prompts = buildPrompts(productCatalog);
      // Fewer angles → fewer heavy vision calls against the tight token/min
      // budget. Most forms read on the first (upright) attempt anyway.
      const attemptAngles = [0, 90, 180];

      let bestEnriched = [];
      let bestScore = -1;
      let attemptNo = 0;

      for (const prompt of prompts) {
        for (const angle of attemptAngles) {
          // Space calls a little so per-minute tokens can refill between them.
          if (attemptNo > 0) await new Promise((r) => setTimeout(r, 1200));
          attemptNo++;
          let attemptDataUrl = processedDataUrl;
          if (angle !== 0) {
            attemptDataUrl = await rotateImageDataUrl(processedDataUrl, angle);
          }
          const base64 = attemptDataUrl.split(",")[1];
          const modelData = await runGemini(base64, "image/jpeg", prompt);
          const rawText = getRawModelText(modelData);
          const parsed = safeParseJSONArray(rawText);

          const enriched = parsed
            .map(enrichScannedItem)
            .filter((x) => x.qty > 0 && (x.productType || x.size || x.remarks));

          const score =
            enriched.reduce((sum, item) => sum + item.confidenceWeight, 0) +
            enriched.length * 5;

          if (score > bestScore) {
            bestScore = score;
            bestEnriched = enriched;
          }
          // Stop as soon as we get a usable read (usually angle 0 + first
          // prompt). Avoids running up to 8 slow Gemini calls per scan.
          if (bestEnriched.length >= 1) break;
        }
        if (bestEnriched.length >= 1) break;
      }

      if (!bestEnriched.length) {
        setScanError("No readable items found in the image.");
      } else {
        const result = bestEnriched.map(
          ({ confidenceWeight, ...rest }) => rest,
        );
        setScanResult(result);
        // ✅ Initialize editable copy so user can adjust qty before adding to cart
        setEditableResults(result.map((item) => ({ ...item })));
      }
    } catch (err) {
      console.error(err);
      setScanError(
        /429/.test(String(err?.message))
          ? "AI abhi busy hai (limit). Thodi der baad dobara try karein."
          : "Could not analyse the image. Please try again.",
      );
    } finally {
      setScanAnalyzing(false);
    }
  };

  const resolveFinalStockForScannedItem = (scanned) => {
    const typeText = String(scanned?.productType || "").toLowerCase();
    const cartName = getScannedCartName(scanned);

    // ✅ Rings: use ring-specific stock lookup
    if (/ring/i.test(typeText)) {
      const ringComputed = getRingFinalRateAndStock(scanned);
      return Number(
        ringComputed?.stock ||
          scanned?.resolvedStock ||
          getSpecialStockForScannedItem(scanned) ||
          0,
      );
    }

    // ✅ TMT: use tmt6StockMap
    if (/tmt|6mm/i.test(typeText)) {
      return Number(getSpecialStockForScannedItem(scanned) || 0);
    }

    // ✅ Binding Wire: use bindingWireStockMap
    if (/binding/i.test(typeText)) {
      return Number(getSpecialStockForScannedItem(scanned) || 0);
    }

    // ✅ Structure: look up by cart name or matched product name in products array
    if (cartName) {
      const matchedByName = (products || []).find(
        (p) =>
          String(p?.name || "")
            .trim()
            .toLowerCase() ===
          String(cartName || "")
            .trim()
            .toLowerCase(),
      );
      if (matchedByName)
        return Number(
          matchedByName?.availableStock ?? matchedByName?.stock ?? 0,
        );
    }

    if (scanned?.matchedProductName) {
      const matchedProduct = (products || []).find(
        (p) =>
          String(p?.name || "")
            .trim()
            .toLowerCase() ===
          String(scanned.matchedProductName || "")
            .trim()
            .toLowerCase(),
      );
      if (matchedProduct)
        return Number(
          matchedProduct?.availableStock ?? matchedProduct?.stock ?? 0,
        );
    }

    return Number(scanned?.resolvedStock || 0);
  };

  const resolveFinalRateForScannedItem = (scanned) => {
    const typeText = String(scanned?.productType || "").toLowerCase();

    if (/ring/i.test(typeText)) {
      const ringComputed = getRingFinalRateAndStock(scanned);
      return Number(
        ringComputed?.rate ||
          scanned?.rate ||
          getSpecialRateForScannedItem(scanned) ||
          0,
      );
    }

    if (/tmt|6mm/i.test(typeText) || /binding/i.test(typeText)) {
      return Number(
        getSpecialRateForScannedItem(scanned) || scanned?.rate || 0,
      );
    }

    if (scanned?.matchedProductName) {
      const matchedProduct = (products || []).find(
        (p) =>
          String(p?.name || "")
            .trim()
            .toLowerCase() ===
          String(scanned.matchedProductName || "")
            .trim()
            .toLowerCase(),
      );
      if (matchedProduct) return Number(matchedProduct?.rate || 0);
    }

    return Number(scanned?.rate || getSpecialRateForScannedItem(scanned) || 0);
  };

  const resolveBaseRateForScannedItem = (scanned) => {
    const typeText = String(scanned?.productType || "").toLowerCase();

    if (/ring/i.test(typeText)) {
      const ringComputed = getRingFinalRateAndStock(scanned);
      return Number(
        ringComputed?.meta?.baseRateKg || scanned?.basic || scanned?.rate || 0,
      );
    }

    if (scanned?.matchedProductName) {
      const matchedProduct = (products || []).find(
        (p) =>
          String(p?.name || "")
            .trim()
            .toLowerCase() ===
          String(scanned.matchedProductName || "")
            .trim()
            .toLowerCase(),
      );
      if (matchedProduct) {
        return Number(
          matchedProduct?.basic ??
            matchedProduct?.baseRate ??
            matchedProduct?.basicRate ??
            matchedProduct?.rate ??
            0,
        );
      }
    }

    return Number(scanned?.basic || scanned?.rate || 0);
  };

  // ✅ UPDATED handleApply: no stock error – add to cart unconditionally; CartPopup validates on submit
  const handleApply = () => {
    if (!editableResults?.length) return;

    const preparedUpdates = [];

    for (const scanned of editableResults) {
      const qty = Number(scanned?.qty || 0);
      if (!qty || qty <= 0) continue;

      const cartName = getScannedCartName(scanned);
      if (!cartName) continue;

      const isRing = /ring/i.test(scanned?.productType || "");
      const ringComputed = isRing ? getRingFinalRateAndStock(scanned) : null;

      const finalRate = resolveFinalRateForScannedItem(scanned);
      const finalStock = resolveFinalStockForScannedItem(scanned);
      const baseRate = resolveBaseRateForScannedItem(scanned);

      preparedUpdates.push({
        name: cartName,
        standardName:
          scanned?.matchedStandardName ||
          scanned?.matchedProductName ||
          cartName,
        size:
          scanned?.matchedSize ||
          scanned?.size ||
          scanned?.matchedStandardName ||
          cartName,
        type: scanned?.matchedType || "",

        qty,
        rate: Number(finalRate || 0),
        basicRate: Number(baseRate || 0),
        baseRate: Number(baseRate || 0),
        basic: Number(baseRate || 0),
        pattiBasic: Number(scanned?.pattiBasic || 0),

        // ✅ Always save resolved stock – CartPopup will show error if insufficient
        stock: Number(finalStock || 0),
        availableStock: Number(finalStock || 0),
        subtotal: Number((qty * finalRate).toFixed(2)),

        perPieceWt: Number(ringComputed?.meta?.perPieceWt || 0),
        qtyUnit: isRing ? "PCS" : "KG",

        meta: /binding/i.test(scanned?.productType || "")
          ? { section: "Binding Wire" }
          : /ring/i.test(scanned?.productType || "")
            ? { section: "Rings", ...(ringComputed?.meta || {}) }
            : /tmt|6mm/i.test(scanned?.productType || "")
              ? { section: "TMT" }
              : { section: "Structure" },

        scannedMeta: {
          productType: scanned?.productType || "",
          size: scanned?.size || "",
          randomFix: scanned?.randomFix || "",
          remarks: scanned?.remarks || "",
          confidence: scanned?.confidence || "low",
        },
      });
    }

    // ✅ Merge into cart – no stock blocking here, CartPopup handles validation
    setCart((prev) => {
      const updated = [...prev];

      preparedUpdates.forEach((payload) => {
        const idx = updated.findIndex(
          (item) =>
            String(item?.name || "")
              .trim()
              .toLowerCase() ===
            String(payload?.name || "")
              .trim()
              .toLowerCase(),
        );

        if (idx !== -1) {
          const mergedQty =
            Number(updated[idx]?.qty || 0) + Number(payload.qty || 0);
          updated[idx] = {
            ...updated[idx],
            ...payload,
            qty: mergedQty,
            subtotal: Number(
              (mergedQty * Number(payload.rate || 0)).toFixed(2),
            ),
          };
        } else {
          updated.push(payload);
        }
      });

      return updated;
    });

    // Carry the scanned Payment Term + Vehicle No to the cart so they
    // auto-fill there (CartPopup reads this on open, then clears it).
    try {
      const scanPaymentTerm =
        editableResults.find((r) => String(r?.paymentTerm || "").trim())
          ?.paymentTerm || "";
      const scanVehicleNo =
        editableResults.find((r) => String(r?.vehicleNo || "").trim())
          ?.vehicleNo || "";
      if (scanPaymentTerm || scanVehicleNo) {
        localStorage.setItem(
          "scanCartMeta:v1",
          JSON.stringify({
            paymentTerm: scanPaymentTerm,
            vehicleNo: scanVehicleNo,
            ts: Date.now(),
          })
        );
      }
    } catch {
      /* non-fatal */
    }

    setScanApplied(true);
    setTimeout(() => onClose?.(), 1000);
  };

  if (!open) return null;

  const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div
          className="w-full max-w-3xl bg-white rounded-3xl shadow-2xl overflow-hidden"
          initial={{ opacity: 0, y: 18, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 12, scale: 0.98 }}
        >
          <div className="flex items-center justify-between px-5 py-4 border-b bg-gray-50">
            <div>
              <h3 className="text-xl font-black text-gray-900">Scan Order</h3>
              <p className="text-sm text-gray-500">
                Upload image, analyse items, edit quantities, then add to cart
              </p>
            </div>
            <button
              onClick={() => {
                resetScan();
                onClose?.();
              }}
              className="w-10 h-10 rounded-full bg-white border shadow-sm hover:bg-gray-50 text-xl"
            >
              ×
            </button>
          </div>

          <div className="p-5 space-y-5 max-h-[85vh] overflow-y-auto">
            {/* Upload / Camera */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="rounded-2xl border border-dashed border-gray-300 p-5 cursor-pointer hover:border-gray-400 transition bg-gray-50">
                <input
                  ref={driveRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleFile(e.target.files?.[0])}
                />
                <div className="flex flex-col items-center justify-center text-center gap-2">
                  <CloudUploadIcon style={{ fontSize: 34 }} />
                  <div className="font-bold text-gray-800">
                    Upload from device
                  </div>
                  <div className="text-sm text-gray-500">
                    JPG, PNG, or camera image
                  </div>
                </div>
              </label>

              <>
                {/* Hidden camera input — NOT inside a label */}
                <input
                  ref={cameraRef}
                  type="file"
                  accept="image/*"
                  {...(isMobile ? { capture: "environment" } : {})}
                  className="hidden"
                  onChange={(e) => handleFile(e.target.files?.[0])}
                />
                <button
                  type="button"
                  onClick={() => cameraRef.current?.click()}
                  className="rounded-2xl border border-dashed border-gray-300 p-5 cursor-pointer hover:border-gray-400 transition bg-gray-50 w-full"
                >
                  <div className="flex flex-col items-center justify-center text-center gap-2">
                    <CameraAltIcon style={{ fontSize: 34 }} />
                    <div className="font-bold text-gray-800">Use camera</div>
                    <div className="text-sm text-gray-500">
                      {isMobile
                        ? "Capture and analyse instantly"
                        : "Upload image from device"}
                    </div>
                  </div>
                </button>
              </>
            </div>

            {/* Preview */}
            {scanPreviewUrl ? (
              <div className="rounded-2xl border p-4 bg-white">
                <div className="flex items-center justify-between mb-3">
                  <div className="font-bold text-gray-800">Preview</div>
                  <button
                    onClick={resetScan}
                    className="text-sm font-semibold text-red-500 hover:underline"
                  >
                    Remove
                  </button>
                </div>
                <img
                  src={scanPreviewUrl}
                  alt="scan preview"
                  className="w-full max-h-[340px] object-contain rounded-2xl bg-gray-50"
                />
              </div>
            ) : null}

            {/* Action buttons */}
            <div className="flex flex-wrap gap-3">
              {scanAnalyzing && (
                <div className="inline-flex items-center gap-2 px-5 py-3 rounded-2xl font-bold bg-gray-100 text-gray-700">
                  <AutoFixHighIcon className="animate-pulse" />
                  Analysing image…
                </div>
              )}

              {editableResults?.length ? (
                <button
                  onClick={handleApply}
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-2xl font-bold bg-green-600 text-white hover:bg-green-700"
                >
                  Add to Cart
                </button>
              ) : null}
            </div>

            {/* Error (analysis errors only, NOT stock errors) */}
            {scanError ? (
              <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
                {scanError}
              </div>
            ) : null}

            {/* Success */}
            {scanApplied ? (
              <div className="rounded-2xl border border-green-200 bg-green-50 px-4 py-3 text-sm font-semibold text-green-700">
                Items added to cart. Stock will be validated on order
                submission.
              </div>
            ) : null}

            {/* ✅ Analysis Result with editable qty */}
            {editableResults?.length ? (
              <div className="rounded-2xl border p-4 bg-white">
                <div className="font-black text-gray-900 mb-1">
                  Analysis Result
                </div>
                <p className="text-xs text-gray-500 mb-4">
                  Review and edit quantities (Kg) before adding to cart.
                </p>

                <div className="space-y-3">
                  {editableResults.map((item, idx) => (
                    <div
                      key={`${item?.matchedProductName || item?.productType || "item"}-${idx}`}
                      className="rounded-xl border border-gray-200 bg-gray-50 p-3"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <div className="font-bold text-gray-800">
                            {item.matchedProductName ||
                              item.productType ||
                              "Unknown Item"}
                          </div>

                          {!!item.size && (
                            <div className="text-sm text-gray-600 mt-1">
                              Size:{" "}
                              <span className="font-semibold">{item.size}</span>
                            </div>
                          )}

                          {!!item.remarks && (
                            <div className="text-sm text-gray-500 mt-1">
                              Remarks: {item.remarks}
                            </div>
                          )}

                          {/* ✅ Rate info (read-only) */}
                          {(() => {
                            const rate = resolveFinalRateForScannedItem(item);
                            return rate > 0 ? (
                              <div className="text-xs text-indigo-600 font-semibold mt-1">
                                Rate: ₹{Number(rate).toFixed(2)}/kg
                              </div>
                            ) : null;
                          })()}
                        </div>

                        <div className="text-xs font-bold uppercase text-indigo-600 shrink-0">
                          {item.confidence || "low"}
                        </div>
                      </div>

                      {/* ✅ Editable Qty field */}
                      <div className="mt-3 flex items-center gap-3">
                        <label className="text-xs font-bold text-gray-700 whitespace-nowrap">
                          Qty (Kg):
                        </label>
                        <input
                          type="number"
                          min="1"
                          value={item.qty || ""}
                          onChange={(e) => {
                            const val = e.target.value;
                            const num =
                              val === "" ? 0 : Math.max(0, Number(val));
                            setEditableResults((prev) =>
                              prev.map((r, i) =>
                                i === idx ? { ...r, qty: num } : r,
                              ),
                            );
                          }}
                          className="w-32 px-3 py-2 text-sm border-2 border-gray-300 rounded-xl outline-none focus:border-yellow-500 focus:ring-4 focus:ring-yellow-100 font-semibold"
                          placeholder="Enter Qty"
                        />
                        <span className="text-xs text-gray-500 font-medium">
                          {item.qty > 0
                            ? `≈ ₹${(Number(resolveFinalRateForScannedItem(item) || 0) * item.qty).toLocaleString("en-IN")}`
                            : ""}
                        </span>

                        {/* ✅ Remove item button */}
                        <button
                          type="button"
                          onClick={() =>
                            setEditableResults((prev) =>
                              prev.filter((_, i) => i !== idx),
                            )
                          }
                          className="ml-auto text-xs font-semibold text-red-500 hover:underline"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* ✅ Total summary */}
                {editableResults.length > 0 && (
                  <div className="mt-4 pt-3 border-t flex justify-between items-center text-sm font-semibold text-gray-700">
                    <span>
                      {editableResults.filter((x) => x.qty > 0).length} item(s)
                    </span>
                    <span>
                      Total Qty:{" "}
                      <strong>
                        {editableResults
                          .reduce((s, r) => s + Number(r.qty || 0), 0)
                          .toLocaleString("en-IN")}{" "}
                        Kg
                      </strong>
                    </span>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
/* =========================
   STRUCTURE SECTION
========================= */
function StructureSection({
  currentProducts,
  startIndex,
  qtyInputs,
  setQtyInputs,
  errors,
  setErrors,
  onAdd,
  setPreviewImage,
  rateOverrideByName,
}) {
  return (
    <>
      {/* Desktop Grid — only items with valid rate (filtered upstream) */}
      <div className="hidden md:grid md:grid-cols-4 grid-cols-1 xl:grid-cols-6 gap-6 justify-items-center mb-20">
        {currentProducts.map((product, i) => {
          const inputKey = getProductInputKey(product);
          const outOfStock = Number(product.availableStock || 0) <= 0;
          const disabled = outOfStock;

          const effectiveRate =
            Number(rateOverrideByName?.[product.name]) > 0
              ? Number(rateOverrideByName[product.name])
              : Number(product.rate || 0);

          const stockStatus = getStockStatus(product.availableStock);

          return (
            <motion.div
              key={`${product.name}-${inputKey}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              whileHover={{ y: disabled ? 0 : -6 }}
              className={`relative w-full max-w-[14rem] bg-white rounded-3xl shadow-lg overflow-hidden transition-all ${
                disabled ? "opacity-80" : "hover:shadow-2xl"
              }`}
            >
              <div
                className="relative flex items-center justify_center cursor-zoom-in overflow-hidden"
                onClick={() => setPreviewImage(product.img)}
              >
                <img
                  src={product.img}
                  alt={product.name}
                  className={`lg:h-32 max-md:h-32 w-full object-contain transition-transform duration-300 ${
                    outOfStock
                      ? "grayscale opacity-50"
                      : "group-hover:scale-110"
                  }`}
                />
                {outOfStock && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <span className="text-red-600 font-bold text-lg px-6 py-3 transform -rotate-12">
                      OUT OF STOCK
                    </span>
                  </div>
                )}
              </div>

              <div className="p-4 space-y-3">
                <div className="flex justify-between items-start gap-2">
                  <h3 className="font-bold text-sm text-gray-800 line-clamp-2 flex-1">
                    {product.name}
                  </h3>
                  <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 text-[10px] font-bold rounded-full">
                    {typeFullName[product.type] || product.type}
                  </span>
                </div>

                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-gray-600 font-semibold">Stock</p>
                    <span
                      className={`text-[11px] font-bold uppercase ${stockStatus.textColor}`}
                    >
                      {stockStatus.label}
                    </span>
                  </div>

                  <div className="w-full h-1.5 rounded-full bg-gray-200 overflow-hidden">
                    <div
                      className={`h-full ${stockStatus.barColor} transition-all duration-300`}
                      style={{ width: `${stockStatus.pct}%` }}
                    />
                  </div>

                  <p className="text-xl font-bold text-gray-800">
                    ₹{effectiveRate}/kg
                  </p>
                </div>

                <div className="relative">
                  <input
                    type="number"
                    min="1"
                    placeholder="Qty (Kg)"
                    value={qtyInputs[inputKey] || ""}
                    onChange={(e) => {
                      const val = e.target.value;
                      const num = Number(val);

                      setQtyInputs((prev) => ({ ...prev, [inputKey]: val }));

                      if (!val || num <= 0) {
                        setErrors((prev) => ({
                          ...prev,
                          [inputKey]: "Please enter a valid quantity",
                        }));
                      } else if (num > Number(product.availableStock || 0)) {
                        setErrors((prev) => ({
                          ...prev,
                          [inputKey]: "Please add a lower quantity",
                        }));
                      } else {
                        setErrors((prev) => ({ ...prev, [inputKey]: "" }));
                      }
                    }}
                    disabled={disabled}
                    className={`w-full px-2 py-3 pr-24 text-sm border-2 rounded-xl outline-none ${
                      disabled
                        ? "bg-gray-100 border-gray-300 cursor-not-allowed"
                        : errors[inputKey]
                          ? "border-red-500 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                          : "border-gray-300 focus:border-yellow-500 focus:ring-4 focus:ring-yellow-100"
                    }`}
                  />

                  <motion.button
                    onClick={() =>
                      onAdd({ ...product, rate: effectiveRate }, inputKey)
                    }
                    disabled={
                      disabled ||
                      !qtyInputs[inputKey] ||
                      Number(qtyInputs[inputKey]) <= 0 ||
                      Number(qtyInputs[inputKey]) >
                        Number(product.availableStock || 0) ||
                      !!errors[inputKey]
                    }
                    className={`absolute right-2 top-1/2 -translate-y-1/2 px-4 py-2 text-sm font-bold rounded-lg ${
                      disabled
                        ? "bg-gray-400 text-gray-700"
                        : "bg-yellow-500 hover:bg-yellow-600 text-black shadow-md"
                    }`}
                  >
                    Add
                  </motion.button>
                </div>

                {errors[inputKey] && (
                  <motion.p
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-red-500 text-xs font-medium mt-1"
                  >
                    {errors[inputKey]}
                  </motion.p>
                )}
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Mobile List */}
      <div className="md:hidden flex flex-col gap-3 mb-20">
        {currentProducts.map((product, i) => {
          const inputKey = getProductInputKey(product);
          const outOfStock = Number(product.availableStock || 0) <= 0;
          const disabled = outOfStock;

          const effectiveRate =
            Number(rateOverrideByName?.[product.name]) > 0
              ? Number(rateOverrideByName[product.name])
              : Number(product.rate || 0);

          const stockStatus = getStockStatus(product.availableStock);

          return (
            <div
              key={`${product.name}-mobile-${inputKey}`}
              className="w-full bg-white rounded-2xl shadow-md px-4 py-3"
            >
              <div className="flex items-center gap-4">
                <div
                  onClick={() => setPreviewImage(product.img)}
                  className="w-16 h-16 rounded-xl bg-gray-50 flex items-center justify-center overflow-hidden border"
                >
                  <img
                    src={product.img}
                    alt={product.name}
                    className={`w-full h-full object-contain ${outOfStock ? "grayscale opacity-60" : ""}`}
                  />
                </div>

                <div className="flex-1 text-left">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-gray-800 line-clamp-1">
                      {product.name}
                    </p>
                    <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 text-[11px] font-bold rounded-full">
                      {typeFullName[product.type] || product.type}
                    </span>
                  </div>

                  <p className="text-sm text-gray-700 mt-1 font-semibold">
                    ₹{effectiveRate}/kg
                  </p>

                  <div className="mt-2 space-y-1">
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-gray-600 font-semibold">
                        Stock
                      </p>
                      <span
                        className={`text-[11px] font-bold uppercase ${stockStatus.textColor}`}
                      >
                        {stockStatus.label}
                      </span>
                    </div>

                    <div className="w-full h-1.5 rounded-full bg-gray-200 overflow-hidden">
                      <div
                        className={`h-full ${stockStatus.barColor} transition-all duration-300`}
                        style={{ width: `${stockStatus.pct}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="relative mt-[10px]">
                <input
                  type="number"
                  min="1"
                  placeholder="Qty (Kg)"
                  value={qtyInputs[inputKey] || ""}
                  onChange={(e) => {
                    const val = e.target.value;
                    const num = Number(val);

                    setQtyInputs((prev) => ({ ...prev, [inputKey]: val }));

                    if (!val || num <= 0) {
                      setErrors((prev) => ({
                        ...prev,
                        [inputKey]: "Please enter a valid quantity",
                      }));
                    } else if (num > Number(product.availableStock || 0)) {
                      setErrors((prev) => ({
                        ...prev,
                        [inputKey]: "Please add a lower quantity",
                      }));
                    } else {
                      setErrors((prev) => ({ ...prev, [inputKey]: "" }));
                    }
                  }}
                  disabled={disabled}
                  className={`w-full px-2 py-3 pr-24 text-sm border-2 rounded-xl outline-none ${
                    disabled
                      ? "bg-gray-100 border-gray-300 cursor-not-allowed"
                      : errors[inputKey]
                        ? "border-red-500 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                        : "border-gray-300 focus:border-yellow-500 focus:ring-4 focus:ring-yellow-100"
                  }`}
                />

                <motion.button
                  onClick={() =>
                    onAdd({ ...product, rate: effectiveRate }, inputKey)
                  }
                  disabled={
                    disabled ||
                    !qtyInputs[inputKey] ||
                    Number(qtyInputs[inputKey]) <= 0 ||
                    Number(qtyInputs[inputKey]) >
                      Number(product.availableStock || 0) ||
                    !!errors[inputKey]
                  }
                  className={`absolute right-2 top-1/2 -translate-y-1/2 px-4 py-2 text-sm font-bold rounded-lg ${
                    disabled
                      ? "bg-gray-400 text-gray-700"
                      : "bg-yellow-500 hover:bg-yellow-600 text-black shadow-md"
                  }`}
                >
                  Add
                </motion.button>
              </div>

              {errors[inputKey] && (
                <p className="text-red-500 text-xs font-medium mt-2">
                  {errors[inputKey]}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}

function StructurePagination({ totalPages, currentPage, onChange }) {
  if (totalPages <= 1) return null;

  return (
    <>
      {/* Desktop */}
      <div className="fixed hidden md:block bottom-0 left-0 right-0 pointer-events-none">
        <div className="flex justify-center py-6 pointer-events-auto">
          <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 px-6 py-4">
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={onChange}
              size="large"
              showFirstButton
              showLastButton
              sx={{
                "& .Mui-selected": {
                  bgcolor: "#facc15 !important",
                  color: "black",
                  fontWeight: "bold",
                },
                "& .MuiPaginationItem-root": {
                  borderRadius: "12px",
                  border: "1px solid #e5e7eb",
                  margin: "0 4px",
                },
              }}
            />
          </div>
        </div>
      </div>

      {/* Mobile */}
      <div className="fixed inset-x-0 bottom-[57px] z-[40] pointer-events-none md:hidden">
        <div className="flex justify-center py-3 px-4 pointer-events-auto">
          <div className="bg-white rounded-full shadow-2xl border border-gray-200 px-5 md:py-3 py-1 backdrop-blur-sm">
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={onChange}
              size="medium"
              siblingCount={0}
              boundaryCount={1}
              sx={{
                "& .MuiPagination-ul": { flexWrap: "nowrap" },
                "& .MuiPaginationItem-root": {
                  minWidth: "38px",
                  height: "38px",
                  margin: "0 3px",
                  fontSize: "0.95rem",
                  fontWeight: "bold",
                },
                "& .Mui-selected": {
                  backgroundColor: "#facc15 !important",
                  color: "black",
                },
              }}
            />
          </div>
        </div>
      </div>
    </>
  );
}

/* =========================
   ✅ TMT SECTION (previous 6MM)
========================= */
function TmtSection({
  options,
  tmt6Base,
  tmt6Qty,
  setTmt6Qty,
  tmt6Err,
  setTmt6Err,
  onAdd,
  dealer6mmPerMT,
  setPreviewImage,
  tmt6StockMap,
}) {
  const baseKg = (Number(dealer6mmPerMT) || 0) / 1000;

  return (
    <div className="mt-2 mb-10">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {options.map((opt) => {
          const parityKg = parityMTtoKg(opt.parityPerMT);
          const base = baseKg > 0 ? baseKg : Number(tmt6Base.rate) || 0;
          const finalRate = base + parityKg;

          const availableStock = Number(tmt6StockMap?.[opt.key] || 0);
          const disabledByStock = availableStock <= 0;
          const disabled = disabledByStock;
          const stockStatus = getStockStatus(availableStock);

          return (
            <div
              key={opt.key}
              className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm hover:shadow-md transition"
            >
              {opt?.raw?.Image ||
              opt?.raw?.image ||
              opt?.raw?.IMG ||
              opt?.raw?.img ? (
                <div
                  className="mb-3 rounded-xl overflow-hidden bg-gray-50 cursor-zoom-in"
                  onClick={() =>
                    setPreviewImage(
                      opt?.raw?.Image ||
                        opt?.raw?.image ||
                        opt?.raw?.IMG ||
                        opt?.raw?.img,
                    )
                  }
                >
                  <img
                    src={
                      opt?.raw?.Image ||
                      opt?.raw?.image ||
                      opt?.raw?.IMG ||
                      opt?.raw?.img
                    }
                    alt={opt.label}
                    className={`w-full h-32 object-contain ${availableStock <= 0 ? "grayscale opacity-60" : ""}`}
                  />
                </div>
              ) : null}

              <div className="flex items-start justify-between gap-3">
                <p className="font-bold text-gray-900">{opt.label}</p>
                <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-yellow-100 text-yellow-800 whitespace-nowrap">
                  6mm TMT
                </span>
              </div>

              <div className="mt-3 space-y-1">
                <p className="text-lg font-extrabold text-gray-900">
                  ₹{finalRate.toFixed(2)}/kg
                </p>
              </div>

              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <p className="text-xs text-gray-600 font-semibold">Stock</p>
                  <span
                    className={`text-[11px] font-bold uppercase ${stockStatus.textColor}`}
                  >
                    {stockStatus.label}
                  </span>
                </div>

                <div className="w-full h-1.5 rounded-full bg-gray-200 overflow-hidden">
                  <div
                    className={`h-full ${stockStatus.barColor} transition-all duration-300`}
                    style={{ width: `${stockStatus.pct}%` }}
                  />
                </div>
              </div>

              <div className="relative mt-3">
                <input
                  type="number"
                  min="1"
                  placeholder="Qty (Kg)"
                  value={tmt6Qty[opt.key] || ""}
                  onChange={(e) => {
                    const val = e.target.value;
                    const num = Number(val);
                    setTmt6Qty((p) => ({ ...p, [opt.key]: val }));

                    if (!val || num <= 0) {
                      setTmt6Err((p) => ({
                        ...p,
                        [opt.key]: "Please enter a valid quantity",
                      }));
                    } else if (num > availableStock) {
                      setTmt6Err((p) => ({
                        ...p,
                        [opt.key]: "Please add a lower quantity",
                      }));
                    } else {
                      setTmt6Err((p) => ({ ...p, [opt.key]: "" }));
                    }
                  }}
                  disabled={disabled}
                  className={`w-full px-3 py-3 pr-24 text-sm border-2 rounded-xl outline-none ${
                    disabled
                      ? "bg-gray-100 border-gray-300 cursor-not-allowed"
                      : tmt6Err[opt.key]
                        ? "border-red-500 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                        : "border-gray-300 focus:border-yellow-500 focus:ring-4 focus:ring-yellow-100"
                  }`}
                />

                <button
                  onClick={() =>
                    onAdd(opt, finalRate, base, parityKg, availableStock)
                  }
                  disabled={
                    disabled ||
                    !tmt6Qty[opt.key] ||
                    Number(tmt6Qty[opt.key]) <= 0 ||
                    Number(tmt6Qty[opt.key]) > availableStock ||
                    !!tmt6Err[opt.key]
                  }
                  className={`absolute right-2 top-1/2 -translate-y-1/2 px-4 py-2 text-sm font-bold rounded-lg ${
                    disabled
                      ? "bg-gray-400 text-gray-700"
                      : "bg-yellow-500 hover:bg-yellow-600 text-black shadow-md"
                  }`}
                >
                  Add
                </button>
              </div>

              {tmt6Err[opt.key] && (
                <p className="text-red-600 text-xs font-semibold mt-2">
                  {tmt6Err[opt.key]}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function BindingWireSection({
  options,
  rateMap,
  qtyMap,
  setQtyMap,
  errMap,
  setErrMap,
  onAdd,
  stockMap,
  setPreviewImage,
}) {
  const BW_IMG = "/products/binding-wire.jpeg";
  return (
    <div className="mt-2 mb-10">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {options.map((opt) => {
          // Per-variant rate (MS / GI) from the dealer sheet.
          const rate = Number(rateMap?.[opt.key] || 0);
          const availableStock = Number(stockMap?.[opt.key] || 0);
          const disabled = availableStock <= 0;
          const stockStatus = getStockStatus(availableStock);

          return (
            <div
              key={opt.key}
              className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm hover:shadow-md transition"
            >
              {/* Binding wire image (shared for MS & GI) */}
              <RingImage
                src={BW_IMG}
                label={opt.label}
                dimmed={availableStock <= 0}
                onZoom={() => setPreviewImage?.(BW_IMG)}
              />
              <div className="flex items-start justify-between gap-3">
                <p className="font-bold text-gray-900">{opt.label}</p>
                <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-yellow-100 text-yellow-800">
                  BINDING WIRE
                </span>
              </div>

              <div className="mt-3 space-y-1">
                <p className="text-lg font-extrabold text-gray-900">
                  ₹{rate.toFixed(2)}/kg
                </p>
              </div>

              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <p className="text-xs text-gray-600 font-semibold">Stock</p>
                  <span
                    className={`text-[11px] font-bold uppercase ${stockStatus.textColor}`}
                  >
                    {stockStatus.label}
                  </span>
                </div>

                <div className="w-full h-1.5 rounded-full bg-gray-200 overflow-hidden">
                  <div
                    className={`h-full ${stockStatus.barColor} transition-all duration-300`}
                    style={{ width: `${stockStatus.pct}%` }}
                  />
                </div>
              </div>

              <div className="relative mt-3">
                <input
                  type="number"
                  min="1"
                  placeholder="Qty (Kg)"
                  value={qtyMap?.[opt.key] || ""}
                  onChange={(e) => {
                    const v = e.target.value;
                    const n = Number(v);

                    setQtyMap((p) => ({ ...p, [opt.key]: v }));

                    if (!v || n <= 0) {
                      setErrMap((p) => ({
                        ...p,
                        [opt.key]: "Please enter a valid quantity",
                      }));
                    } else if (n > availableStock) {
                      setErrMap((p) => ({
                        ...p,
                        [opt.key]: "Please add a lower quantity",
                      }));
                    } else {
                      setErrMap((p) => ({ ...p, [opt.key]: "" }));
                    }
                  }}
                  disabled={disabled}
                  className={`w-full px-3 py-3 pr-24 text-sm border-2 rounded-xl outline-none ${
                    disabled
                      ? "bg-gray-100 border-gray-300 cursor-not-allowed"
                      : errMap?.[opt.key]
                        ? "border-red-500 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                        : "border-gray-300 focus:border-yellow-500 focus:ring-4 focus:ring-yellow-100"
                  }`}
                />

                <button
                  onClick={() => onAdd(opt, rate, availableStock)}
                  disabled={
                    disabled ||
                    !qtyMap?.[opt.key] ||
                    Number(qtyMap?.[opt.key]) <= 0 ||
                    Number(qtyMap?.[opt.key]) > availableStock ||
                    !!errMap?.[opt.key]
                  }
                  className={`absolute right-2 top-1/2 -translate-y-1/2 px-4 py-2 text-sm font-bold rounded-lg ${
                    disabled
                      ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                      : "bg-yellow-500 hover:bg-yellow-600 text-black shadow-md"
                  }`}
                >
                  Add
                </button>
              </div>

              {errMap?.[opt.key] && (
                <p className="text-red-600 text-xs font-semibold mt-2">
                  {errMap[opt.key]}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* =========================
   ✅ RINGS SECTION (Rank-wise + Pagination like Structure)
   stirrup = size name
   Rank = sorting (1,2,3...)
   Rate = parity
   finalRateKg = baseRateKg + parityKg
========================= */
// Ring image with a fixed-size box. If no matching image exists in
// public/rings, it renders a same-size placeholder (never collapses) so
// every card in the grid keeps a uniform height — no empty stretched gaps.
function RingImage({ label, src: srcOverride, dimmed, onZoom }) {
  const [error, setError] = React.useState(false);
  const src = srcOverride || `/rings/${encodeURIComponent(label)}.jpeg`;

  if (error) {
    return (
      <div className="w-full h-32 mb-3 rounded-xl bg-gray-50 border border-dashed border-gray-200 flex flex-col items-center justify-center text-gray-300 select-none">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="w-8 h-8"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.5}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z"
          />
        </svg>
        <span className="text-[10px] font-semibold mt-1 text-gray-400">
          No image
        </span>
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={label}
      loading="lazy"
      onClick={onZoom}
      onError={() => setError(true)}
      className={`w-full h-32 object-contain mb-3 rounded-xl bg-gray-50 cursor-zoom-in ${
        dimmed ? "grayscale opacity-60" : ""
      }`}
    />
  );
}

function RingsSection({
  ringsOptions,
  ringsBaseRateKg,
  ringsQty,
  setRingsQty,
  ringsErr,
  setRingsErr,
  onAdd,
  ringsStockMap,
  setPreviewImage,
}) {
  return (
    <div className="mt-2 mb-20">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {ringsOptions.map((opt) => {
          const parityKg = toKgRateFromMaybeMT(opt?.parity || 0);
          const baseKg = Number(ringsBaseRateKg || 0);
          const finalRateKg = baseKg + parityKg;
          const availableStock = Number(ringsStockMap?.[opt.key] || 0);
          const disabled = availableStock <= 0;
          const stockStatus = getStockStatus(availableStock);
          return (
            <div
              key={opt.key}
              className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm hover:shadow-md transition"
            >
              {/* Ring image — from public/rings, keyed by normalized size.
                  Falls back to a same-size placeholder box (no collapse). */}
              <RingImage
                label={opt.label}
                dimmed={availableStock <= 0}
                onZoom={() =>
                  setPreviewImage(`/rings/${encodeURIComponent(opt.label)}.jpeg`)
                }
              />
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <p className="font-bold text-gray-900">{opt.label}</p>
                  <div className="mt-1 flex items-center gap-2"></div>
                </div>
                <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-yellow-100 text-yellow-800">
                  RINGS
                </span>
              </div>

              <div className="mt-3 space-y-1">
                <p className="text-lg font-extrabold text-gray-900">
                  ₹{finalRateKg.toFixed(2)}/kg
                </p>
              </div>

              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <p className="text-xs text-gray-600 font-semibold">Stock</p>
                  <span
                    className={`text-[11px] font-bold uppercase ${stockStatus.textColor}`}
                  >
                    {stockStatus.label}
                  </span>
                </div>

                <div className="w-full h-1.5 rounded-full bg-gray-200 overflow-hidden">
                  <div
                    className={`h-full ${stockStatus.barColor} transition-all duration-300`}
                    style={{ width: `${stockStatus.pct}%` }}
                  />
                </div>
              </div>

              <div className="relative mt-3">
                <input
                  type="number"
                  min="1"
                  placeholder="Qty (Kg)"
                  value={ringsQty?.[opt.key] || ""}
                  onChange={(e) => {
                    const v = e.target.value;
                    const n = Number(v);

                    setRingsQty((p) => ({ ...p, [opt.key]: v }));

                    if (!v || n <= 0) {
                      setRingsErr((p) => ({
                        ...p,
                        [opt.key]: "Please enter a valid quantity",
                      }));
                    } else if (n > availableStock) {
                      setRingsErr((p) => ({
                        ...p,
                        [opt.key]: "Please add a lower quantity",
                      }));
                    } else {
                      setRingsErr((p) => ({ ...p, [opt.key]: "" }));
                    }
                  }}
                  disabled={disabled}
                  className={`w-full px-3 py-3 pr-24 text-sm border-2 rounded-xl outline-none ${
                    disabled
                      ? "bg-gray-100 border-gray-300 cursor-not-allowed"
                      : ringsErr?.[opt.key]
                        ? "border-red-500 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                        : "border-gray-300 focus:border-yellow-500 focus:ring-4 focus:ring-yellow-100"
                  }`}
                />

                <button
                  onClick={() =>
                    onAdd(opt, finalRateKg, baseKg, parityKg, availableStock)
                  }
                  disabled={
                    disabled ||
                    !ringsQty?.[opt.key] ||
                    Number(ringsQty?.[opt.key]) <= 0 ||
                    Number(ringsQty?.[opt.key]) > availableStock ||
                    !!ringsErr?.[opt.key]
                  }
                  className={`absolute right-2 top-1/2 -translate-y-1/2 px-4 py-2 text-sm font-bold rounded-lg ${
                    disabled
                      ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                      : "bg-yellow-500 hover:bg-yellow-600 text-black shadow-md"
                  }`}
                >
                  Add
                </button>
              </div>

              {ringsErr?.[opt.key] && (
                <p className="text-red-600 text-xs font-semibold mt-2">
                  {ringsErr[opt.key]}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RingsPaginationFixed({ totalPages, currentPage, onChange }) {
  if (totalPages <= 1) return null;

  return (
    <>
      {/* Desktop */}
      <div className="fixed hidden md:block bottom-0 left-0 right-0 pointer-events-none z-[50]">
        <div className="flex justify-center py-6 pointer-events-auto">
          <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 px-6 py-4">
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={onChange}
              size="large"
              showFirstButton
              showLastButton
              sx={{
                "& .Mui-selected": {
                  bgcolor: "#facc15 !important",
                  color: "black",
                  fontWeight: "bold",
                },
                "& .MuiPaginationItem-root": {
                  borderRadius: "12px",
                  border: "1px solid #e5e7eb",
                  margin: "0 4px",
                },
              }}
            />
          </div>
        </div>
      </div>

      {/* Mobile */}
      <div className="fixed inset-x-0 bottom-[84px] z-[50] pointer-events-none md:hidden">
        <div className="flex justify-center py-3 px-4 pointer-events-auto">
          <div className="bg-white rounded-full shadow-2xl border border-gray-200 px-5 py-3 backdrop-blur-sm">
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={onChange}
              size="medium"
              siblingCount={0}
              boundaryCount={1}
              sx={{
                "& .MuiPagination-ul": { flexWrap: "nowrap" },
                "& .MuiPaginationItem-root": {
                  minWidth: "38px",
                  height: "38px",
                  margin: "0 3px",
                  fontSize: "0.95rem",
                  fontWeight: "bold",
                },
                "& .Mui-selected": {
                  backgroundColor: "#facc15 !important",
                  color: "black",
                },
              }}
            />
          </div>
        </div>
      </div>
    </>
  );
}

/* =========================
   MAIN PAGE
========================= */
export default function ProductsPage() {
  const outlet = useOutletContext() || {};
  const {
    searchQuery = "",
    cart = [],
    setCart = () => {},
    selectedCategory = "All",
    selectedSize = "All",
    selectedRingSize: selectedRingSizeFromOutlet,
    setSelectedRingSize: setSelectedRingSizeFromOutlet,
    ringSizeOptions: ringSizeOptionsFromOutlet,
    setRingSizeOptions: setRingSizeOptionsFromOutlet,
    activeSection = "Structure",
    setActiveSection = () => {},
  } = outlet;

  const [selectedRingSizeLocal, setSelectedRingSizeLocal] = useState("All");
  const [ringSizeOptionsLocal, setRingSizeOptionsLocal] = useState([]);
  const selectedRingSize =
    selectedRingSizeFromOutlet !== undefined
      ? selectedRingSizeFromOutlet
      : selectedRingSizeLocal;
  const setSelectedRingSize =
    setSelectedRingSizeFromOutlet || setSelectedRingSizeLocal;
  const ringSizeOptions =
    ringSizeOptionsFromOutlet !== undefined
      ? ringSizeOptionsFromOutlet
      : ringSizeOptionsLocal;
  const setRingSizeOptions =
    setRingSizeOptionsFromOutlet || setRingSizeOptionsLocal;

  // ✅ Logged-in dealer from localStorage
  const dealerObj = useMemo(
    () => safeJsonParse(localStorage.getItem("dealer"), null),
    [],
  );
  const dealerKey = useMemo(() => getDealerKey(dealerObj), [dealerObj]);
  const CART_KEY = useMemo(() => cartStorageKey(dealerKey), [dealerKey]);

  // ✅ gst + phone for dealer-wise rate fetch
  const dealerGST = useMemo(() => {
    const p = dealerObj?.profile || dealerObj || {};
    return String(p?.["GSTIN/UIN"] || p?.GSTIN || "").trim();
  }, [dealerObj]);

  const dealerPhone10 = useMemo(() => {
    const p = dealerObj?.profile || dealerObj || {};
    return normalizePhone10(
      p?.phone || p?.["Contact No"] || dealerObj?.phone || "",
    );
  }, [dealerObj]);

  // ✅ dealer-wise rates (from DEALER_RATE_URL) — localStorage me cache
  // taaki refresh pe ₹0.00 flicker na ho. Background fetch update karta hai.
  const dealerRatesCacheKey = useMemo(
    () => `dealerRates:v1:${dealerPhone10 || dealerGST || "anon"}`,
    [dealerPhone10, dealerGST],
  );
  const [dealerRates, setDealerRates] = useState(() => {
    try {
      const raw = localStorage.getItem(
        `dealerRates:v1:${dealerPhone10 || dealerGST || "anon"}`,
      );
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === "object") return parsed;
      }
    } catch {}
    return { sixmmPerMT: 0, bindingWirePerKg: 0, ringsBase: 0 };
  });

  // ✅ Track if dealer rates fetch has completed (to distinguish "loading" from "not open")
  const [dealerRatesLoaded, setDealerRatesLoaded] = useState(false);

  // ✅ Stock cache in localStorage — refresh pe TMT/BW/Rings stock instant dikhe
  // (warna ek brief moment ke liye 0 / "OUT OF STOCK" flicker hota hai).
  const SPECIAL_STOCKS_KEY = "specialStocks:v1";
  const [specialStocks, setSpecialStocks] = useState(() => {
    try {
      const raw = localStorage.getItem(SPECIAL_STOCKS_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === "object") {
          return {
            tmt6: parsed.tmt6 || {},
            bw: parsed.bw || {},
            rings: parsed.rings || {},
          };
        }
      }
    } catch {}
    return { tmt6: {}, bw: {}, rings: {} };
  });

  const [qtyInputs, setQtyInputs] = useState({});
  const [errors, setErrors] = useState({});

  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  // Lazy initial state — agar cache me data hai to loader spinner skip karo
  // (applyProducts cache se sync hydrate kar lega first effect tick me).
  const [loading, setLoading] = useState(() => {
    const cached = getCachedData(PRODUCTS_CACHE_KEY, PRODUCTS_CACHE_TS_KEY);
    return !(Array.isArray(cached) && cached.length > 0);
  });
  const [previewImage, setPreviewImage] = useState(null);

  const [sheetClosed, setSheetClosed] = useState(false);
  const [timeClosed, setTimeClosed] = useState(isAfter630IST());

  const [isCartOpen, setIsCartOpen] = useState(false);
  const cartRef = useRef(null);
  const [scanOrderOpen, setScanOrderOpen] = useState(false);
  // Items fed from the Order Request Form's "Add to Cart" (skip image scan).
  const [formScanItems, setFormScanItems] = useState(null);
  // Feedback popup — opened from the WelcomeDashboard CTA shown when no
  // section tab has been selected yet.
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [orderFormOpen, setOrderFormOpen] = useState(false);
  // Stat-card popups on the welcome dashboard.
  const [pendingOpen, setPendingOpen] = useState(false);
  const [trackingOpen, setTrackingOpen] = useState(false);
  const [outstandingOpen, setOutstandingOpen] = useState(false);
  const [strPopup, setStrPopup] = useState(null); // "dispatch" | "predispatch" | null

  // Pre-fetch the combined orders sheet (pending + history) on mount
  // and keep it fresh via polling. This means the popups open instantly
  // because the data is already in cache by the time the user clicks.
  useEffect(() => {
    // Fire the first fetches immediately so the caches are warm before
    // the user even sees the dashboard cards render counts.
    fetchAndCacheOutstanding();
    // Then refresh every 60s while the dashboard is mounted.
    const id = setInterval(() => {
      fetchAndCacheOutstanding();
    }, 60_000);
    // Also refresh whenever the tab regains focus — cheap way to keep
    // numbers honest after the user comes back from another tab.
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        fetchAndCacheOutstanding();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  // TMT (6MM logic stays same)
  const [tmt6Qty, setTmt6Qty] = useState({});
  const [tmt6Err, setTmt6Err] = useState({});

  // Binding Wire
  const [bwQty, setBwQty] = useState({});
  const [bwErr, setBwErr] = useState({});

  // ✅ Rings
  const [ringsRows, setRingsRows] = useState([]);
  const [ringsLoading, setRingsLoading] = useState(false);
  const [ringsQty, setRingsQty] = useState({});
  const [ringsErr, setRingsErr] = useState({});
  const [ringsPage, setRingsPage] = useState(1);

  /* =========================
     ✅ PER-SECTION BOOKING NOT OPEN CHECKS
     - Structure: check RATE column (empty or "Booking Not Open" text) across products
     - TMT: dealer 6MM rate missing
     - Binding Wire: dealer binding wire rate missing
     - Rings: dealer rings base rate missing
  ========================= */
  const structureBookingNotOpen = useMemo(() => {
    if (!products.length) return false;
    return products.some((p) => {
      const rawRate = String(p?.raw?.["RATE"] || "").trim();
      if (rawRate === "") return true;
      if (rawRate.toLowerCase().includes("booking not open")) return true;
      return false;
    });
  }, [products]);

  const tmtBookingNotOpen = useMemo(() => {
    if (!dealerRatesLoaded) return false; // still loading, don't show closed
    return Number(dealerRates.sixmmPerMT || 0) <= 0;
  }, [dealerRates.sixmmPerMT, dealerRatesLoaded]);

  const bwBookingNotOpen = useMemo(() => {
    if (!dealerRatesLoaded) return false;
    // Open if ANY of MS / GI / legacy binding-wire rate is present.
    const any =
      Number(dealerRates.bindingWireMsPerKg || 0) ||
      Number(dealerRates.bindingWireGiPerKg || 0) ||
      Number(dealerRates.bindingWirePerKg || 0);
    return Number(any) <= 0;
  }, [
    dealerRates.bindingWireMsPerKg,
    dealerRates.bindingWireGiPerKg,
    dealerRates.bindingWirePerKg,
    dealerRatesLoaded,
  ]);

  const ringsBookingNotOpen = useMemo(() => {
    if (!dealerRatesLoaded) return false;
    return Number(dealerRates.ringsBase || 0) <= 0;
  }, [dealerRates.ringsBase, dealerRatesLoaded]);

  // ✅ Global closers (apply to all sections)
  const globallyClosed = useMemo(
    () => timeClosed || sheetClosed,
    [timeClosed, sheetClosed],
  );

  // ✅ Check if at least one section is currently bookable
  const hasAnyOpenSection = useMemo(() => {
    if (globallyClosed) return false;
    return (
      !structureBookingNotOpen ||
      !tmtBookingNotOpen ||
      !bwBookingNotOpen ||
      !ringsBookingNotOpen
    );
  }, [
    globallyClosed,
    structureBookingNotOpen,
    tmtBookingNotOpen,
    bwBookingNotOpen,
    ringsBookingNotOpen,
  ]);

  const pageTopPadding = useMemo(() => {
    return activeSection === "Structure"
      ? "pt-[190px] md:pt-[6rem]"
      : "pt-[130px] md:pt-[6rem]";
  }, [activeSection]);

  /* =========================
     CART PERSISTENCE
  ========================= */
  useEffect(() => {
    if (Array.isArray(cart) && cart.length > 0) return;
    const saved = safeJsonParse(localStorage.getItem(CART_KEY), []);
    if (Array.isArray(saved) && saved.length > 0) setCart(saved);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [CART_KEY]);

  useEffect(() => {
    if (!Array.isArray(cart)) return;
    localStorage.setItem(CART_KEY, JSON.stringify(cart));
  }, [cart, CART_KEY]);

  /* =========================
     LIVE-SYNC CART STOCK WITH PRODUCTS
     Sheet me stock badle (polling), Structure cart items ke availableStock
     ko silent update karte hain — taaki user ko pata chale agar woh
     "out of stock" ho gaya hai jab tak woh checkout pe hai.
     ⚠️ RATE ko intentionally touch nahi karte — kyunki cart me jo rate
     stored hai woh dealer-specific overridden rate hai (effectiveRate),
     aur sheet ka raw rate alag hota hai. Rate overwrite karne se cart
     ke prices toot jaate hain.
  ========================= */
  useEffect(() => {
    if (!Array.isArray(products) || products.length === 0) return;
    if (!Array.isArray(cart) || cart.length === 0) return;

    const productByName = new Map(
      products.map((p) => [String(p?.name || "").trim().toLowerCase(), p]),
    );

    let changed = false;
    const next = cart.map((item) => {
      const isStructureItem =
        item?.section === "Structure" || item?.category === "structure";
      if (!isStructureItem) return item;

      const fresh = productByName.get(
        String(item?.name || "").trim().toLowerCase(),
      );
      if (!fresh) return item;

      const newStock = Number(fresh?.availableStock || 0);
      const oldStock = Number(item?.availableStock || 0);
      if (newStock === oldStock) return item;

      changed = true;
      return { ...item, availableStock: newStock, stock: newStock };
    });

    if (changed) setCart(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products]);

  useEffect(() => {
    const t = setInterval(() => setTimeClosed(isAfter630IST()), 20000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        isCartOpen &&
        cartRef.current &&
        !cartRef.current.contains(e.target)
      ) {
        setIsCartOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isCartOpen]);

  /* =========================
     ✅ FETCH DEALER RATES BY GST/PHONE
     ✅ Rings base fetched like Binding Wire
  ========================= */
  useEffect(() => {
    const run = async () => {
      try {
        if (!dealerGST && !dealerPhone10) {
          setDealerRates({ sixmmPerMT: 0, bindingWirePerKg: 0, ringsBase: 0 });
          setDealerRatesLoaded(true);
          return;
        }

        const qs = new URLSearchParams();
        if (dealerGST) qs.set("gst", dealerGST);
        if (dealerPhone10) qs.set("phone", dealerPhone10);

        const res = await fetch(`${DEALER_RATE_URL}?${qs.toString()}`, {
          cache: "no-store",
        });
        const payload = await res.json().catch(() => null);

        const rows = extractRows(payload);
        const row =
          pickFirstMatchByGstOrPhone(rows, dealerGST, dealerPhone10) ||
          rows[0] ||
          null;

        if (!row) {
          setDealerRates({ sixmmPerMT: 0, bindingWirePerKg: 0, ringsBase: 0 });
          setDealerRatesLoaded(true);
          return;
        }

        const sixmmPerMT = getNum(row, [
          "6MM",
          "6mm",
          "6MM Rate",
          "6MM Rate/MT",
          "Rate 6MM",
          "6MM_Rate",
        ]);

        const bindingWirePerKg = getNum(row, [
          "BINDING WIRE",
          "Binding Wire",
          "BindingWire",
          "Binding Wire Rate",
          "BW",
          "BW Rate",
          "BindingWireRate",
        ]);

        // MS & GI have separate columns in the dealer sheet; fall back to the
        // legacy single "BINDING WIRE" column if a variant column is missing.
        const bindingWireMsPerKg = getNum(row, [
          "BINDING WIRE (MS)",
          "BINDING WIRE(MS)",
          "BINDING WIRE - MS",
          "BINDING WIRE MS",
          "Binding Wire (MS)",
          "BW (MS)",
          "BW MS",
        ]);
        const bindingWireGiPerKg = getNum(row, [
          "BINDING WIRE (GI)",
          "BINDING WIRE(GI)",
          "BINDING WIRE - GI",
          "BINDING WIRE GI",
          "Binding Wire (GI)",
          "BW (GI)",
          "BW GI",
        ]);

        const ringsBase = getNum(row, [
          "RING",
          "RINGS",
          "Ring",
          "Rings",
          "MS RING",
          "MS RINGS",
          "MS Rings",
          "MSRing",
          "MSRings",
          "RING RATE",
          "RINGS RATE",
          "Ring Rate",
          "Rings Rate",
          "MS RINGS RATE",
          "MS RING RATE",
          "RINGS ",
          "RING ",
        ]);

        const fresh = {
          sixmmPerMT,
          bindingWirePerKg,
          bindingWireMsPerKg,
          bindingWireGiPerKg,
          ringsBase,
        };
        setDealerRates(fresh);
        setDealerRatesLoaded(true);
        try {
          localStorage.setItem(dealerRatesCacheKey, JSON.stringify(fresh));
        } catch {}
      } catch (e) {
        console.error("Dealer rate fetch failed:", e);
        // Fetch fail ho to cached value rakhne do — overwrite mat karo zeros se
        setDealerRatesLoaded(true);
      }
    };

    run();
  }, [dealerGST, dealerPhone10, dealerRatesCacheKey]);

  const fetchSpecialStocks = useCallback(async () => {
    try {
      const res = await fetch(STOCK_URL, { cache: "no-store" });
      const payload = await res.json().catch(() => []);

      const rows = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
          ? payload.data
          : [];

      const next = { tmt6: {}, bw: {}, rings: {} };

      rows.forEach((row) => {
        const product = normalizeText(row?.PRPDUCT || row?.PRODUCT);
        const stock = toSafeStock(row?.STOCK);

        if (product === "6mm") {
          const size = normalizeSizeKey(row?.SIZE);
          if (size) next.tmt6[size] = stock;
        } else if (product === "bw") {
          const size = normalizeSizeKey(row?.SIZE);
          if (size) next.bw[size] = stock;
        } else if (product === "ring" || product === "rings") {
          const size = normalizeRingStockKey(row?.SIZE);
          if (size) next.rings[size] = stock;
        }
      });

      setSpecialStocks(next);
      try {
        localStorage.setItem(SPECIAL_STOCKS_KEY, JSON.stringify(next));
      } catch {}
    } catch (err) {
      console.error("Special stock fetch failed:", err);
    }
  }, [SPECIAL_STOCKS_KEY]);

  useEffect(() => {
    fetchSpecialStocks();
  }, [fetchSpecialStocks]);

  // Auto-poll stock every 30s silently so sold-out / new arrivals reflect live.
  usePolling(fetchSpecialStocks, 30_000);

  /* =========================
     FETCH PRODUCTS SHEET (stale-while-revalidate + auto-poll)
     ✅ sheetClosed now only reflects the explicit "Booking Not Open" flag column.
        Rate-based closure is per-section (structureBookingNotOpen memo).
  ========================= */
  const applyProducts = useCallback((data) => {
    if (!Array.isArray(data)) return;

    // Sheet-wide flag (admin manually marks "Booking Not Open" column on any row
    // to close everything). Per-row rate-empty check ab GLOBAL nahi — har card
    // apne aap rate-empty hone par "Booking Not Open" dikhayega.
    const bookingClosedByFlag = data.some(
      (row) => String(row["Booking Not Open"] || "").trim() !== "",
    );
    setSheetClosed(bookingClosedByFlag);

    const isRateEmpty = (row) =>
      String(row["RATE"] ?? "").trim() === "";

    const formatted = data
      .map((row) => {
        const rawRank = row["Rank"];
        const hasRank =
          rawRank !== undefined &&
          rawRank !== null &&
          String(rawRank).trim() !== "";
        const rank = hasRank
          ? Number(String(rawRank).trim())
          : Number.POSITIVE_INFINITY;

        return {
          name: row["Standard Name"] || "Unknown Product",
          type: row["TYPE"] || "",
          img: row["Image"] || row["Img"] || "/images/default.png",
          rate: Number(row["RATE"] || 0),
          availableStock: Number(row["Today Stock"] || 0),
          basic: Number(row["Basic"] || 0),
          pattiBasic: Number(row["Patti Basic"] || 0),
          rank,
          // Per-product flag — rate empty hai to is item ke liye booking not open
          bookingNotOpen: isRateEmpty(row),
          raw: row,
        };
      })
      .sort((a, b) => a.rank - b.rank);

    setProducts(formatted);
    setFilteredProducts(formatted);
  }, []);

  useEffect(() => {
    const fetchProducts = async () => {
      // 1. Show cached instantly — no loading spinner if we have cache
      const cached = getCachedData(PRODUCTS_CACHE_KEY, PRODUCTS_CACHE_TS_KEY);
      const hasCached = Array.isArray(cached) && cached.length > 0;
      if (hasCached) applyProducts(cached);
      else setLoading(true);

      // 2. Background fetch from backend (Mongo cache is fast — no Apps Script blocker).
      //    Backend ka apna 30s polling fresh data laata hai sheet se.
      try {
        const res = await fetch(SHEET_URL, { cache: "no-store" });
        const data = await res.json();
        setCachedData(PRODUCTS_CACHE_KEY, PRODUCTS_CACHE_TS_KEY, data);
        applyProducts(data);
      } catch (err) {
        console.error("Failed to fetch products:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [applyProducts]);

  // Auto-poll: every 30s, re-read products from backend (silent background update).
  usePolling(async () => {
    try {
      const res = await fetch(SHEET_URL, { cache: "no-store" });
      const data = await res.json();
      setCachedData(PRODUCTS_CACHE_KEY, PRODUCTS_CACHE_TS_KEY, data);
      applyProducts(data);
    } catch {}
  }, 30_000);

  // ✅ RINGS: stale-while-revalidate + auto-poll while section is active
  const applyRings = useCallback((rows) => {
    const cleaned = (Array.isArray(rows) ? rows : [])
      .map((r, idx) => {
        const stirrup = normalizeRingLabel(
          r?.Stirrup || r?.stirrup || r?.Size || r?.size || "",
        );

        const parity = Number(r?.Rate || r?.rate || 0);
        const name = String(r?.Name || r?.name || "").trim();

        const rank = getRank(r, [
          "Rank",
          "RANK",
          "rank",
          "Sequence",
          "SEQ",
          "Sort",
          "SORT",
          "Order",
          "ORDER",
        ]);

        const perPieceWt = getRingPerPieceWeight(r);

        return {
          _i: idx,
          stirrup,
          parity,
          name,
          rank,
          perPieceWt,
          raw: r,
        };
      })
      .filter((x) => x.stirrup);

    setRingsRows(cleaned);
    setRingsPage(1);
  }, []);

  useEffect(() => {
    const run = async () => {
      if (activeSection !== "Rings") return;

      // 1. Show cached rings instantly — no spinner if cache hai
      const cached = getCachedData(RINGS_CACHE_KEY, RINGS_CACHE_TS_KEY);
      const hasCached = Array.isArray(cached) && cached.length > 0;
      if (hasCached) applyRings(cached);
      else setRingsLoading(true);

      // 2. Background fetch from backend (Mongo cache — fast)
      try {
        const res = await fetch(RINGS_URL, { cache: "no-store" });
        const payload = await res.json().catch(() => null);
        const rows = extractRows(payload);
        setCachedData(RINGS_CACHE_KEY, RINGS_CACHE_TS_KEY, rows);
        applyRings(rows);
      } catch (e) {
        console.error("Rings fetch failed:", e);
      } finally {
        setRingsLoading(false);
      }
    };

    run();
  }, [activeSection, applyRings]);

  // Auto-poll rings silently every 30s, only while user is on the Rings section
  usePolling(
    async () => {
      const res = await fetch(RINGS_URL, { cache: "no-store" });
      const payload = await res.json().catch(() => null);
      const rows = extractRows(payload);
      setCachedData(RINGS_CACHE_KEY, RINGS_CACHE_TS_KEY, rows);
      applyRings(rows);
    },
    30_000,
    activeSection === "Rings"
  );

  useEffect(() => {
    if (activeSection !== "Rings") {
      setSelectedRingSize("All");
    }
  }, [activeSection, setSelectedRingSize]);

  /* =========================
     STRUCTURE FILTER ONLY
     ✅ No longer short-circuits on isBookingClosed — per-section closure is handled in render.
  ========================= */
  useEffect(() => {
    if (globallyClosed) {
      setFilteredProducts([]);
      return;
    }
    if (activeSection !== "Structure") return;

    // ✅ Empty-rate items hide karo from Structure list — agar product ka rate
    // nahi set hai sheet me, to use grid me dikhana nahi. Agar saare products
    // empty-rate ho jaayein to per-section "Booking Will Open Soon" block dikhega.
    let result = products.filter((p) => p?.bookingNotOpen !== true);

    if (selectedCategory !== "All") {
      const targetType = typeMap[selectedCategory];
      if (targetType) result = result.filter((p) => p.type === targetType);
    }

    if (selectedSize && selectedSize !== "All") {
      result = result.filter((p) => p.name === selectedSize);
    }

    if (searchQuery?.trim()) {
      const q = searchQuery.toLowerCase().trim();
      const detectedType = detectTypeFromQuery(q);
      if (detectedType) {
        result = result.filter(
          (p) => String(p.type || "").trim() === detectedType,
        );
      }

      const nameQuery = stripTypeWords(q);
      if (nameQuery) {
        result = result.filter((p) =>
          String(p.name || "")
            .toLowerCase()
            .includes(nameQuery),
        );
      }
    }

    setFilteredProducts(result);
    setCurrentPage(1);
  }, [
    products,
    selectedCategory,
    selectedSize,
    searchQuery,
    globallyClosed,
    activeSection,
  ]);

  /* =========================
     CLEAN STALE INPUTS/ERRORS
  ========================= */
  useEffect(() => {
    if (activeSection !== "Structure") return;

    const validKeys = new Set(
      filteredProducts.map((p) => getProductInputKey(p)),
    );

    setQtyInputs((prev) => {
      const next = {};
      Object.keys(prev || {}).forEach((key) => {
        if (validKeys.has(key)) next[key] = prev[key];
      });
      return next;
    });

    setErrors((prev) => {
      const next = {};
      Object.keys(prev || {}).forEach((key) => {
        if (validKeys.has(key)) next[key] = prev[key];
      });
      return next;
    });
  }, [filteredProducts, activeSection]);

  /* =========================
     TMT FILTER USING SEARCH QUERY (previous 6MM)
  ========================= */
  const filteredTmtOptions = useMemo(() => {
    const q = String(searchQuery || "")
      .toLowerCase()
      .trim();
    if (!q) return TMT6_OPTIONS;
    return TMT6_OPTIONS.filter((o) =>
      String(o.label || "")
        .toLowerCase()
        .includes(q),
    );
  }, [searchQuery]);

  /* =========================
     Binding Wire FILTER (options only)
  ========================= */
  const tmt6StockMap = useMemo(() => {
    return {
      mix: Number(
        specialStocks?.tmt6?.[normalizeSizeKey("Mix Size Booking")] || 0,
      ),
      "30-40": Number(specialStocks?.tmt6?.[normalizeSizeKey("30 to 40")] || 0),
    };
  }, [specialStocks]);

  const filteredBwOptions = useMemo(() => {
    const q = String(searchQuery || "")
      .toLowerCase()
      .trim();
    if (!q) return BW_OPTIONS;
    return BW_OPTIONS.filter((o) =>
      String(o.label || "")
        .toLowerCase()
        .includes(q),
    );
  }, [searchQuery]);

  const bindingWireStockMap = useMemo(() => {
    return {
      ms: Number(specialStocks?.bw?.[normalizeSizeKey("MS")] || 0),
      gi: Number(specialStocks?.bw?.[normalizeSizeKey("GI")] || 0),
    };
  }, [specialStocks]);

  // ✅ Binding Wire rates — MS & GI have separate dealer columns.
  // Fall back to the legacy single "BINDING WIRE" column if a variant is blank.
  const bindingWireRateMapKg = useMemo(() => {
    const legacy = Number(dealerRates.bindingWirePerKg || 0);
    const ms = Number(dealerRates.bindingWireMsPerKg || 0) || legacy;
    const gi = Number(dealerRates.bindingWireGiPerKg || 0) || legacy;
    return {
      ms: toKgRateFromMaybeMT(ms),
      gi: toKgRateFromMaybeMT(gi),
    };
  }, [
    dealerRates.bindingWirePerKg,
    dealerRates.bindingWireMsPerKg,
    dealerRates.bindingWireGiPerKg,
  ]);

  // Single default (MS-primary) — used by the scan-order fallback.
  const bindingWireBaseRateKg = bindingWireRateMapKg.ms;

  /* =========================
     ✅ RINGS (dealer base + parity list)
  ========================= */
  const ringsBaseRateKg = useMemo(() => {
    return toKgRateFromMaybeMT(dealerRates.ringsBase || 0);
  }, [dealerRates.ringsBase]);

  const computedRingSizeOptions = useMemo(() => {
    return Array.from(
      new Set(
        ringsRows.map((r) => String(r?.stirrup || "").trim()).filter(Boolean),
      ),
    ).sort((a, b) => a.localeCompare(b));
  }, [ringsRows]);

  useEffect(() => {
    setRingSizeOptions(computedRingSizeOptions);
  }, [computedRingSizeOptions, setRingSizeOptions]);

  const filteredRingsOptionsAll = useMemo(() => {
    const q = String(searchQuery || "")
      .toLowerCase()
      .trim();

    let list = ringsRows
      .map((r) => ({
        key: `ring-${r._i}`,
        label: r.stirrup,
        parity: r.parity,
        name: r.name,
        rank: r.rank,
        raw: r.raw,
      }))
      .sort((a, b) => {
        const ar = Number.isFinite(a.rank) ? a.rank : Number.POSITIVE_INFINITY;
        const br = Number.isFinite(b.rank) ? b.rank : Number.POSITIVE_INFINITY;
        if (ar !== br) return ar - br;
        return String(a.label || "").localeCompare(String(b.label || ""));
      });

    if (selectedRingSize && selectedRingSize !== "All") {
      list = list.filter(
        (x) => String(x.label || "").trim() === String(selectedRingSize).trim(),
      );
    }

    if (!q) return list;

    return list.filter((x) => {
      const s = `${x.label || ""} ${x.name || ""}`.toLowerCase();
      return s.includes(q);
    });
  }, [ringsRows, searchQuery, selectedRingSize]);

  const ringsStockMap = useMemo(() => {
    const map = {};
    filteredRingsOptionsAll.forEach((opt) => {
      map[opt.key] = Number(
        specialStocks?.rings?.[normalizeRingStockKey(opt.label)] || 0,
      );
    });
    return map;
  }, [specialStocks, filteredRingsOptionsAll]);

  const ringsTotalPages =
    Math.ceil(filteredRingsOptionsAll.length / RINGS_PER_PAGE) || 1;
  const ringsStart = (ringsPage - 1) * RINGS_PER_PAGE;
  const ringsOptionsPage = filteredRingsOptionsAll.slice(
    ringsStart,
    ringsStart + RINGS_PER_PAGE,
  );

  useEffect(() => {
    if (ringsPage > ringsTotalPages) setRingsPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ringsTotalPages]);

  const handleRingsPageChange = (e, value) => {
    setRingsPage(value);
    window.scrollTo({ top: 220, behavior: "smooth" });
  };

  /* =========================
     PAGINATION FOR STRUCTURE
  ========================= */
  const totalPages = Math.ceil(filteredProducts.length / PRODUCTS_PER_PAGE);
  const startIndex = (currentPage - 1) * PRODUCTS_PER_PAGE;
  const currentProducts = filteredProducts.slice(
    startIndex,
    startIndex + PRODUCTS_PER_PAGE,
  );

  const handlePageChange = (e, value) => {
    setCurrentPage(value);
    window.scrollTo({ top: 500, behavior: "smooth" });
  };

  /* =========================
     ADD TO CART HANDLERS
  ========================= */
  const handleAddToCart = useCallback(
    (product) => {
      const inputKey = getProductInputKey(product);
      const qty = Number(qtyInputs[inputKey] || 0);

      if (!qty || qty <= 0) {
        setErrors((prev) => ({
          ...prev,
          [inputKey]: "Please enter a valid quantity",
        }));
        return;
      }

      // ✅ Already-in-cart check (matches TMT / BW / Rings behavior)
      const cartName = String(product?.name || "").trim();
      const isAlreadyInCart = cart.some(
        (item) =>
          String(item?.name || "").trim().toLowerCase() ===
          cartName.toLowerCase(),
      );
      if (isAlreadyInCart) {
        setErrors((prev) => ({
          ...prev,
          [inputKey]: "Already added to cart",
        }));
        return;
      }

      const availableStock = toSafeStock(
        product?.availableStock ?? product?.stock ?? 0,
      );

      if (availableStock <= 0) {
        setErrors((prev) => ({
          ...prev,
          [inputKey]: "This item is out of stock",
        }));
        return;
      }

      if (qty > availableStock) {
        setErrors((prev) => ({
          ...prev,
          [inputKey]: "Required quantity is not available right now",
        }));
        return;
      }

      const rateKg = Number(product?.rate || 0);
      const baseRate = Number(
        product?.basic ??
          product?.baseRate ??
          product?.basicRate ??
          product?.rate ??
          0,
      );
      const pattiBasic = Number(product?.pattiBasic || 0);

      const standardName = String(
        product?.standardName || product?.name || "",
      ).trim();
      const typeCode = String(product?.type || product?.TYPE || "").trim();

      const newItem = {
        name: cartName,
        standardName,
        size: standardName,
        type: typeCode,

        qty,
        rate: rateKg,
        basicRate: baseRate,
        baseRate: baseRate,
        basic: baseRate,
        pattiBasic,

        stock: availableStock,
        availableStock: availableStock,

        subtotal: Number((qty * rateKg).toFixed(2)),
        image: product?.img || product?.image || "",
        category: "structure",
        section: "Structure",
      };

      // already-in-cart check upar already ho chuka — yahaan bas append karte hain
      setCart((prev) => [...prev, newItem]);

      setQtyInputs((prev) => ({
        ...prev,
        [inputKey]: "",
      }));

      setErrors((prev) => ({
        ...prev,
        [inputKey]: "",
      }));
    },
    [cart, qtyInputs, setCart, setErrors, setQtyInputs],
  );

  // ✅ TMT add uses finalRate passed from TmtSection
  const handleAddTmt6ToCart = (
    opt,
    finalRate,
    baseRateKg,
    parityKg,
    availableStock,
  ) => {
    const qty = Number(tmt6Qty[opt.key] || 0);
    setTmt6Err((p) => ({ ...p, [opt.key]: "" }));

    if (!qty || qty <= 0) {
      setTmt6Err((p) => ({ ...p, [opt.key]: "Please enter a valid quantity" }));
      return;
    }

    if (qty > Number(availableStock || 0)) {
      setTmt6Err((p) => ({ ...p, [opt.key]: "Please add a lower quantity" }));
      return;
    }

    const cartName = `TMT - ${opt.label}`;
    const isAlreadyInCart = cart.some((item) => item.name === cartName);
    if (isAlreadyInCart) {
      setTmt6Err((p) => ({ ...p, [opt.key]: "Already added to cart" }));
      return;
    }

    setCart((prev) => [
      ...prev,
      {
        name: cartName,
        qty,
        rate: Number(finalRate || 0),
        subtotal: qty * Number(finalRate || 0),
        availableStock: Number(availableStock || 0),
        meta: {
          baseRateKg: Number(baseRateKg || 0),
          parityKg: Number(parityKg || 0),
          parityPerMT: Number(opt.parityPerMT || 0),
          dealer6mmPerMT: Number(dealerRates.sixmmPerMT || 0),
        },
      },
    ]);

    setTmt6Qty((p) => ({ ...p, [opt.key]: "" }));
    setTmt6Err((p) => ({ ...p, [opt.key]: "" }));
  };

  // ✅ Binding Wire add
  const handleAddBindingWireToCart = (opt, rateKg, availableStock) => {
    const qty = Number(bwQty?.[opt.key] || 0);
    setBwErr((p) => ({ ...p, [opt.key]: "" }));

    if (!qty || qty <= 0) {
      setBwErr((p) => ({ ...p, [opt.key]: "Please enter a valid quantity" }));
      return;
    }

    if (Number(rateKg || 0) <= 0) {
      setBwErr((p) => ({ ...p, [opt.key]: "Dealer rate not found" }));
      return;
    }

    if (qty > Number(availableStock || 0)) {
      setBwErr((p) => ({ ...p, [opt.key]: "Please add a lower quantity" }));
      return;
    }

    const cartName = `Binding Wire - ${opt.label}`;
    const isAlreadyInCart = cart.some((item) => item.name === cartName);
    if (isAlreadyInCart) {
      setBwErr((p) => ({ ...p, [opt.key]: "Already added to cart" }));
      return;
    }

    const rk = Number(rateKg || 0);

    setCart((prev) => [
      ...prev,
      {
        name: cartName,
        qty,
        rate: rk,
        subtotal: qty * rk,
        availableStock: Number(availableStock || 0),
        meta: {
          section: "Binding Wire",
          sizeKey: opt.key,
          sizeLabel: opt.label,
          dealerBindingWireRateKg: rk,
        },
      },
    ]);

    setBwQty((p) => ({ ...p, [opt.key]: "" }));
    setBwErr((p) => ({ ...p, [opt.key]: "" }));
  };

  // ✅ Rings add
  const handleAddRingsToCart = (
    opt,
    finalRateKg,
    baseKg,
    parityKg,
    availableStock,
  ) => {
    const qty = Number(ringsQty?.[opt.key] || 0);
    setRingsErr((p) => ({ ...p, [opt.key]: "" }));

    if (!qty || qty <= 0) {
      setRingsErr((p) => ({
        ...p,
        [opt.key]: "Please enter a valid quantity",
      }));
      return;
    }

    if (Number(baseKg || 0) <= 0) {
      setRingsErr((p) => ({ ...p, [opt.key]: "Dealer ring base not found" }));
      return;
    }

    if (qty > Number(availableStock || 0)) {
      setRingsErr((p) => ({ ...p, [opt.key]: "Please add a lower quantity" }));
      return;
    }

    const cartName = `Rings - ${opt.label}`;
    const isAlreadyInCart = cart.some((item) => item.name === cartName);
    if (isAlreadyInCart) {
      setRingsErr((p) => ({ ...p, [opt.key]: "Already added to cart" }));
      return;
    }

    const rk = Number(finalRateKg || 0);
    const perPieceWt = Number(
      opt?.perPieceWt || opt?.raw?.["Per Piece Wt."] || 0,
    );

    setCart((prev) => [
      ...prev,
      {
        name: cartName,
        qty,
        rate: rk,
        subtotal: qty * rk,
        availableStock: Number(availableStock || 0),

        // ✅ top-level par pass karo taaki backend direct use kar sake
        perPieceWt,
        qtyUnit: "PCS",

        meta: {
          section: "Rings",
          sizeName: opt.label,
          rank: Number.isFinite(opt.rank) ? opt.rank : null,
          parityRaw: Number(opt.parity || 0),
          parityKg: Number(parityKg || 0),
          baseRateKg: Number(baseKg || 0),
          dealerRingsBaseRaw: Number(dealerRates.ringsBase || 0),
          source: "RINGS_URL",
          perPieceWt,
        },
      },
    ]);

    setRingsQty((p) => ({ ...p, [opt.key]: "" }));
    setRingsErr((p) => ({ ...p, [opt.key]: "" }));
  };

  const handleRemoveFromCart = (name) =>
    setCart((prev) => prev.filter((item) => item.name !== name));

  const cartTotal = cart.reduce(
    (sum, item) => sum + (item.subtotal || item.qty * item.rate),
    0,
  );

  /* =========================
     ✅ RENDER HELPERS
  ========================= */
  // Booking-not-open block for a section (reusable)
  const renderBookingNotOpenBlock = () => (
    <BookingClosedBlock
      sheetClosed={true}
      bookingClosedTitle="Booking Will Open Soon"
      subtitle="Please check back soon."
    />
  );

  return (
    <div
      className={`min-h-screen bg-gray-50 ${pageTopPadding} pb-[7.5rem] md:pb-10`}
    >
      <div className="mx-auto px-6 pt-[10px]">
        {/* ✅ Tabs are ALWAYS visible when not loading (even when booking is closed/not open) */}
        <Tabs
          activeSection={activeSection}
          setActiveSection={(v) => {
            setActiveSection(v);
            setCurrentPage(1);
            setRingsPage(1);
          }}
          loading={loading}
          onTop={() => window.scrollTo({ top: 200, behavior: "smooth" })}
          onDownloadForm={() => setOrderFormOpen(true)}
        />

        <OrderRequestForm
          open={orderFormOpen}
          onClose={() => setOrderFormOpen(false)}
          dealerPhone={dealerPhone10}
          onAddToCart={(items, meta) => {
            // Carry payment term + vehicle to the cart, then open the scan
            // review with these preset items (no image / Gemini needed).
            try {
              if (meta?.paymentTerm || meta?.vehicleNo) {
                localStorage.setItem(
                  "scanCartMeta:v1",
                  JSON.stringify({
                    paymentTerm: meta.paymentTerm || "",
                    vehicleNo: meta.vehicleNo || "",
                    ts: Date.now(),
                  })
                );
              }
            } catch {
              /* ignore */
            }
            setFormScanItems(items);
            setOrderFormOpen(false);
            setScanOrderOpen(true);
          }}
        />

        <div className="flex w-full flex-col gap-3 md:flex-row md:items-center md:justify-between">
          {/* ✅ Section-aware title (hide when that section is closed/empty) */}
          {!globallyClosed &&
          activeSection === "Structure" &&
          !structureBookingNotOpen ? (
            <h1 className="text-base md:text-xl font-bold text-gray-800 mb-0 md:mb-4 font-['DM-sans']">
              {selectedCategory} Products
              <span className="text-gray-500 text-sm md:text-lg font-normal ml-3">
                ({filteredProducts.length} items)
              </span>
            </h1>
          ) : !globallyClosed &&
            activeSection === "Rings" &&
            !ringsBookingNotOpen ? (
            <h1 className="text-base md:text-xl font-bold text-gray-800 mb-0 md:mb-4 font-['DM-sans']">
              Rings
              <span className="text-gray-500 text-sm md:text-lg font-normal ml-3">
                ({filteredRingsOptionsAll.length} items)
              </span>
            </h1>
          ) : (
            <div />
          )}

          {/* ✅ Cart / Scan visible only on the Order Here page (section active) */}
          {!loading && activeSection && hasAnyOpenSection && (
            <div className="flex items-center gap-2 justify-end">
              {/* ✅ Scan Order Button */}
              <button
                type="button"
                onClick={() => setScanOrderOpen(true)}
                className="flex items-center gap-1.5 px-3 py-1 max-md:mb-[10px] rounded-full bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 text-black font-bold shadow-md transition text-sm active:scale-95"
                title="Scan order image and auto-fill cart"
              >
                <DocumentScannerIcon sx={{ fontSize: 17 }} />
                <span className="hidden sm:inline">Scan Order</span>
                <span className="sm:hidden">Scan</span>
              </button>

              <CartDropdown
                cart={cart}
                cartTotal={cartTotal}
                isCartOpen={isCartOpen}
                setIsCartOpen={setIsCartOpen}
                onRemove={handleRemoveFromCart}
                cartRef={cartRef}
              />
            </div>
          )}
        </div>

        {/* ✅ RENDER LOGIC:
            1. Loading → spinner
            2. Global close (time-closed OR sheet flag) → global block
            3. Per-section → show either content or per-section "Booking Will Open Soon"
        */}
        {loading ? (
          <LoadingBlock />
        ) : timeClosed ? (
          <BookingClosedBlock
            sheetClosed={false}
            bookingClosedTitle="Booking is Closed"
          />
        ) : sheetClosed ? (
          <BookingClosedBlock
            sheetClosed={true}
            bookingClosedTitle="Booking Will Open Soon"
          />
        ) : !activeSection ? (
          // No section selected yet → show welcome dashboard with pending
          // orders count, total orders count, and feedback CTA. Tab pulse
          // + "Order Here →" arrow from the Tabs component above guide
          // the user to make a selection.
          <WelcomeDashboard
            dealerPhone={dealerPhone10}
            onFeedbackClick={() => setFeedbackOpen(true)}
            onPendingClick={() => setPendingOpen(true)}
            onTrackingClick={() => setTrackingOpen(true)}
            onOutstandingClick={() => setOutstandingOpen(true)}
            onDispatchClick={() => setStrPopup("dispatch")}
            onPreDispatchClick={() => setStrPopup("predispatch")}
          />
        ) : (
          <>
            {/* ✅ STRUCTURE SECTION */}
            {activeSection === "Structure" &&
              (structureBookingNotOpen ? (
                renderBookingNotOpenBlock()
              ) : filteredProducts.length === 0 ? (
                <EmptyState />
              ) : (
                <>
                  <StructureSection
                    currentProducts={currentProducts}
                    startIndex={startIndex}
                    qtyInputs={qtyInputs}
                    setQtyInputs={setQtyInputs}
                    errors={errors}
                    setErrors={setErrors}
                    onAdd={handleAddToCart}
                    setPreviewImage={setPreviewImage}
                  />
                  <StructurePagination
                    totalPages={totalPages}
                    currentPage={currentPage}
                    onChange={handlePageChange}
                  />
                </>
              ))}

            {/* ✅ TMT SECTION */}
            {activeSection === "TMT" &&
              (tmtBookingNotOpen ? (
                renderBookingNotOpenBlock()
              ) : (
                <TmtSection
                  options={filteredTmtOptions}
                  tmt6Base={{
                    found: false,
                    rate: 0,
                    availableStock: 0,
                    basic: 0,
                    pattiBasic: 0,
                    img: "/images/default.png",
                    name: "TMT",
                  }}
                  tmt6Qty={tmt6Qty}
                  setTmt6Qty={setTmt6Qty}
                  tmt6Err={tmt6Err}
                  setTmt6Err={setTmt6Err}
                  onAdd={handleAddTmt6ToCart}
                  dealer6mmPerMT={dealerRates.sixmmPerMT}
                  tmt6StockMap={tmt6StockMap}
                  setPreviewImage={setPreviewImage}
                />
              ))}

            {/* ✅ BINDING WIRE SECTION */}
            {activeSection === "Binding Wire" &&
              (bwBookingNotOpen ? (
                renderBookingNotOpenBlock()
              ) : (
                <BindingWireSection
                  options={filteredBwOptions}
                  rateMap={bindingWireRateMapKg}
                  qtyMap={bwQty}
                  setQtyMap={setBwQty}
                  errMap={bwErr}
                  setErrMap={setBwErr}
                  onAdd={handleAddBindingWireToCart}
                  stockMap={bindingWireStockMap}
                  setPreviewImage={setPreviewImage}
                />
              ))}

            {/* ✅ RINGS SECTION */}
            {activeSection === "Rings" &&
              (ringsBookingNotOpen ? (
                renderBookingNotOpenBlock()
              ) : ringsLoading ? (
                <LoadingBlock />
              ) : filteredRingsOptionsAll.length === 0 ? (
                <EmptyState />
              ) : (
                <>
                  <RingsSection
                    ringsOptions={ringsOptionsPage}
                    ringsBaseRateKg={ringsBaseRateKg}
                    ringsQty={ringsQty}
                    setRingsQty={setRingsQty}
                    ringsErr={ringsErr}
                    setRingsErr={setRingsErr}
                    onAdd={handleAddRingsToCart}
                    ringsStockMap={ringsStockMap}
                    setPreviewImage={setPreviewImage}
                  />

                  <RingsPaginationFixed
                    totalPages={ringsTotalPages}
                    currentPage={ringsPage}
                    onChange={handleRingsPageChange}
                  />
                </>
              ))}
          </>
        )}
      </div>

      <ImagePreviewModal
        previewImage={previewImage}
        setPreviewImage={setPreviewImage}
      />

      {/* ✅ Scan Order Modal */}
      <ScanOrderModal
        open={scanOrderOpen}
        onClose={() => {
          setScanOrderOpen(false);
          setFormScanItems(null);
        }}
        cart={cart}
        setCart={setCart}
        products={filteredProducts}
        sixmmPerMT={dealerRates.sixmmPerMT}
        bindingWirePerKg={bindingWireBaseRateKg}
        bindingWireRateMapKg={bindingWireRateMapKg}
        ringsBaseRateKg={ringsBaseRateKg}
        tmt6StockMap={tmt6StockMap}
        bindingWireStockMap={bindingWireStockMap}
        ringsStockMap={ringsStockMap}
        allRingsOptions={filteredRingsOptionsAll}
        presetItems={formScanItems}
      />

      {/* Feedback popup — triggered from the welcome dashboard CTA */}
      <FeedbackPopup
        open={feedbackOpen}
        onClose={() => setFeedbackOpen(false)}
      />

      {/* Pending orders + Order tracking — opened from the welcome
          dashboard stat cards. Pure data viewers, no mutations. */}
      <PendingOrdersPopup
        open={pendingOpen}
        onClose={() => setPendingOpen(false)}
        dealerPhone={dealerPhone10}
      />
      <OrderTrackingPopup
        open={trackingOpen}
        onClose={() => setTrackingOpen(false)}
        dealerPhone={dealerPhone10}
      />
      <OutstandingPopup
        open={outstandingOpen}
        onClose={() => setOutstandingOpen(false)}
        dealerPhone={dealerPhone10}
      />
      <StrDispatchPopup
        open={!!strPopup}
        feed={strPopup || "dispatch"}
        onClose={() => setStrPopup(null)}
        dealerPhone={dealerPhone10}
      />
    </div>
  );
}
